import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
  Calendar, Key, MessageSquare, FileText, Plus, Trash2, Heart,
  Eye, Search, Send, ChevronLeft, ChevronRight, Download,
  X, Edit3, Loader2, Lock, Globe, User, Users, EyeOff, MoreHorizontal,
  ThumbsUp, Share2, Bell, Image as ImageIcon, Smile, Menu, ChevronDown, Pin,
  Phone, Mail, MapPin, Clock, Building2, ChevronUp, ChevronsUpDown, Inbox, Info, ExternalLink,
  Check, Ban, AlertTriangle,
} from "lucide-react";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SSE_BASE = process.env.REACT_APP_BACKEND_URL;

// ── SSE hook ──────────────────────────────────────────────────────────────────
// Lytter på server-sent events og opdaterer posts-state live.
// sseUrl: fuld URL til SSE-stream endpoint (inkl. token query param)
// currentUser: bruges til liked_by_me/seen_by_me beregning
// feedPath: fx "hub/feed" eller "hub/elev-feed" — bruges til at hente fuld post ved billeder
function useFeedSSE(sseUrl, setPosts, currentUser, token, feedPath) {
  const esRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    if (!sseUrl) return;
    let es;

    const connect = () => {
      es = new window.EventSource(sseUrl);
      esRef.current = es;

      es.addEventListener("post.new", async (e) => {
        const post = JSON.parse(e.data);
        // Hvis posten har billeder, hent den frisk for at få image-data med
        if (post.image_count > 0 && token && feedPath) {
          try {
            const r = await fetch(`${API}/${feedPath}/${post.post_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (r.ok) {
              const full = await r.json();
              full.liked_by_me = (full.likes || []).includes(currentUser);
              full.seen_by_me = (full.seen_by || []).includes(currentUser);
              setPosts(prev => {
                if (prev.some(p => p.post_id === full.post_id)) return prev;
                const next = [full, ...prev];
                return [...next.filter(p => p.pinned), ...next.filter(p => !p.pinned)];
              });
              return;
            }
          } catch {}
        }
        post.liked_by_me = (post.likes || []).includes(currentUser);
        post.seen_by_me = (post.seen_by || []).includes(currentUser);
        setPosts(prev => {
          if (prev.some(p => p.post_id === post.post_id)) return prev;
          const next = [post, ...prev];
          return [...next.filter(p => p.pinned), ...next.filter(p => !p.pinned)];
        });
      });

      es.addEventListener("post.delete", (e) => {
        const { post_id } = JSON.parse(e.data);
        setPosts(prev => prev.filter(p => p.post_id !== post_id));
      });

      es.addEventListener("post.like", (e) => {
        const { post_id, likes } = JSON.parse(e.data);
        setPosts(prev => prev.map(p => p.post_id === post_id
          ? { ...p, likes, liked_by_me: (likes || []).includes(currentUser) }
          : p));
      });

      es.addEventListener("post.pin", (e) => {
        const { post_id, pinned } = JSON.parse(e.data);
        setPosts(prev => {
          const updated = prev.map(p => p.post_id === post_id ? { ...p, pinned } : p);
          return [...updated.filter(p => p.pinned), ...updated.filter(p => !p.pinned)];
        });
      });

      es.addEventListener("comment.new", (e) => {
        const { post_id, kommentar } = JSON.parse(e.data);
        setPosts(prev => prev.map(p => p.post_id === post_id
          ? { ...p, kommentarer: [...(p.kommentarer || []), kommentar] }
          : p));
      });

      es.addEventListener("comment.delete", (e) => {
        const { post_id, kommentar_id } = JSON.parse(e.data);
        setPosts(prev => prev.map(p => p.post_id === post_id
          ? { ...p, kommentarer: (p.kommentarer || []).filter(k => k.kommentar_id !== kommentar_id) }
          : p));
      });

      es.addEventListener("post.edit", (e) => {
        const { post_id, ...updates } = JSON.parse(e.data);
        setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, ...updates } : p));
      });

      es.onerror = () => {
        es.close();
        // Reconnect efter 3s
        retryRef.current = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      clearTimeout(retryRef.current);
      if (esRef.current) esRef.current.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseUrl]);
}

const CITY_COLORS = {
  "København":  "#FF2A6D",
  "Koebenhavn": "#FF2A6D",
  "Aarhus":     "#3B82F6",
  "Odense":     "#F59E0B",
  "Aalborg":    "#10B981",
  "Kolding":    "#8B5CF6",
  "Fælles":     "#6B7280",
  "Online":     "#0EA5E9",
};
const CITY_BG = {
  "København":  "rgba(255,42,109,0.12)",
  "Koebenhavn": "rgba(255,42,109,0.12)",
  "Aarhus":     "rgba(59,130,246,0.12)",
  "Odense":     "rgba(245,158,11,0.12)",
  "Aalborg":    "rgba(16,185,129,0.12)",
  "Kolding":    "rgba(139,92,246,0.12)",
  "Fælles":     "rgba(107,114,128,0.12)",
  "Online":     "rgba(14,165,233,0.12)",
};

const CITIES = ["København", "Aarhus", "Odense", "Aalborg", "Kolding"];
const CITIES_WITH_FAELLES = [...CITIES, "Fælles"];
const CITIES_WITH_FAELLES_ONLINE = [...CITIES, "Fælles", "Online"];

const FEED_TAGS = [
  { id: "kosmetolog",   label: "Kosmetolog",          color: "#a855f7" },
  { id: "frisor",       label: "Frisør",              color: "#3B82F6" },
  { id: "vigtig",       label: "Vigtigt",             color: "#EF4444" },
  { id: "andet",        label: "Andet",               color: "#6B7280" },
  { id: "koebenhavn",   label: "København",           color: "#FF2A6D" },
  { id: "aarhus",       label: "Aarhus",              color: "#3B82F6" },
  { id: "odense",       label: "Odense",              color: "#F59E0B" },
  { id: "aalborg",      label: "Aalborg",             color: "#10B981" },
  { id: "kolding",      label: "Kolding",             color: "#8B5CF6" },
];

const EVENT_TYPES = [
  "Eksamen", "Hold start", "Lash og Brow kursus", "Korean lash lift kursus",
  "Hotstone kursus", "TNBC besøg", "Studievejleder besøg", "Kunde aften",
  "Læge eksamen", "Læge undervisning", "Andet",
];

const MONTH_NAMES = ["Januar","Februar","Marts","April","Maj","Juni","Juli","August","September","Oktober","November","December"];
const DAY_NAMES = ["Ma","Ti","On","To","Fr","Lø","Sø"];

function fmtTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "lige nu";
    if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} dage siden`;
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ts; }
}

function fmtTsFull(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function fileIcon(ct = "") {
  if (ct.includes("pdf")) return "📄";
  if (ct.includes("word") || ct.includes("doc")) return "📝";
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return "📊";
  if (ct.includes("image")) return "🖼";
  return "📎";
}

// Module-level image cache — fetched once per session
let _memberImagePromise = null;
const _memberImageMap = {}; // name → file_id | null

function _ensureMemberImages(token) {
  if (_memberImagePromise) return _memberImagePromise;
  _memberImagePromise = axios.get(`${API}/hub/member-images`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => {
      const map = r.data?.images || {};
      for (const [name, fid] of Object.entries(map)) {
        _memberImageMap[name] = fid || null;
      }
    })
    .catch(() => {});
  return _memberImagePromise;
}

function Avatar({ name, size = "md", token }) {
  const sz = size === "lg" ? "w-11 h-11 text-base" : size === "sm" ? "w-7 h-7 text-[13px]" : "w-9 h-9 text-sm";
  const [fileId, setFileId] = useState(_memberImageMap[name] || null);
  const [ready, setReady] = useState(name in _memberImageMap);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!name || !token || ready) return;
    _ensureMemberImages(token).then(() => {
      setFileId(_memberImageMap[name] || null);
      setReady(true);
    });
  }, [name, token, ready]);

  if (ready && fileId && !imgError) {
    return (
      <img
        src={`${API}/podio/image/${fileId}`}
        alt={name}
        className={`${sz} rounded-full object-cover border border-border shrink-0`}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-[#FF2A6D]/20 to-purple-500/20 flex items-center justify-center shrink-0 font-semibold text-[#FF2A6D]`}>
      {name?.charAt(0)?.toUpperCase() || "?"}
    </div>
  );
}

// ── Kontakter (HUB-forside) ───────────────────────────────────────────────────
// Kompakt boks i sidebar, der viser totalt antal aktive medarbejdere og åbner
// en modal med søg/filter/visning. Returnerer KUN navn, stilling, by,
// arbejds-telefon og arbejds-email — INTET privat.

function KontaktBoks({ token }) {
  const [kontakter, setKontakter] = useState(null); // null = ikke hentet endnu
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [afdelingFilter, setAfdelingFilter] = useState("Alle");

  const ensureLoaded = useCallback(async () => {
    if (kontakter !== null || loading) return; // cache: kun hent én gang
    setLoading(true);
    setErrored(false);
    try {
      const r = await axios.get(`${API}/hub/kontakter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKontakter(r.data?.kontakter || []);
    } catch {
      setErrored(true);
      setKontakter([]); // tom liste så vi ikke retrier i en evig loop
    }
    setLoading(false);
  }, [kontakter, loading, token]);

  // Hent totalt antal ved mount så knappen kan vise "(N)"
  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  // Escape-tast lukker modal
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal]);

  const total = kontakter?.length ?? 0;

  const filtered = useMemo(() => {
    if (!kontakter) return [];
    const q = search.trim().toLowerCase();
    return kontakter.filter((k) => {
      if (afdelingFilter !== "Alle" && (k.afdeling || "") !== afdelingFilter) return false;
      if (!q) return true;
      return (
        (k.navn || "").toLowerCase().includes(q) ||
        (k.stilling || "").toLowerCase().includes(q) ||
        (k.afdeling || "").toLowerCase().includes(q)
      );
    });
  }, [kontakter, search, afdelingFilter]);

  return (
    <>
      <button
        type="button"
        onClick={() => { ensureLoaded(); setShowModal(true); }}
        className="w-full rounded-2xl border border-border bg-card hover:bg-secondary/40 transition-colors text-left shadow-sm overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-xl bg-[#FF2A6D]/10 flex items-center justify-center shrink-0">
            <Users className="w-4 h-4 text-[#FF2A6D]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">
              Kontakter {total > 0 && <span className="text-muted-foreground font-semibold">({total})</span>}
            </p>
            <p className="text-[13px] text-muted-foreground leading-tight mt-0.5">
              {loading && total === 0 ? "Henter..." : errored ? "Klik for at prøve igen" : "Alle aktive medarbejdere"}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </div>
      </button>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 pt-20"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[82vh] overflow-hidden flex flex-col border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-[#FF2A6D]/10 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-[#FF2A6D]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight">Kontakter</p>
                  <p className="text-[13px] text-muted-foreground leading-tight">{total} aktive medarbejdere</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Luk"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Søg + filter */}
            <div className="px-5 py-3 border-b border-border space-y-2 bg-secondary/10">
              <div className="flex items-center gap-2 bg-background rounded-xl border border-border px-3 py-2">
                <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Søg navn, stilling, by..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    aria-label="Ryd søgning"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider shrink-0">
                  Afdeling
                </label>
                <select
                  value={afdelingFilter}
                  onChange={(e) => setAfdelingFilter(e.target.value)}
                  className="flex-1 bg-background border border-border rounded-lg px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                >
                  <option value="Alle">Alle</option>
                  {CITIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Liste */}
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {!loading && errored && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-sm">Kunne ikke hente kontakter</p>
                  <button
                    type="button"
                    onClick={() => { setKontakter(null); ensureLoaded(); }}
                    className="mt-2 text-[13px] text-[#FF2A6D] font-semibold hover:underline"
                  >
                    Prøv igen
                  </button>
                </div>
              )}
              {!loading && !errored && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Users className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">Ingen kontakter fundet</p>
                </div>
              )}
              {!loading && !errored && filtered.length > 0 && (
                <ul className="space-y-1.5">
                  {filtered.map((k, idx) => (
                    <li
                      key={`${k.navn}-${idx}`}
                      className="rounded-xl border border-border bg-background px-3 py-2.5"
                    >
                      <p className="text-sm font-semibold leading-tight">{k.navn}</p>
                      {k.stilling && (
                        <p className="text-[13px] text-muted-foreground leading-tight mt-0.5">{k.stilling}</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
                        {k.afdeling && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {k.afdeling}
                          </span>
                        )}
                        {k.arbejds_telefon && (
                          <a
                            href={`tel:${k.arbejds_telefon.replace(/\s+/g, "")}`}
                            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                          >
                            <Phone className="w-3 h-3" />
                            {k.arbejds_telefon}
                          </a>
                        )}
                        {k.arbejds_email && (
                          <a
                            href={`mailto:${k.arbejds_email}`}
                            className="inline-flex items-center gap-1 hover:text-foreground transition-colors break-all"
                          >
                            <Mail className="w-3 h-3" />
                            {k.arbejds_email}
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ── Dashboard overview ────────────────────────────────────────────────────────

function DashboardOverview({ token, isAdmin, isSuperAdmin, isMember, currentUser, onNavigate }) {
  const now = new Date();
  const [events, setEvents] = useState([]);
  const [calEvents, setCalEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tilbud, setTilbud] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [replyTo, setReplyTo] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  // Feed composer state
  const [newTekst, setNewTekst] = useState("");
  const [newTitel, setNewTitel] = useState("");
  const [newTags, setNewTags] = useState([]);
  const [newSynlighed, setNewSynlighed] = useState(isAdmin || isSuperAdmin ? "alle" : "medarbejdere");
  const [showTitelField, setShowTitelField] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [posting, setPosting] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]);
  const [showSeen, setShowSeen] = useState({});
  const [showLikes, setShowLikes] = useState({});
  const [activeTags, setActiveTags] = useState([]);
  const [search, setSearch] = useState("");
  const [skjulAlle, setSkjulAlle] = useState(false);
  const [notifikationer, setNotifikationer] = useState([]);
  const imageInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // SSE — live feed-opdateringer
  const sseUrl = token ? `${SSE_BASE}/api/hub/feed/stream?token=${encodeURIComponent(token)}` : null;
  useFeedSSE(sseUrl, setPosts, currentUser, token, "hub/feed");

  // Notifikationer (fx godkendt/afvist ferieanmodning) — poll hvert minut.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const hentNotif = () => {
      axios.get(`${API}/hub/notifikationer`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (!cancelled) setNotifikationer(r.data.notifikationer || []); })
        .catch(() => {});
    };
    hentNotif();
    const t = setInterval(hentNotif, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  const markerNotifLaest = async (notif_id) => {
    setNotifikationer(prev => prev.filter(n => n.notif_id !== notif_id));
    try {
      await axios.post(`${API}/hub/notifikationer/${notif_id}/laest`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  };

  // Beregn mandag og lørdag for indeværende uge
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  const dow = monday.getDay();
  monday.setDate(monday.getDate() - (dow === 0 ? 6 : dow - 1));
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  saturday.setHours(23, 59, 59, 999);

  // Hent evt. to måneder hvis ugen spænder over månedsskiftet
  const fetchMonths = [];
  fetchMonths.push({ y: monday.getFullYear(), m: monday.getMonth() + 1 });
  if (saturday.getMonth() !== monday.getMonth()) {
    fetchMonths.push({ y: saturday.getFullYear(), m: saturday.getMonth() + 1 });
  }

  useEffect(() => {
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    const kalRequests = fetchMonths.map(({ y, m }) =>
      axios.get(`${API}/hub/kalender?year=${y}&month=${m}`, hdrs)
    );
    Promise.all([
      ...kalRequests,
      axios.get(`${API}/hub/feed?limit=50`, hdrs),
      axios.get(`${API}/hub/dokumenter`, hdrs),
      axios.get(`${API}/hub/tilbud`, hdrs).catch(() => ({ data: { tilbud: [] } })),
      axios.get(`${API}/hub/kalender?year=${now.getFullYear()}&month=${now.getMonth()+1}`, hdrs),
    ]).then(results => {
      const kalResults = results.slice(0, fetchMonths.length);
      const [feed, dok, til, curMonthKal] = results.slice(fetchMonths.length);
      const allEvents = kalResults.flatMap(r => r.data.events || []);
      setEvents(allEvents);
      setCalEvents(curMonthKal?.data?.events || []);
      setPosts(feed.data.posts || []);
      setDocs(dok.data.dokumenter || []);
      setTilbud(til.data.tilbud || []);
    }).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const weekNumber = (() => {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  })();

  // Filtrer events til indeværende uge — events har year+month+day fra backend
  const weekEvents = events
    .filter(e => {
      if (!e.year || !e.month || !e.day) return false;
      const d = new Date(e.year, e.month - 1, e.day);
      return d >= monday && d <= saturday;
    })
    .sort((a, b) => {
      const da = new Date(a.year, a.month - 1, a.day);
      const db = new Date(b.year, b.month - 1, b.day);
      return da - db;
    });

  // Fødselsdage i dag — tjek dag OG måned
  const todayBirthdays = events.filter(e =>
    e.type === "foedselsdag" &&
    e.day === now.getDate() &&
    e.month === now.getMonth() + 1
  );

  const handlePost = async () => {
    if (!newTekst.trim()) return;
    setPosting(true);
    try {

      if (attachedImages.length > 0) {
        const fd = new FormData();
        fd.append("titel", newTitel);
        fd.append("tekst", newTekst);
        fd.append("tags", JSON.stringify(newTags));
        fd.append("synlighed", newSynlighed);
        for (const img of attachedImages) fd.append("images", img.file, img.file.name);
        await axios.post(`${API}/hub/feed`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/hub/feed`, { titel: newTitel, tekst: newTekst, tags: newTags, synlighed: newSynlighed }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); setNewTags([]); setShowTagPicker(false);
      setNewSynlighed(isAdmin || isSuperAdmin ? "alle" : "medarbejdere");
    } catch {}
    setPosting(false);
  };

  const handleImageAttach = (e) => {
    const files = Array.from(e.target.files || []);
    const items = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setAttachedImages(prev => [...prev, ...items]);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setAttachedImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const handleLike = (post_id) => {
    // Optimistisk toggle af liked_by_me — SSE opdaterer likes-arrayet bagefter
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    axios.post(`${API}/hub/feed/${post_id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {
      // Reverter ved fejl
      setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    });
  };

  const handleSeen = async (post_id) => {
    await axios.post(`${API}/hub/feed/${post_id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p, seen_by_me: true, seen_by: [...new Set([...(p.seen_by||[]), currentUser])],
    } : p));
  };

  const handleDeletePost = async (post_id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    await axios.delete(`${API}/hub/feed/${post_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.filter(p => p.post_id !== post_id));
  };

  const handleDeleteComment = async (post_id, kommentar_id) => {
    await axios.delete(`${API}/hub/feed/${post_id}/kommentar/${kommentar_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id
      ? { ...p, kommentarer: (p.kommentarer||[]).filter(k => k.kommentar_id !== kommentar_id) } : p));
  };

  const handleComment = async (post_id) => {
    const tekst = (commentDraft[post_id] || "").trim();
    if (!tekst) return;
    setSendingComment(s => ({ ...s, [post_id]: true }));
    try {
      const body = { tekst };
      if (replyTo[post_id]) body.reply_to = replyTo[post_id];
      await axios.post(`${API}/hub/feed/${post_id}/kommentar`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommentDraft(d => ({ ...d, [post_id]: "" }));
      setReplyTo(r => ({ ...r, [post_id]: null }));
    } catch {}
    setSendingComment(s => ({ ...s, [post_id]: false }));
  };

  const handlePin = async (post_id) => {
    const r = await axios.post(`${API}/hub/feed/${post_id}/pin`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => {
      const updated = prev.map(p => p.post_id === post_id ? { ...p, pinned: r.data.pinned } : p);
      return [...updated.filter(p => p.pinned), ...updated.filter(p => !p.pinned)];
    });
  };

  const handleEditPost = async (post_id, updates) => {
    const r = await axios.patch(`${API}/hub/feed/${post_id}`, updates, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, ...r.data } : p));
  };

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      const hdrs = { headers: { Authorization: `Bearer ${token}` } };
      const params = q ? `?search=${encodeURIComponent(q)}` : "";
      axios.get(`${API}/hub/feed${params}&limit=50`, hdrs)
        .then(r => setPosts(r.data.posts || []));
    }, 400);
  };

  const filteredPosts = (activeTags.length === 0
    ? posts
    : posts.filter(p => activeTags.every(tag => (p.tags || []).includes(tag))))
    .filter(p => !skjulAlle || p.synlighed === "medarbejdere");

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Mini-kalender helpers (delt mellem højre kolonne) — beregnes én gang her
  const calCY = now.getFullYear(), calCM = now.getMonth() + 1, calCD = now.getDate();
  const calFirstDow = (() => { let d = new Date(calCY, calCM - 1, 1).getDay() - 1; return d < 0 ? 6 : d; })();
  const calDaysInMon = new Date(calCY, calCM, 0).getDate();
  const calGrid = [];
  for (let i = 0; i < calFirstDow; i++) calGrid.push(null);
  for (let d = 1; d <= calDaysInMon; d++) calGrid.push(d);
  const calDotsByDay = {};
  for (const ev of calEvents) {
    if (!ev.day) continue;
    if (!calDotsByDay[ev.day]) calDotsByDay[ev.day] = [];
    calDotsByDay[ev.day].push(ev.color || CITY_COLORS[ev.city] || "#6B7280");
  }
  const upcomingEvents = calEvents
    .filter(e => e.day && e.day >= calCD)
    .sort((a, b) => a.day - b.day)
    .slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Notifikationer — fx ferieanmodning godkendt/afvist */}
      {notifikationer.length > 0 && (
        <div className="space-y-2">
          {notifikationer.map(n => (
            <div key={n.notif_id} className={`flex items-start gap-3 p-3 rounded-2xl border ${
              n.type === "ferie_godkendt"
                ? "bg-emerald-500/10 border-emerald-500/30"
                : n.type === "ferie_afvist"
                ? "bg-red-500/10 border-red-500/30"
                : "bg-secondary/30 border-border"
            }`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="text-[13px] text-muted-foreground">{n.message}</p>
              </div>
              <button onClick={() => markerNotifLaest(n.notif_id)}
                aria-label="Markér som læst"
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* #48-followup: Live Topscorer-kassen ligger i tab-rækken (Dashboard.js),
          øverst ved siden af menuen — ude til højre, under avataren. */}

      {/* Fødselsdags-banner flyttet til højre sidebar (lige over Kalender). */}

      {/* Mobil (Krav #7): Tidsregistrering lige under fødselsdags-banneret,
          foldbar og foldet sammen som default. Skjult på desktop (lg) hvor
          boksen i stedet vises i højre sidebar. */}
      <div className="lg:hidden">
        {/* Periode-sum + Anmod om ferie sidder i BUNDEN af Tidsregistrering-boksen. */}
        <DagensArbejdstid
          token={token} currentUser={currentUser} collapsible defaultCollapsed
          footer={<PersonligKasse token={token} currentUser={currentUser} variant="periode" />}
        />
      </div>

      {/* Hoved-layout: 2/3 venstre (feed + halvkolonner) · 1/3 højre (sidebar) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

        {/* ── VENSTRE KOLONNE (2/3) ── */}
        <div className="space-y-4 lg:col-span-2 min-w-0">

          {/* Feed: composer + filter + posts uden ydre kasse/scroll.
              Feedet står altid åbent (den foldbare Feed-boks er fjernet). */}
          <div>
            {/* Fuldt feed — altid synligt på både mobil og desktop. */}
            <div className="block">
              {/* Composer + søg + tag-filter */}
              <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden mb-4">
                {/* Composer — medarbejdere, admin, superadmin */}
                {(isAdmin || isSuperAdmin || isMember) && (
                  <div className="border-b border-border">
                    <div className="flex items-start gap-3 p-4">
                      <Avatar name={currentUser} size="md" token={token} />
                      <div className="flex-1 min-w-0">
                        {showTitelField && (
                          <input type="text" placeholder="Overskrift (valgfri)" value={newTitel}
                            onChange={e => setNewTitel(e.target.value)}
                            className="w-full bg-secondary rounded-xl px-3 h-8 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 border border-border" />
                        )}
                        <textarea
                          placeholder={`Hvad tænker du på, ${currentUser?.split(" ")[0] || ""}?`}
                          value={newTekst}
                          onChange={e => setNewTekst(e.target.value)}
                          rows={newTekst ? 4 : 2}
                          className="w-full bg-secondary rounded-xl px-3 py-2 text-sm focus:outline-none resize-none border border-border focus:ring-2 focus:ring-[#FF2A6D]/30 transition-all"
                        />
                        {attachedImages.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {attachedImages.map((img, idx) => (
                              <div key={idx} className="relative">
                                <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-xl border border-border" />
                                <button onClick={() => removeImage(idx)}
                                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center shadow-sm hover:bg-red-500/10">
                                  <X className="w-3 h-3 text-muted-foreground" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {(newTekst || attachedImages.length > 0) && (
                      <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-secondary/20 flex-wrap">
                        <div className="w-full">
                          <p className="text-[13px] font-semibold mb-2">Synlighed</p>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="synlighed-composer" checked={newSynlighed === "alle"} onChange={() => setNewSynlighed("alle")} />
                              <span className="text-sm">For alle</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="synlighed-composer" checked={newSynlighed === "medarbejdere"} onChange={() => setNewSynlighed("medarbejdere")} />
                              <span className="text-sm">Kun for medarbejdere</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setShowTitelField(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors
                              ${showTitelField ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                            <Edit3 className="w-3.5 h-3.5" /> Overskrift
                          </button>
                          <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageAttach} />
                          <button onClick={() => imageInputRef.current?.click()}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-secondary transition-colors">
                            <ImageIcon className="w-3.5 h-3.5" /> Billede
                          </button>
                          <button onClick={() => setShowTagPicker(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${showTagPicker || newTags.length > 0 ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                            # Tags {newTags.length > 0 && `(${newTags.length})`}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); setNewTags([]); setShowTagPicker(false); setNewSynlighed(isAdmin || isSuperAdmin ? "alle" : "medarbejdere"); }}
                            className="px-3 py-1.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
                            Annuller
                          </button>
                          <button onClick={handlePost} disabled={posting || (!newTekst.trim() && attachedImages.length === 0)}
                            className="px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
                            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Del opslag
                          </button>
                        </div>
                        {showTagPicker && (
                          <div className="w-full flex flex-wrap gap-1.5">
                            {FEED_TAGS.map(tag => {
                              const active = newTags.includes(tag.id);
                              return (
                                <button key={tag.id}
                                  onClick={() => setNewTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                                  style={active
                                    ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                                    : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                                  # {tag.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {!showTagPicker && (
                          <div className="w-full flex flex-wrap gap-2 items-center">
                            <p className="text-[13px] text-muted-foreground">Foreslåede tags:</p>
                            {["andet"].map(tag => {
                              const active = newTags.includes(tag);
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => setNewTags(prev => active ? prev.filter(t => t !== tag) : [...prev, tag])}
                                  className={`text-[13px] px-2 py-0.5 rounded-full border transition-colors ${
                                    active
                                      ? "bg-[#FF2A6D]/10 border-[#FF2A6D]/40 text-[#FF2A6D]"
                                      : "bg-secondary border-border text-muted-foreground hover:border-[#FF2A6D]/40"
                                  }`}
                                >
                                  #{tag}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Søg + tag-filter */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
                  <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <input
                    type="text"
                    placeholder="Søg i opslag..."
                    value={search}
                    onChange={e => handleSearch(e.target.value)}
                    className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
                  />
                  {search && (
                    <button onClick={() => handleSearch("")} className="text-muted-foreground hover:text-foreground">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <label className="flex items-center gap-2 text-[13px] cursor-pointer shrink-0 pl-2 border-l border-border">
                    <input
                      type="checkbox"
                      checked={skjulAlle}
                      onChange={e => setSkjulAlle(e.target.checked)}
                      className="accent-[#FF2A6D]"
                    />
                    <span className="text-muted-foreground">Kun medarbejdere</span>
                  </label>
                </div>
                <div className="px-3 py-2 flex flex-wrap gap-x-2 gap-y-2">
                  {FEED_TAGS.map(tag => {
                    const active = activeTags.includes(tag.id);
                    return (
                      <button key={tag.id}
                        onClick={() => setActiveTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all whitespace-nowrap"
                        style={active
                          ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                          : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                        # {tag.label}
                      </button>
                    );
                  })}
                  {activeTags.length > 0 && (
                    <button onClick={() => setActiveTags([])}
                      className="px-2.5 py-1 rounded-full text-[13px] font-semibold border border-border text-muted-foreground hover:bg-secondary transition-colors whitespace-nowrap">
                      Ryd filter
                    </button>
                  )}
                </div>
              </div>

              {/* Opslag-liste */}
              <div className="space-y-3">
                {filteredPosts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm">{search || activeTags.length > 0 ? "Ingen opslag matcher søgning/filter" : "Ingen opslag endnu — vær den første!"}</p>
                  </div>
                ) : filteredPosts.map(post => (
                  <FeedPost key={post.post_id} post={post} currentUser={currentUser} token={token} API={API}
                    openComments={!!openComments[post.post_id]}
                    onToggleComments={() => {
                      setOpenComments(o => ({ ...o, [post.post_id]: !o[post.post_id] }));
                      if (!post.seen_by_me) handleSeen(post.post_id);
                    }}
                    onLike={() => handleLike(post.post_id)}
                    showSeen={false}
                    onToggleSeen={() => { if (!post.seen_by_me) handleSeen(post.post_id); }}
                    showLikes={!!showLikes[post.post_id]}
                    onToggleLikes={() => setShowLikes(s => ({ ...s, [post.post_id]: !s[post.post_id] }))}
                    commentDraft={commentDraft[post.post_id] || ""}
                    onCommentChange={v => setCommentDraft(d => ({ ...d, [post.post_id]: v }))}
                    onCommentSubmit={() => handleComment(post.post_id)}
                    sendingComment={!!sendingComment[post.post_id]}
                    replyTo={replyTo[post.post_id] || null}
                    onReply={(k) => { setReplyTo(r => ({ ...r, [post.post_id]: k })); setOpenComments(o => ({ ...o, [post.post_id]: true })); }}
                    onCancelReply={() => setReplyTo(r => ({ ...r, [post.post_id]: null }))}
                    onDelete={() => handleDeletePost(post.post_id)}
                    canDelete={isSuperAdmin || post.created_by === currentUser}
                    canEdit={isSuperAdmin || post.created_by === currentUser}
                    onEdit={(updates) => handleEditPost(post.post_id, updates)}
                    onDeleteComment={(kid) => handleDeleteComment(post.post_id, kid)}
                    canPin={isSuperAdmin}
                    onPin={() => handlePin(post.post_id)}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* ── HØJRE KOLONNE (1/3) ── */}
        <div className="space-y-4 lg:col-span-1 min-w-0">

          {/* Profilboks fjernet — profil tilgås via avatar i headeren. */}

          {/* I dag-boks — prominent gradient bevares. Kun desktop: på mobil
              vises boksen i stedet under fødselsdags-banneret (Krav #7).
              Periode-sum + Anmod om ferie sidder i bunden af boksen (footer). */}
          <div className="hidden lg:block">
            <DagensArbejdstid
              token={token} currentUser={currentUser}
              footer={<PersonligKasse token={token} currentUser={currentUser} variant="periode" />}
            />
          </div>

          {/* Kontakter-boks fjernet (#38.4) — medarbejderlisten tilgås nu via
              Medarbejdere-fanen, så boksen var overflødig på forsiden. */}

          {/* Fødselsdage i dag — kompakt version lige over kalenderen */}
          {todayBirthdays.length > 0 && (
            <div className="rounded-2xl p-3 bg-[#FF2A6D]/5 border border-[#FF2A6D]/20">
              <p className="text-[#FF2A6D] text-[12px] font-semibold uppercase tracking-widest mb-1">🎂 I dag</p>
              {todayBirthdays.map((b, i) => (
                <p key={i} className="text-sm font-bold text-foreground leading-snug">{b.name} har fødselsdag!</p>
              ))}
            </div>
          )}

          {/* Mini-kalender */}
          <div className="rounded-2xl border border-border overflow-hidden bg-card w-full">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border"
              style={{ background: "linear-gradient(135deg, rgba(255,42,109,0.08), rgba(139,92,246,0.04))" }}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-xl bg-[#FF2A6D]/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-[#FF2A6D]" />
                </div>
                <span className="font-bold text-sm truncate">Kalender</span>
              </div>
              <button onClick={() => onNavigate("kalender")} className="text-[13px] text-[#FF2A6D] font-semibold hover:underline shrink-0">Se alle →</button>
            </div>
            <div className="px-3 pt-2 pb-1">
              <div className="grid grid-cols-7 mb-0.5">
                {DAY_NAMES.map(d => (
                  <div key={d} className="text-center text-[12px] font-semibold text-muted-foreground py-0.5">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calGrid.map((day, i) => {
                  if (!day) return <div key={`e${i}`} />;
                  const isToday = day === calCD;
                  const dots = calDotsByDay[day] || [];
                  return (
                    <div key={`d${i}`} className="flex flex-col items-center py-0.5">
                      <span className={`text-[13px] font-semibold w-5 h-5 flex items-center justify-center rounded-full ${isToday ? "bg-[#FF2A6D] text-white" : "text-foreground"}`}>{day}</span>
                      {dots.length > 0 && (
                        <div className="flex gap-px mt-0.5">
                          {dots.slice(0, 3).map((c, idx) => (
                            <span key={idx} className="w-1 h-1 rounded-full" style={{ backgroundColor: c }} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {upcomingEvents.length > 0 && (
              <div className="border-t border-border divide-y divide-border">
                {upcomingEvents.map((ev, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: ev.color || CITY_COLORS[ev.city] || "#6B7280" }} />
                    <span className="text-[13px] text-muted-foreground shrink-0">{ev.day}. {MONTH_NAMES[calCM-1].slice(0,3)}</span>
                    <span className="text-[13px] font-medium truncate">{ev.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Aktuelle tilbud */}
          <div className="rounded-2xl border border-border overflow-hidden bg-card">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border"
              style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(234,179,8,0.04))" }}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                  <span className="text-sm">🏷️</span>
                </div>
                <span className="font-bold text-sm truncate">Aktuelle tilbud</span>
              </div>
              <button onClick={() => onNavigate("tilbud")} className="text-[13px] text-orange-500 font-semibold hover:underline shrink-0">Se alle →</button>
            </div>
            {tilbud.length === 0 ? (
              <div className="py-4 text-center text-[13px] text-muted-foreground">Ingen aktuelle tilbud</div>
            ) : (
              <div className="divide-y divide-border">
                {tilbud.slice(0, 3).map(t => (
                  <div key={t.tilbud_id} className="px-5 py-3">
                    <p className="text-[13px] font-semibold">{t.overskrift}</p>
                    {t.varighed && <p className="text-[13px] text-muted-foreground mt-0.5">{t.varighed}</p>}
                    {t.rabatkode && (
                      <span className="inline-block mt-1 text-[13px] font-mono bg-orange-500/10 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded-lg">{t.rabatkode}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filer */}
          <div className="rounded-2xl border border-border overflow-hidden bg-card">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border"
              style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(249,115,22,0.04))" }}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <FileText className="w-3.5 h-3.5 text-amber-500" />
                </div>
                <span className="font-bold text-sm truncate">Filer</span>
              </div>
              <button onClick={() => onNavigate("filer")} className="text-[13px] text-amber-500 font-semibold hover:underline shrink-0">Se alle →</button>
            </div>
            {docs.length === 0 ? (
              <div className="py-4 text-center text-[13px] text-muted-foreground">Ingen filer endnu</div>
            ) : (
              <div className="divide-y divide-border">
                {docs.slice(0, 5).map(doc => (
                  <div key={doc.doc_id} className="flex items-center gap-2.5 px-4 py-2.5">
                    <span className="text-base shrink-0">{fileIcon(doc.content_type)}</span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold truncate">{doc.filename}</p>
                      <p className="text-[13px] text-muted-foreground truncate">{fmtBytes(doc.size)} · {doc.uploaded_by}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}


// ── Kalender ──────────────────────────────────────────────────────────────────

const SortableTh = ({ col, label, sortCol, sortDir, onSort }) => (
  <th onClick={() => onSort(col)} className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none">
    <span className="flex items-center gap-1">
      {label}
      {sortCol === col ? (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
    </span>
  </th>
);

const fmtDato = (ev) => {
  if (!ev || !ev.day || !ev.month || !ev.year) return "";
  const d = new Date(ev.year, ev.month - 1, ev.day);
  return d.toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" });
};

function KalenderTab({ token, isAdmin, isSuperAdmin }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const [newEvent, setNewEvent] = useState({ event_type: "Eksamen", custom_title: "", city: "København", dato: todayStr, hele_dagen: true, fra_tid: "", til_tid: "" });
  const [adding, setAdding] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [reqForm, setReqForm] = useState({ event_type: "Andet", custom_title: "", dato: todayStr, beskrivelse: "", city: "København", hele_dagen: true, fra_tid: "", til_tid: "" });
  const [submittingReq, setSubmittingReq] = useState(false);
  const [reqSuccess, setReqSuccess] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [reqsLoading, setReqsLoading] = useState(false);

  // Liste-visning state
  const [visning, setVisning] = useState("maaned");
  const [allEvents, setAllEvents] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [listFilterType, setListFilterType] = useState("");
  const [listFilterBy, setListFilterBy] = useState("");
  const [listFilterPeriode, setListFilterPeriode] = useState("md");
  const [customFra, setCustomFra] = useState("");
  const [customTil, setCustomTil] = useState("");
  const [listSort, setListSort] = useState("date");
  const [listDir, setListDir] = useState("asc");

  const toggleFilter = (city) => {
    setActiveFilters(prev =>
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
    setSelectedDay(null);
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/hub/kalender?year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setEvents(r.data.events || [])).catch(() => {}).finally(() => setLoading(false));
  }, [token, year, month]);

  useEffect(() => { load(); }, [load]);

  // Hent -12 til +6 måneder events når liste-visning er aktiv (dækker alle filter-cases)
  const loadList = useCallback(() => {
    setListLoading(true);
    const today = new Date();
    const months = [];
    for (let i = -12; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({ y: d.getFullYear(), m: d.getMonth() + 1 });
    }
    Promise.all(months.map(({ y, m }) =>
      axios.get(`${API}/hub/kalender?year=${y}&month=${m}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.data.events || [])
        .catch(() => [])
    )).then(results => {
      setAllEvents(results.flat());
    }).finally(() => setListLoading(false));
  }, [token]);

  useEffect(() => {
    if (visning === "liste" && allEvents.length === 0) loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visning]);

  const loadRequests = useCallback(() => {
    if (!isSuperAdmin) return;
    setReqsLoading(true);
    axios.get(`${API}/admin/kalender-requests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPendingRequests((r.data.requests || []).filter(req => req.status === "pending")))
      .catch(() => {})
      .finally(() => setReqsLoading(false));
  }, [token, isSuperAdmin]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleGodkend = async (request_id) => {
    await axios.put(`${API}/admin/kalender-requests/${request_id}/godkend`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPendingRequests(prev => prev.filter(r => r.request_id !== request_id));
    load();
  };

  const handleAfvis = async (request_id) => {
    await axios.put(`${API}/admin/kalender-requests/${request_id}/afvis`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPendingRequests(prev => prev.filter(r => r.request_id !== request_id));
  };

  const handleSubmitRequest = async () => {
    setSubmittingReq(true);
    try {
      await axios.post(`${API}/hub/kalender/request`, reqForm, { headers: { Authorization: `Bearer ${token}` } });
      setReqSuccess(true);
      setShowRequest(false);
      setTimeout(() => setReqSuccess(false), 4000);
    } catch {}
    setSubmittingReq(false);
  };

  const prevMonth = () => { if (month === 1) { setYear(y => y-1); setMonth(12); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 12) { setYear(y => y+1); setMonth(1); } else setMonth(m => m+1); };

  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const filteredEvents = activeFilters.length === 0
    ? events
    : events.filter(e => e.type === "helligdag" || e.city === "Fælles" || e.city === "Online" || activeFilters.includes(e.city));

  const eventsByDay = {};
  for (const ev of filteredEvents) {
    const d = ev.day;
    if (!eventsByDay[d]) eventsByDay[d] = [];
    eventsByDay[d].push(ev);
  }

  const listEvents = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let fra = null;
    let til = null;
    if (listFilterPeriode === "md") {
      fra = new Date(today.getFullYear(), today.getMonth(), 1);
      til = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (listFilterPeriode === "3m") {
      fra = startOfToday;
      til = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
    } else if (listFilterPeriode === "6m") {
      fra = startOfToday;
      til = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());
    } else if (listFilterPeriode === "b3m") {
      fra = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "b6m") {
      fra = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "bAar") {
      fra = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "custom") {
      if (customFra) {
        const [fy, fm, fd] = customFra.split("-").map(Number);
        fra = new Date(fy, fm - 1, fd);
      }
      if (customTil) {
        const [ty, tm, td] = customTil.split("-").map(Number);
        til = new Date(ty, tm - 1, td);
      }
    }
    return allEvents
      .filter(ev => {
        if (!ev.day || !ev.month || !ev.year) return false;
        const d = new Date(ev.year, ev.month - 1, ev.day);
        if (listFilterPeriode === "alt") {
          // Ingen dato-grænse
        } else {
          if (fra && d < fra) return false;
          if (til && d > til) return false;
        }
        if (listFilterType && ev.event_type !== listFilterType) return false;
        if (listFilterBy && ev.city !== listFilterBy) return false;
        if (listSearch) {
          const q = listSearch.toLowerCase();
          if (!((ev.title || "").toLowerCase().includes(q) || (ev.event_type || "").toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => {
        let av, bv;
        if (listSort === "date") {
          av = new Date(a.year, a.month - 1, a.day).getTime();
          bv = new Date(b.year, b.month - 1, b.day).getTime();
        } else {
          av = (a[listSort] || "").toString().toLowerCase();
          bv = (b[listSort] || "").toString().toLowerCase();
        }
        if (av === bv) return 0;
        return listDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
  }, [allEvents, listFilterType, listFilterBy, listFilterPeriode, customFra, customTil, listSearch, listSort, listDir]);

  const handleSort = (col) => {
    if (listSort === col) setListDir(d => d === "asc" ? "desc" : "asc");
    else { setListSort(col); setListDir("asc"); }
  };

  const handleAddEvent = async () => {
    setAdding(true);
    try {
      const [evYear, evMonth, evDay] = newEvent.dato.split("-").map(Number);
      const title = newEvent.event_type === "Andet" ? newEvent.custom_title : newEvent.event_type;
      await axios.post(`${API}/hub/kalender`, {
        year: evYear, month: evMonth, day: evDay,
        event_type: newEvent.event_type,
        custom_title: newEvent.custom_title, title, city: newEvent.city,
        color: CITY_COLORS[newEvent.city] || "#6B7280",
        hele_dagen: !!newEvent.hele_dagen,
        fra_tid: newEvent.hele_dagen ? "" : (newEvent.fra_tid || ""),
        til_tid: newEvent.hele_dagen ? "" : (newEvent.til_tid || ""),
      }, { headers: { Authorization: `Bearer ${token}` } });
      // If event is in current view month, reload
      if (evYear === year && evMonth === month) load();
      else { setYear(evYear); setMonth(evMonth); }
      setAllEvents([]);
      setShowAdd(false);
    } catch {}
    setAdding(false);
  };

  const handleDelete = async (event_id) => {
    await axios.delete(`${API}/hub/kalender/${event_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setEvents(prev => prev.filter(e => e.event_id !== event_id));
    setAllEvents(prev => prev.filter(e => e.event_id !== event_id));
    setSelectedDay(null);
    setSelectedEvent(null);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar — lækker på mobil: navigation/toggle på én linje, knapperne i
          deres egen lige-delte række nedenunder. Desktop: alt på én linje. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3">
          {visning === "maaned" && (
            <div className="flex items-center gap-1 sm:gap-2 min-w-0">
              <button onClick={prevMonth} aria-label="Forrige måned"
                className="p-2 rounded-lg hover:bg-secondary active:bg-secondary/70 transition-colors shrink-0"><ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4" /></button>
              <h2 className="text-base font-bold text-center w-36 sm:w-44 truncate">{MONTH_NAMES[month-1]} {year}</h2>
              <button onClick={nextMonth} aria-label="Næste måned"
                className="p-2 rounded-lg hover:bg-secondary active:bg-secondary/70 transition-colors shrink-0"><ChevronRight className="w-5 h-5 sm:w-4 sm:h-4" /></button>
            </div>
          )}
          <div className="inline-flex rounded-full border border-border bg-secondary p-1 text-[13px] font-semibold shrink-0">
            <button
              onClick={() => setVisning("maaned")}
              className={`px-3.5 py-1.5 sm:py-1 rounded-full transition-colors ${visning === "maaned" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Måned
            </button>
            <button
              onClick={() => setVisning("liste")}
              className={`px-3.5 py-1.5 sm:py-1 rounded-full transition-colors ${visning === "liste" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Liste
            </button>
          </div>
        </div>
        <div className="flex items-stretch gap-2">
          <button onClick={() => { setReqForm({ event_type: "Andet", custom_title: "", dato: todayStr, beskrivelse: "", city: "København", hele_dagen: true, fra_tid: "", til_tid: "" }); setShowRequest(true); }}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg border border-border text-[13px] font-semibold hover:bg-secondary active:bg-secondary/70 transition-colors">
            <Plus className="w-3.5 h-3.5 shrink-0" /> Foreslå
          </button>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 active:opacity-80 transition-opacity">
              <Plus className="w-3.5 h-3.5 shrink-0" /> Tilføj begivenhed
            </button>
          )}
        </div>
      </div>

      {reqSuccess && (
        <div className="rounded-xl bg-green-500/10 border border-green-500/20 px-4 py-2.5 text-[13px] font-medium text-green-600 dark:text-green-400">
          Din begivenhed er sendt til godkendelse
        </div>
      )}

      {isSuperAdmin && pendingRequests.length > 0 && (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-amber-500/5 flex items-center justify-between">
            <span className="text-[13px] font-bold text-amber-600 dark:text-amber-400">Afventende requests ({pendingRequests.length})</span>
            {reqsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="divide-y divide-border">
            {pendingRequests.map(req => (
              <div key={req.request_id} className="px-4 py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold">{req.event_type === "Andet" ? req.custom_title : req.event_type}</p>
                  <p className="text-[13px] text-muted-foreground mt-0.5">{req.dato}{req.city ? ` · ${req.city}` : ""}</p>
                  {/* Opfølgning #34 (#1): vis hele beskrivelsen så godkenderen ved hvad hun godkender */}
                  {req.beskrivelse && req.beskrivelse.trim() && (
                    <p className="text-[13px] text-foreground mt-1 whitespace-pre-wrap break-words bg-secondary/40 rounded-lg px-2.5 py-1.5">{req.beskrivelse}</p>
                  )}
                  <p className="text-[13px] text-muted-foreground mt-1">Foreslået af {req.requested_by}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => handleGodkend(req.request_id)}
                    className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors">
                    Godkend
                  </button>
                  <button onClick={() => handleAfvis(req.request_id)}
                    className="px-2.5 py-1 rounded-lg text-[13px] font-semibold bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors">
                    Afvis
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visning === "maaned" && (<>
      {/* By-filter */}
      <div className="flex flex-wrap gap-2 items-center">
        {CITIES.map(city => {
          const active = activeFilters.includes(city);
          return (
            <button key={city}
              onClick={() => toggleFilter(city)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all"
              style={active
                ? { backgroundColor: CITY_COLORS[city], color: "#fff" }
                : { backgroundColor: CITY_BG[city], color: CITY_COLORS[city] }}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: active ? "rgba(255,255,255,0.7)" : CITY_COLORS[city] }} />
              {city}
            </button>
          );
        })}
        {activeFilters.length > 0 && (
          <button onClick={() => { setActiveFilters([]); setSelectedDay(null); }}
            className="px-2.5 py-1 rounded-full text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            Ryd filter
          </button>
        )}
        <span className="text-[13px] text-muted-foreground/50 ml-1">· Fælles og helligdage vises altid</span>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter kalender...</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="grid grid-cols-[28px_repeat(7,1fr)] md:grid-cols-[36px_repeat(7,1fr)] border-b border-border bg-secondary/30">
            <div className="py-2.5" />
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-[13px] font-semibold text-muted-foreground py-2.5">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-[28px_repeat(7,1fr)] md:grid-cols-[36px_repeat(7,1fr)]">
            {(() => {
              // Beregn ISO ugenummer for en dato
              const isoWeek = (date) => {
                const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
                const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
              };

              const cells = [];
              days.forEach((day, i) => {
                const colInRow = i % 7;
                // Indsæt uge-celle som første element i hver række
                if (colInRow === 0) {
                  const cellDate = day
                    ? new Date(year, month - 1, day)
                    : new Date(year, month - 1, 1 - startDow + i);
                  const wn = isoWeek(cellDate);
                  cells.push(
                    <div key={`w${i}`} className="border-b border-r border-border bg-secondary/5 flex items-start justify-center pt-2">
                      <span className="text-[12px] md:text-[12px] font-bold text-muted-foreground/50">{wn}</span>
                    </div>
                  );
                }

                const dayEvents = day ? (eventsByDay[day] || []) : [];
                const isToday = day && year === now.getFullYear() && month === now.getMonth()+1 && day === now.getDate();
                const isSelected = day === selectedDay;

                cells.push(
                  <div key={`d${i}`}
                    onClick={() => day && setSelectedDay(selectedDay === day ? null : day)}
                    className={`min-h-[56px] md:min-h-[120px] border-b border-r border-border p-1 md:p-1.5 transition-colors
                      ${day ? "cursor-pointer hover:bg-secondary/20" : "bg-secondary/5"}
                      ${isSelected ? "bg-secondary/30" : ""}`}>
                    {day && (
                      <>
                        <div className={`text-[13px] md:text-[13px] font-bold mb-0.5 md:mb-1 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full mx-auto
                          ${isToday ? "bg-[#FF2A6D] text-white" : "text-foreground"}`}>
                          {day}
                        </div>
                        {/* Mobil: farveprikker */}
                        <div className="flex md:hidden flex-wrap gap-0.5 justify-center mt-0.5">
                          {dayEvents.slice(0, 3).map((ev, idx) => (
                            <button key={idx}
                              onClick={e => { e.stopPropagation(); setSelectedEvent({ ...ev, day, month, year }); }}
                              className="w-2 h-2 rounded-full shrink-0 hover:scale-125 transition-transform"
                              style={{ backgroundColor: ev.color || "#6B7280" }} />
                          ))}
                          {dayEvents.length > 3 && <span className="text-[12px] text-muted-foreground leading-none">+{dayEvents.length-3}</span>}
                        </div>
                        {/* Desktop: 3-linjers event-kort */}
                        <div className="hidden md:block space-y-1">
                          {dayEvents.slice(0, 2).map((ev, idx) => {
                            const erHeleDagen = ev.hele_dagen || (!ev.fra_tid && !ev.til_tid);
                            const erFoedselsdag = ev.type === "foedselsdag";
                            const erHelligdag = ev.type === "helligdag";
                            const tidsTekst = erFoedselsdag || erHelligdag || erHeleDagen
                              ? "Hele dagen"
                              : `${ev.fra_tid || ""} – ${ev.til_tid || ""}`;
                            return (
                              <button key={idx}
                                onClick={e => { e.stopPropagation(); setSelectedEvent({ ...ev, day, month, year }); }}
                                className="w-full text-left rounded px-1.5 py-1 leading-tight hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: (ev.color || "#6B7280") + "20", color: ev.color || "#6B7280" }}>
                                <div className="text-[13px] font-semibold truncate flex items-center gap-1">
                                  {ev.city === "Online" && <Globe className="w-3 h-3 shrink-0" />}
                                  <span className="truncate">{ev.title}</span>
                                </div>
                                <div className="text-[12px] opacity-80 truncate">{tidsTekst}</div>
                                {ev.city && <div className="text-[12px] opacity-70 truncate">{ev.city}</div>}
                              </button>
                            );
                          })}
                          {dayEvents.length > 2 && (
                            <button onClick={e => { e.stopPropagation(); setSelectedDay(day); }}
                              className="w-full text-left text-[12px] text-muted-foreground hover:text-foreground px-1.5 py-0.5">
                              + {dayEvents.length - 2} flere
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              });
              return cells;
            })()}
          </div>
        </div>
      )}
      </>)}

      {visning === "liste" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input type="text" placeholder="Søg på navn eller type..." value={listSearch}
              onChange={e => setListSearch(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm w-full max-w-xs focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            <select value={listFilterType} onChange={e => setListFilterType(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="">Alle typer</option>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={listFilterBy} onChange={e => setListFilterBy(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="">Alle byer</option>
              {CITIES_WITH_FAELLES_ONLINE.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={listFilterPeriode} onChange={e => setListFilterPeriode(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="md">Indeværende måned</option>
              <option value="3m">Næste 3 mdr</option>
              <option value="6m">Næste 6 mdr</option>
              <option value="b3m">Sidste 3 mdr</option>
              <option value="b6m">Sidste 6 mdr</option>
              <option value="bAar">Sidste år</option>
              <option value="alt">Alt</option>
              <option value="custom">Tilpasset</option>
            </select>
            {listFilterPeriode === "custom" && (
              <>
                <input type="date" value={customFra} onChange={e => setCustomFra(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none" />
                <input type="date" value={customTil} onChange={e => setCustomTil(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none" />
              </>
            )}
            <button onClick={loadList} disabled={listLoading}
              className="px-3 h-9 rounded-lg border border-border text-[13px] font-medium hover:bg-secondary transition-colors disabled:opacity-50">
              {listLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Opdater"}
            </button>
          </div>

          <div className="rounded-xl border border-border overflow-hidden">
            {listLoading && allEvents.length === 0 ? (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <p className="text-[13px] text-muted-foreground mt-3">Henter begivenheder...</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/50">
                    <SortableTh col="date" label="Dato" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <SortableTh col="title" label="Begivenhed" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <SortableTh col="event_type" label="Type" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Tidspunkt</th>
                    <SortableTh col="city" label="By / lokation" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {listEvents.map((ev, i) => {
                    const erHeleDagen = ev.hele_dagen || (!ev.fra_tid && !ev.til_tid);
                    const erFoedselsdag = ev.type === "foedselsdag";
                    const erHelligdag = ev.type === "helligdag";
                    const tidsTekst = erFoedselsdag || erHelligdag || erHeleDagen
                      ? "Hele dagen"
                      : `${ev.fra_tid || ""} – ${ev.til_tid || ""}`;
                    return (
                      <tr key={ev.event_id || `${ev.year}-${ev.month}-${ev.day}-${i}`}
                        className="border-b border-border hover:bg-secondary/30 cursor-pointer"
                        onClick={() => setSelectedEvent({ ...ev })}>
                        <td className="py-2 px-4 text-[13px] tabular-nums">{fmtDato(ev)}</td>
                        <td className="py-2 px-4 text-sm font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.color || "#6B7280" }} />
                            {ev.city === "Online" && <Globe className="inline w-3.5 h-3.5 text-blue-500" />}
                            {ev.title}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground">{ev.event_type || (ev.type === "helligdag" ? "Helligdag" : ev.type === "foedselsdag" ? "Fødselsdag" : ev.type === "jubilaeum" ? "Jubilæum" : "—")}</td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground tabular-nums">{tidsTekst}</td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground">{ev.city || "—"}</td>
                      </tr>
                    );
                  })}
                  {listEvents.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Ingen begivenheder fundet</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Event popup modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setSelectedEvent(null)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Farvet top-bar */}
            <div className="h-1.5 w-full" style={{ backgroundColor: selectedEvent.color || "#6B7280" }} />
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full shrink-0 mt-0.5" style={{ backgroundColor: selectedEvent.color || "#6B7280" }} />
                  <p className="font-bold text-sm leading-snug">
                    {selectedEvent.city === "Online" && <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {selectedEvent.title}
                  </p>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-muted-foreground hover:text-foreground shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1.5 pl-5">
                <p className="text-[13px] text-muted-foreground">
                  {selectedEvent.day}. {MONTH_NAMES[(selectedEvent.month||month)-1]} {selectedEvent.year||year}
                  {selectedEvent.hele_dagen === false && selectedEvent.fra_tid && selectedEvent.til_tid && (
                    <> · {selectedEvent.fra_tid}–{selectedEvent.til_tid}</>
                  )}
                </p>
                {selectedEvent.city && (
                  <p className="text-[13px] text-muted-foreground">
                    {selectedEvent.city}
                    {selectedEvent.subtype === "elev" && " · Elev"}
                    {selectedEvent.subtype === "medarbejder" && " · Medarbejder"}
                  </p>
                )}
                {selectedEvent.type === "helligdag" && (
                  <p className="text-[13px] text-muted-foreground">Dansk helligdag</p>
                )}
                {selectedEvent.event_type && selectedEvent.type !== "helligdag" && (
                  <p className="text-[13px] text-muted-foreground">{selectedEvent.event_type}</p>
                )}
              </div>
              {/* Opfølgning #34 (#1): vis hele beskrivelsen som egen sektion */}
              {selectedEvent.beskrivelse && selectedEvent.beskrivelse.trim() && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-1">Beskrivelse</p>
                  <p className="text-[13px] text-foreground whitespace-pre-wrap break-words">{selectedEvent.beskrivelse}</p>
                </div>
              )}
              {isAdmin && selectedEvent.event_id && (
                <div className="mt-4 pt-3 border-t border-border flex justify-end">
                  <button onClick={() => handleDelete(selectedEvent.event_id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-red-500 hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Slet begivenhed
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dag-detail */}
      {selectedDay && eventsByDay[selectedDay] && (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/20">
            <p className="text-sm font-bold">{selectedDay}. {MONTH_NAMES[month-1]} {year}</p>
          </div>
          <div className="divide-y divide-border">
            {eventsByDay[selectedDay].map((ev, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ev.color||"#6B7280" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {ev.city === "Online" && <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {ev.title}
                    {ev.hele_dagen === false && ev.fra_tid && ev.til_tid && (
                      <span className="ml-2 text-[13px] text-muted-foreground font-normal">{ev.fra_tid}–{ev.til_tid}</span>
                    )}
                  </p>
                  {ev.city && <p className="text-[13px] text-muted-foreground">{ev.city} {ev.subtype === "elev" ? "· Elev" : ev.subtype === "medarbejder" ? "· Medarbejder" : ""}</p>}
                </div>
                {ev.event_id && (
                  <button onClick={() => handleDelete(ev.event_id)}
                    className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tilføj modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Ny begivenhed</h3>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Dato</label>
                <input type="date" value={newEvent.dato}
                  onChange={e => setNewEvent(n => ({...n, dato: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Type</label>
                <select value={newEvent.event_type} onChange={e => setNewEvent(n => ({...n, event_type: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none">
                  {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              {newEvent.event_type === "Andet" && (
                <div>
                  <label className="text-[13px] text-muted-foreground mb-1 block">Titel</label>
                  <input type="text" placeholder="Beskriv..." value={newEvent.custom_title}
                    onChange={e => setNewEvent(n => ({...n, custom_title: e.target.value}))}
                    className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
              )}
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">By</label>
                <div className="flex flex-wrap gap-1.5">
                  {CITIES_WITH_FAELLES_ONLINE.map(city => (
                    <button key={city} onClick={() => setNewEvent(n => ({...n, city}))}
                      className={`px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all`}
                      style={newEvent.city === city
                        ? { backgroundColor: CITY_COLORS[city], color: "white" }
                        : { backgroundColor: CITY_BG[city]||"rgba(107,114,128,0.08)", color: CITY_COLORS[city]||"#6B7280" }}>
                      {city === "Online" && <Globe className="inline w-3 h-3 mr-1" />}
                      {city}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Tidspunkt</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-add" checked={!newEvent.hele_dagen}
                      onChange={() => setNewEvent(n => ({ ...n, hele_dagen: false }))} />
                    <span className="text-sm">Bestemt tidspunkt</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-add" checked={!!newEvent.hele_dagen}
                      onChange={() => setNewEvent(n => ({ ...n, hele_dagen: true, fra_tid: "", til_tid: "" }))} />
                    <span className="text-sm">Hele dagen</span>
                  </label>
                </div>
                {!newEvent.hele_dagen && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Fra</label>
                      <input type="time" value={newEvent.fra_tid || ""}
                        onChange={e => setNewEvent(n => ({ ...n, fra_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Til</label>
                      <input type="time" value={newEvent.til_tid || ""}
                        onChange={e => setNewEvent(n => ({ ...n, til_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button onClick={handleAddEvent} disabled={adding || !newEvent.dato || (newEvent.event_type==="Andet"&&!newEvent.custom_title.trim())}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tilføj"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Foreslå begivenhed modal */}
      {showRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Foreslå begivenhed</h3>
              <button onClick={() => setShowRequest(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Dato</label>
                <input type="date" value={reqForm.dato}
                  onChange={e => setReqForm(f => ({...f, dato: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Type</label>
                <select value={reqForm.event_type} onChange={e => setReqForm(f => ({...f, event_type: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none">
                  {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Titel</label>
                <input type="text" placeholder="Hvad handler det om?" value={reqForm.custom_title}
                  onChange={e => setReqForm(f => ({...f, custom_title: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Beskrivelse (valgfri)</label>
                <input type="text" placeholder="Kort beskrivelse..." value={reqForm.beskrivelse}
                  onChange={e => setReqForm(f => ({...f, beskrivelse: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">By</label>
                <div className="flex flex-wrap gap-1.5">
                  {CITIES_WITH_FAELLES_ONLINE.map(city => (
                    <button key={city} onClick={() => setReqForm(f => ({...f, city}))}
                      className="px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all"
                      style={reqForm.city === city
                        ? { backgroundColor: CITY_COLORS[city] || "#6B7280", color: "white" }
                        : { backgroundColor: CITY_BG[city] || "rgba(107,114,128,0.08)", color: CITY_COLORS[city] || "#6B7280" }}>
                      {city === "Online" && <Globe className="inline w-3 h-3 mr-1" />}
                      {city}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Tidspunkt</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-req" checked={!reqForm.hele_dagen}
                      onChange={() => setReqForm(f => ({ ...f, hele_dagen: false }))} />
                    <span className="text-sm">Bestemt tidspunkt</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-req" checked={!!reqForm.hele_dagen}
                      onChange={() => setReqForm(f => ({ ...f, hele_dagen: true, fra_tid: "", til_tid: "" }))} />
                    <span className="text-sm">Hele dagen</span>
                  </label>
                </div>
                {!reqForm.hele_dagen && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Fra</label>
                      <input type="time" value={reqForm.fra_tid || ""}
                        onChange={e => setReqForm(f => ({ ...f, fra_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Til</label>
                      <input type="time" value={reqForm.til_tid || ""}
                        onChange={e => setReqForm(f => ({ ...f, til_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowRequest(false)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button onClick={handleSubmitRequest} disabled={submittingReq || !reqForm.dato || !reqForm.custom_title.trim()}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {submittingReq ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send forslag"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Feed (Facebook-stil) ──────────────────────────────────────────────────────

function FeedTab({ token, currentUser, isAdmin, isSuperAdmin }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [activeTags, setActiveTags] = useState([]); // filter
  const [newTekst, setNewTekst] = useState("");
  const [newTitel, setNewTitel] = useState("");
  const [newTags, setNewTags] = useState([]); // tags på nyt opslag
  const [showTitelField, setShowTitelField] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [replyTo, setReplyTo] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [showSeen, setShowSeen] = useState({});
  const [showLikes, setShowLikes] = useState({});
  const [attachedImages, setAttachedImages] = useState([]);
  const imageInputRef = useRef(null);
  const searchTimeout = useRef(null);
  const composerRef = useRef(null);

  const load = useCallback((q = "") => {
    const params = q ? `?search=${encodeURIComponent(q)}` : "";
    axios.get(`${API}/hub/feed${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPosts(r.data.posts || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const filteredPosts = activeTags.length === 0
    ? posts
    : posts.filter(p => activeTags.every(tag => (p.tags || []).includes(tag)));

  useEffect(() => { load(); }, [load]);

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimeout.current);
    setSearching(true);
    searchTimeout.current = setTimeout(() => { load(q); setSearching(false); }, 400);
  };

  const handlePost = async () => {
    if (!newTekst.trim()) return;
    setPosting(true);
    try {

      if (attachedImages.length > 0) {
        const fd = new FormData();
        fd.append("titel", newTitel);
        fd.append("tekst", newTekst);
        fd.append("tags", JSON.stringify(newTags));
        for (const img of attachedImages) fd.append("images", img.file, img.file.name);
        await axios.post(`${API}/hub/feed`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/hub/feed`, { titel: newTitel, tekst: newTekst, tags: newTags }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); setNewTags([]); setShowTagPicker(false);
    } catch {}
    setPosting(false);
  };

  const handleImageAttach = (e) => {
    const files = Array.from(e.target.files || []);
    const items = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setAttachedImages(prev => [...prev, ...items]);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setAttachedImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const handleLike = (post_id) => {
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    axios.post(`${API}/hub/feed/${post_id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {
      setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    });
  };

  const handleSeen = async (post_id) => {
    await axios.post(`${API}/hub/feed/${post_id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p, seen_by_me: true, seen_by: [...new Set([...(p.seen_by||[]), currentUser])],
    } : p));
  };

  const handleComment = async (post_id) => {
    const tekst = (commentDraft[post_id]||"").trim();
    if (!tekst) return;
    setSendingComment(s => ({...s, [post_id]: true}));
    try {
      const body = { tekst };
      if (replyTo[post_id]) body.reply_to = replyTo[post_id];
      const r = await axios.post(`${API}/hub/feed/${post_id}/kommentar`, body, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(prev => prev.map(p => p.post_id === post_id ? {...p, kommentarer: [...(p.kommentarer||[]), r.data]} : p));
      setCommentDraft(d => ({...d, [post_id]: ""}));
      setReplyTo(r => ({...r, [post_id]: null}));
    } catch {}
    setSendingComment(s => ({...s, [post_id]: false}));
  };

  const handleDeletePost = async (post_id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    await axios.delete(`${API}/hub/feed/${post_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.filter(p => p.post_id !== post_id));
  };

  const handleEditPost = async (post_id, updates) => {
    const r = await axios.patch(`${API}/hub/feed/${post_id}`, updates, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, ...r.data } : p));
  };

  const handleDeleteComment = async (post_id, kommentar_id) => {
    await axios.delete(`${API}/hub/feed/${post_id}/kommentar/${kommentar_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(prev => prev.map(p => p.post_id === post_id ? {...p, kommentarer: (p.kommentarer||[]).filter(k => k.kommentar_id !== kommentar_id)} : p));
  };

  return (
    <div className="max-w-[680px] mx-auto space-y-4">
      {/* Søgning */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input type="text" placeholder="Søg i opslag..." value={search} onChange={e => handleSearch(e.target.value)}
          className="w-full bg-secondary border border-border rounded-full pl-10 pr-4 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Tag-filter */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {FEED_TAGS.map(tag => {
          const active = activeTags.includes(tag.id);
          return (
            <button key={tag.id} onClick={() => setActiveTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
              style={active
                ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
              # {tag.label}
            </button>
          );
        })}
        {activeTags.length > 0 && (
          <button onClick={() => setActiveTags([])}
            className="px-2.5 py-1 rounded-full text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors border border-border">
            Ryd filter
          </button>
        )}
      </div>

      {/* Composer — kun for admins */}
      {isAdmin && <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="flex items-start gap-3 p-4">
          <Avatar name={currentUser} size="md" token={token} />
          <div ref={composerRef} className="flex-1">
            {showTitelField && (
              <input type="text" placeholder="Overskrift (valgfri)" value={newTitel}
                onChange={e => setNewTitel(e.target.value)}
                className="w-full bg-secondary rounded-xl px-3 h-8 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 border border-border" />
            )}
            <textarea
              placeholder={`Hvad tænker du på, ${currentUser?.split(" ")[0] || ""}?`}
              value={newTekst}
              onChange={e => setNewTekst(e.target.value)}
              rows={newTekst ? 4 : 2}
              className="w-full bg-secondary rounded-xl px-3 py-2 text-sm focus:outline-none resize-none border border-border focus:ring-2 focus:ring-[#FF2A6D]/30 transition-all"
            />
            {/* Attached image previews */}
            {attachedImages.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {attachedImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-xl border border-border" />
                    <button onClick={() => removeImage(idx)}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center shadow-sm hover:bg-red-500/10 transition-colors">
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {(newTekst || attachedImages.length > 0) && (
          <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-secondary/20">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowTitelField(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors
                  ${showTitelField ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                <Edit3 className="w-3.5 h-3.5" /> Overskrift
              </button>
              <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageAttach} />
              <button onClick={() => imageInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-secondary transition-colors">
                <ImageIcon className="w-3.5 h-3.5" /> Billede
              </button>
              <button onClick={() => setShowTagPicker(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${showTagPicker || newTags.length > 0 ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                # Tags {newTags.length > 0 && `(${newTags.length})`}
              </button>
            </div>
            {showTagPicker && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {FEED_TAGS.map(tag => {
                  const active = newTags.includes(tag.id);
                  return (
                    <button key={tag.id}
                      onClick={() => setNewTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                      style={active
                        ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                        : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                      # {tag.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={() => { setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); }}
                className="px-3 py-1.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
                Annuller
              </button>
              <button onClick={handlePost} disabled={posting || (!newTekst.trim() && attachedImages.length === 0)}
                className="px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Del opslag
              </button>
            </div>
          </div>
        )}
      </div>}

      {/* Feed */}
      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground rounded-2xl border border-border bg-card">
          <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">{search || activeTags.length > 0 ? "Ingen opslag matcher søgning/filter" : "Ingen opslag endnu — vær den første!"}</p>
        </div>
      ) : filteredPosts.map(post => (
        <FeedPost key={post.post_id} post={post} currentUser={currentUser} token={token} API={API}
          openComments={!!openComments[post.post_id]}
          onToggleComments={() => {
            setOpenComments(o => ({...o, [post.post_id]: !o[post.post_id]}));
            if (!post.seen_by_me) handleSeen(post.post_id);
          }}
          onLike={() => handleLike(post.post_id)}
          showSeen={false}
          onToggleSeen={() => { if (!post.seen_by_me) handleSeen(post.post_id); }}
          showLikes={!!showLikes[post.post_id]}
          onToggleLikes={() => setShowLikes(s => ({...s, [post.post_id]: !s[post.post_id]}))}
          commentDraft={commentDraft[post.post_id]||""}
          onCommentChange={v => setCommentDraft(d => ({...d, [post.post_id]: v}))}
          onCommentSubmit={() => handleComment(post.post_id)}
          sendingComment={!!sendingComment[post.post_id]}
          replyTo={replyTo[post.post_id] || null}
          onReply={(k) => { setReplyTo(r => ({...r, [post.post_id]: k})); setOpenComments(o => ({...o, [post.post_id]: true})); }}
          onCancelReply={() => setReplyTo(r => ({...r, [post.post_id]: null}))}
          onDelete={() => handleDeletePost(post.post_id)}
          canDelete={isSuperAdmin || (isAdmin && post.created_by === currentUser)}
          canEdit={isSuperAdmin || (isAdmin && post.created_by === currentUser)}
          onEdit={(updates) => handleEditPost(post.post_id, updates)}
          onDeleteComment={(kid) => handleDeleteComment(post.post_id, kid)}
        />
      ))}
    </div>
  );
}

// Authenticated image component for feed
function AuthImg({ src, token, className, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { url = URL.createObjectURL(blob); setBlobUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src, token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!blobUrl) return <div className={`${className} bg-secondary animate-pulse`} />;
  return <img src={blobUrl} alt="" className={className} onClick={onClick} />;
}

function FeedImages({ post_id, images, token, apiBase, feedPath, onLightbox }) {
  if (images.length === 0) return null;
  const path = feedPath || "hub/feed";
  return (
    <div className={`pb-3 ${images.length === 1 ? "" : "px-4"}`}>
      {images.length === 1 ? (
        <div className="flex justify-center cursor-pointer" onClick={() => onLightbox(0)}>
          <AuthImg src={`${apiBase}/${path}/${post_id}/image/0`} token={token}
            className="max-w-full max-h-[480px] object-contain" />
        </div>
      ) : (
        <div className={`grid gap-1 ${images.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {images.map((_, idx) => (
            <AuthImg key={idx} src={`${apiBase}/${path}/${post_id}/image/${idx}`} token={token}
              className="w-full h-36 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onLightbox(idx)} />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedLightbox({ post_id, images, token, apiBase, feedPath, idx, onClose, onPrev, onNext }) {
  const path = feedPath || "hub/feed";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={onClose}>
        <X className="w-6 h-6" />
      </button>
      {idx > 0 && (
        <button className="absolute left-4 text-white/80 hover:text-white p-3" onClick={e => { e.stopPropagation(); onPrev(); }}>
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}
      <AuthImg src={`${apiBase}/${path}/${post_id}/image/${idx}`} token={token}
        className="max-w-full max-h-[90vh] object-contain rounded-xl"
        onClick={e => e.stopPropagation()} />
      {idx < images.length - 1 && (
        <button className="absolute right-4 text-white/80 hover:text-white p-3" onClick={e => { e.stopPropagation(); onNext(); }}>
          <ChevronRight className="w-7 h-7" />
        </button>
      )}
    </div>
  );
}

function FeedPost({ post, currentUser, token, API: apiBase, openComments, onToggleComments, onLike, showSeen, onToggleSeen,
  showLikes, onToggleLikes, commentDraft, onCommentChange, onCommentSubmit, sendingComment, replyTo, onReply, onCancelReply,
  onDelete, canDelete, onDeleteComment, canPin, onPin, feedTagOverride, feedPath, canEdit, onEdit }) {
  const [showMenu, setShowMenu] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitel, setEditTitel] = useState("");
  const [editTekst, setEditTekst] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [seenHover, setSeenHover] = useState(false);
  const commentInputRef = useRef(null);
  const likeCount = (post.likes||[]).length;
  const commentCount = (post.kommentarer||[]).length;
  const seenCount = (post.seen_by||[]).length;
  const images = post.images || [];
  const tagSource = feedTagOverride || FEED_TAGS;
  const showMenuBtn = canDelete || canPin || canEdit;

  useEffect(() => {
    if (!post.seen_by_me) onToggleSeen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.post_id]);

  const startEdit = () => {
    setEditTitel(post.titel || "");
    setEditTekst(post.tekst || "");
    setEditTags(post.tags || []);
    setEditing(true);
    setShowMenu(false);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    if (!editTekst.trim()) return;
    setSaving(true);
    try {
      await onEdit({ titel: editTitel, tekst: editTekst, tags: editTags });
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div className={`rounded-2xl border bg-card shadow-sm overflow-hidden ${post.pinned ? "border-amber-400/50 ring-1 ring-amber-400/20" : "border-border"}`}>
      {post.pinned && (
        <div className="flex items-center gap-1.5 px-4 pt-2.5 pb-0">
          <Pin className="w-3 h-3 text-amber-500" />
          <span className="text-[13px] font-semibold text-amber-500 uppercase tracking-wide">Fastgjort opslag</span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex items-center gap-3">
          <Avatar name={post.created_by} size="lg" token={token} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold leading-tight">{post.created_by}</p>
              {post.synlighed === "alle" ? (
                <span className="inline-flex items-center gap-1 text-[13px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-semibold">
                  For alle
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[13px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-semibold">
                  Kun for medarbejdere
                </span>
              )}
            </div>
            <p className="text-[13px] text-muted-foreground flex items-center gap-1 mt-0.5">
              {fmtTs(post.created_at)}
              <span className="text-muted-foreground/50">·</span>
              <Globe className="w-2.5 h-2.5" />
            </p>
          </div>
        </div>
        {showMenuBtn && (
          <div className="relative">
            <button onClick={() => setShowMenu(v => !v)}
              className="p-2 rounded-full hover:bg-secondary transition-colors text-muted-foreground">
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-background border border-border rounded-xl shadow-xl py-1 min-w-[160px]">
                  {canEdit && (
                    <button onClick={startEdit}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary/50 transition-colors">
                      <Edit3 className="w-3.5 h-3.5" /> Rediger opslag
                    </button>
                  )}
                  {canPin && (
                    <button onClick={() => { onPin(); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-500 hover:bg-amber-500/5 transition-colors">
                      <Pin className="w-3.5 h-3.5" /> {post.pinned ? "Fjern fastgøring" : "Fastgør opslag"}
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => { onDelete(); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/5 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Slet opslag
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Indhold */}
      {editing ? (
        <div className="px-4 pb-4 space-y-2">
          <input type="text" value={editTitel} onChange={e => setEditTitel(e.target.value)}
            placeholder="Overskrift (valgfri)"
            className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
          <textarea value={editTekst} onChange={e => setEditTekst(e.target.value)} rows={4}
            className="w-full bg-secondary border border-border rounded-xl px-3 py-2 text-sm focus:outline-none resize-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
          <div className="flex flex-wrap gap-1.5">
            {tagSource.map(tag => {
              const active = editTags.includes(tag.id);
              return (
                <button key={tag.id}
                  onClick={() => setEditTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                  style={active
                    ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                    : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                  # {tag.label}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={cancelEdit}
              className="px-3 py-1.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
              Annuller
            </button>
            <button onClick={saveEdit} disabled={saving || !editTekst.trim()}
              className="px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
              Gem ændringer
            </button>
          </div>
        </div>
      ) : (
        <div className="px-4 pb-3">
          {post.titel && <h3 className="font-bold text-base mb-1">{post.titel}</h3>}
          {post.tekst && <p className="text-sm leading-relaxed whitespace-pre-wrap">{post.tekst}</p>}
          {post.edited_at && (
            <p className="text-[13px] text-muted-foreground mt-1">(redigeret)</p>
          )}
          {(post.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(post.tags || []).map(tagId => {
                const tag = tagSource.find(t => t.id === tagId) || FEED_TAGS.find(t => t.id === tagId);
                if (!tag) return null;
                return (
                  <span key={tagId} className="text-[13px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: tag.color + "20", color: tag.color }}>
                    # {tag.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Billeder */}
      {images.length > 0 && (
        <FeedImages post_id={post.post_id} images={images} token={token} apiBase={apiBase}
          feedPath={feedPath} onLightbox={(idx) => setLightboxIdx(idx)} />
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <FeedLightbox post_id={post.post_id} images={images} token={token} apiBase={apiBase}
          feedPath={feedPath} idx={lightboxIdx} onClose={() => setLightboxIdx(null)} onPrev={() => setLightboxIdx(i => i - 1)} onNext={() => setLightboxIdx(i => i + 1)} />
      )}

      {/* Tæller-linje */}
      {(likeCount > 0 || commentCount > 0 || seenCount > 0) && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-border/50">
          <div className="flex items-center gap-3">
            {likeCount > 0 && (
              <button onClick={onToggleLikes} className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
                <span className="w-5 h-5 rounded-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 fill-white text-white" />
                </span>
                {likeCount}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
            {commentCount > 0 && (
              <button onClick={onToggleComments} className="hover:underline">{commentCount} kommentar{commentCount !== 1 ? "er" : ""}</button>
            )}
            {seenCount > 0 && (
              <div className="relative" onMouseEnter={() => setSeenHover(true)} onMouseLeave={() => setSeenHover(false)}>
                <span className="flex items-center gap-1 cursor-default text-muted-foreground">
                  <Eye className="w-3 h-3" /> {seenCount} sete
                </span>
                {seenHover && (
                  <div className="absolute right-0 bottom-full mb-1.5 z-50 bg-card border border-border rounded-xl shadow-xl p-3 min-w-[160px] max-w-[220px]">
                    <p className="text-[13px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">Set af</p>
                    <div className="flex flex-col gap-1">
                      {(post.seen_by||[]).map(name => (
                        <span key={name} className="text-[13px] font-medium truncate">{name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Likes-liste popup */}
      {showLikes && likeCount > 0 && (
        <div className="px-4 py-2 border-t border-border/50 bg-secondary/10">
          <div className="flex flex-wrap gap-1">
            {(post.likes||[]).map(name => (
              <span key={name} className="flex items-center gap-1 text-[13px] bg-background border border-border px-2 py-0.5 rounded-full">
                <Heart className="w-2.5 h-2.5 fill-red-500 text-red-500" /> {name}
              </span>
            ))}
          </div>
        </div>
      )}


      {/* Reaktions-knapper — Facebook-stil */}
      <div className="flex border-t border-border">
        <button onClick={onLike}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold transition-colors hover:bg-secondary/50
            ${post.liked_by_me ? "text-[#FF2A6D]" : "text-muted-foreground"}`}>
          <ThumbsUp className={`w-4 h-4 ${post.liked_by_me ? "fill-[#FF2A6D] text-[#FF2A6D]" : ""}`} />
          Synes godt om
        </button>
        <div className="w-px bg-border" />
        <button onClick={onToggleComments}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold transition-colors hover:bg-secondary/50
            ${openComments ? "text-blue-500" : "text-muted-foreground"}`}>
          <MessageSquare className="w-4 h-4" />
          Kommenter
        </button>
      </div>

      {/* Kommentarer */}
      {openComments && (
        <div className="border-t border-border bg-secondary/10">
          {(post.kommentarer||[]).map(k => (
            <div key={k.kommentar_id} className="flex items-start gap-2.5 px-4 pt-3 pb-1 group">
              <Avatar name={k.created_by} size="sm" token={token} />
              <div className="flex-1 min-w-0">
                {k.reply_to && (
                  <div className="flex items-center gap-1.5 mb-1 ml-1">
                    <div className="w-3 h-3 border-l-2 border-t-2 border-muted-foreground/40 rounded-tl-sm shrink-0 self-end mb-0.5" />
                    <div className="bg-secondary/60 rounded-xl px-2.5 py-1 text-[13px] text-muted-foreground truncate max-w-[200px]">
                      <span className="font-semibold">{k.reply_to.created_by}</span>
                      {" "}{k.reply_to.tekst}
                    </div>
                  </div>
                )}
                <div className="bg-secondary rounded-2xl px-3 py-2 inline-block max-w-full">
                  <p className="text-[13px] font-bold">{k.created_by}</p>
                  <p className="text-sm">{k.tekst}</p>
                </div>
                <div className="flex items-center gap-3 mt-1 ml-1">
                  <p className="text-[13px] text-muted-foreground">{fmtTs(k.created_at)}</p>
                  <button
                    onClick={() => { onReply({ kommentar_id: k.kommentar_id, created_by: k.created_by, tekst: k.tekst }); setTimeout(() => commentInputRef.current?.focus(), 50); }}
                    className="text-[13px] text-muted-foreground hover:text-foreground font-semibold transition-colors">
                    Svar
                  </button>
                </div>
              </div>
              <button onClick={() => onDeleteComment(k.kommentar_id)}
                className="mt-2 p-1 rounded text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {/* Ny kommentar */}
          <div className="flex flex-col gap-0 px-4 py-3">
            {replyTo && (
              <div className="flex items-center gap-2 mb-2 ml-10">
                <div className="flex-1 flex items-center gap-2 bg-secondary/60 rounded-xl px-3 py-1.5 text-[13px] text-muted-foreground">
                  <span className="font-semibold text-foreground">{replyTo.created_by}</span>
                  <span className="truncate">{replyTo.tekst}</span>
                  <button onClick={onCancelReply} className="ml-auto shrink-0 text-muted-foreground hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2.5">
              <Avatar name={currentUser} size="sm" token={token} />
              <div className="flex-1 flex items-center gap-2 bg-secondary rounded-full px-3 h-9">
                <input ref={commentInputRef} type="text" placeholder={replyTo ? `Svar ${replyTo.created_by}...` : "Skriv en kommentar..."} value={commentDraft}
                  onChange={e => onCommentChange(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onCommentSubmit(); } }}
                  className="flex-1 bg-transparent text-sm focus:outline-none" />
                <button onClick={onCommentSubmit} disabled={sendingComment || !commentDraft.trim()}
                  className="text-[#FF2A6D] disabled:opacity-30 transition-opacity">
                  {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Koder ─────────────────────────────────────────────────────────────────────

function KoderTab({ token, isAdmin }) {
  const [koder, setKoder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newKode, setNewKode] = useState({ titel: "", domæne: "", brugernavn: "", kode: "", noter: "" });
  const [adding, setAdding] = useState(false);
  const [visible, setVisible] = useState({});
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hub/koder`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setKoder(r.data.koder||[]))
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const handleAdd = async () => {
    if (!newKode.titel.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/hub/koder`, newKode, { headers: { Authorization: `Bearer ${token}` } });
      setKoder(prev => [...prev, r.data]);
      setNewKode({ titel: "", domæne: "", brugernavn: "", kode: "", noter: "" });
      setShowAdd(false);
    } catch {}
    setAdding(false);
  };

  const handleDelete = async (kode_id) => {
    if (!window.confirm("Slet?")) return;
    await axios.delete(`${API}/hub/koder/${kode_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setKoder(prev => prev.filter(k => k.kode_id !== kode_id));
  };

  const handleUpdate = async (kode_id, update) => {
    await axios.put(`${API}/hub/koder/${kode_id}`, update, { headers: { Authorization: `Bearer ${token}` } });
    setKoder(prev => prev.map(k => k.kode_id === kode_id ? {...k, ...update} : k));
    setEditing(null);
  };

  const copy = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Vigtige koder og logins</p>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">
            <Plus className="w-3.5 h-3.5" /> Tilføj kode
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : koder.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Key className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen koder endnu</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {koder.map(k => (
            <KodeKort key={k.kode_id} kode={k}
              isAdmin={isAdmin}
              isVisible={!!visible[k.kode_id]}
              onToggleVisible={() => setVisible(v => ({...v, [k.kode_id]: !v[k.kode_id]}))}
              onDelete={() => handleDelete(k.kode_id)}
              onCopy={copy}
              editing={editing === k.kode_id}
              onEdit={() => setEditing(k.kode_id)}
              onSave={(upd) => handleUpdate(k.kode_id, upd)}
              onCancelEdit={() => setEditing(null)}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Ny kode</h3>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2.5">
              {[["titel","Overskrift *"],["domæne","Domæne / URL"],["brugernavn","Brugernavn"],["kode","Kode"],["noter","Noter"]].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[13px] text-muted-foreground mb-1 block">{label}</label>
                  <input type="text" value={newKode[key]} onChange={e => setNewKode(n => ({...n, [key]: e.target.value}))}
                    className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary">Annuller</button>
              <button onClick={handleAdd} disabled={adding||!newKode.titel.trim()}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KodeKort({ kode, isAdmin, isVisible, onToggleVisible, onDelete, onCopy, editing, onEdit, onSave, onCancelEdit }) {
  const [draft, setDraft] = useState({...kode});
  useEffect(() => { setDraft({...kode}); }, [kode, editing]);

  if (editing) return (
    <div className="rounded-2xl border-2 border-[#FF2A6D]/30 p-4 space-y-2 bg-background">
      {[["titel","Overskrift"],["domæne","Domæne"],["brugernavn","Brugernavn"],["kode","Kode"],["noter","Noter"]].map(([key, label]) => (
        <div key={key}>
          <label className="text-[13px] text-muted-foreground mb-0.5 block uppercase tracking-wide">{label}</label>
          <input type="text" value={draft[key]||""} onChange={e => setDraft(d => ({...d, [key]: e.target.value}))}
            className="w-full bg-secondary border border-border rounded-lg px-2 h-8 text-[13px] focus:outline-none" />
        </div>
      ))}
      <div className="flex gap-2 justify-end pt-1">
        <button onClick={onCancelEdit} className="px-3 py-1 text-[13px] border border-border rounded-lg hover:bg-secondary">Annuller</button>
        <button onClick={() => onSave(draft)} className="px-3 py-1 text-[13px] bg-[#FF2A6D] text-white rounded-lg">Gem</button>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-border p-4 space-y-2.5 group hover:shadow-sm transition-shadow bg-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
            <Lock className="w-3.5 h-3.5 text-purple-500" />
          </div>
          <span className="font-bold text-sm truncate">{kode.titel}</span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button onClick={onEdit} className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary">
              <Edit3 className="w-3 h-3" />
            </button>
            <button onClick={onDelete} className="p-1 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      {kode.domæne && (
        <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Globe className="w-3 h-3 shrink-0" />
          <span className="truncate">{kode.domæne}</span>
        </div>
      )}
      {kode.brugernavn && (
        <div>
          <p className="text-[13px] text-muted-foreground uppercase tracking-wide mb-0.5">Brugernavn</p>
          <button onClick={() => onCopy(kode.brugernavn)} className="text-[13px] font-mono hover:text-[#FF2A6D] transition-colors" title="Klik for at kopiere">
            {kode.brugernavn}
          </button>
        </div>
      )}
      {kode.kode && (
        <div>
          <p className="text-[13px] text-muted-foreground uppercase tracking-wide mb-0.5">Kode</p>
          <div className="flex items-center gap-1.5">
            <button onClick={() => onCopy(kode.kode)} className="text-[13px] font-mono hover:text-[#FF2A6D] transition-colors flex-1 text-left" title="Klik for at kopiere">
              {isVisible ? kode.kode : "••••••••"}
            </button>
            <button onClick={onToggleVisible} className="text-muted-foreground hover:text-foreground transition-colors">
              {isVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
      {kode.noter && <p className="text-[13px] text-muted-foreground border-t border-border pt-2">{kode.noter}</p>}
    </div>
  );
}


// ── Dokumenter ─────────────────────────────────────────────────────────────────

function DokumenterTab({ token, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [navnModal, setNavnModal] = useState(null);
  const [navnDraft, setNavnDraft] = useState("");
  const [viewer, setViewer] = useState(null); // { doc, blobUrl, ct }
  const [viewerLoading, setViewerLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hub/dokumenter`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDocs(r.data.dokumenter||[]))
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  // Cleanup blob URL on viewer close
  useEffect(() => {
    return () => { if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl); };
  }, [viewer]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNavnDraft(file.name);
    setNavnModal({ file });
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!navnModal?.file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", navnModal.file);
      fd.append("navn", navnDraft.trim() || navnModal.file.name);
      const r = await axios.post(`${API}/hub/dokumenter`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDocs(prev => [r.data, ...prev]);
      setNavnModal(null); setNavnDraft("");
    } catch { alert("Upload fejlede"); }
    setUploading(false);
  };

  const handleDelete = async (doc_id, filename) => {
    if (!window.confirm(`Slet "${filename}"?`)) return;
    await axios.delete(`${API}/hub/dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDocs(prev => prev.filter(d => d.doc_id !== doc_id));
    if (viewer?.doc?.doc_id === doc_id) setViewer(null);
  };

  const handleDownload = async (doc_id, filename) => {
    const r = await fetch(`${API}/hub/dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleView = async (doc) => {
    if (viewer?.doc?.doc_id === doc.doc_id) { setViewer(null); return; }
    setViewerLoading(true);
    try {
      const r = await fetch(`${API}/hub/dokumenter/${doc.doc_id}?inline=true`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl);
      setViewer({ doc, blobUrl, ct: doc.content_type });
    } catch {}
    setViewerLoading(false);
  };

  const canView = (ct = "") =>
    ct.includes("pdf") || ct.includes("image");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-base font-bold text-foreground">Delte dokumenter</p>
        {isAdmin && (
          <div>
            <input ref={fileRef} type="file" className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
              onChange={handleFileSelect} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Upload
            </button>
          </div>
        )}
      </div>

      {navnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Navngiv dokumentet</h3>
              <button onClick={() => setNavnModal(null)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <input type="text" value={navnDraft} onChange={e => setNavnDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleUpload()} autoFocus
              className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNavnModal(null)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary">Annuller</button>
              <button onClick={handleUpload} disabled={uploading}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <FileText className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen dokumenter endnu</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          {docs.map((doc, i) => (
            <div key={doc.doc_id}>
              {/* Mobil: fil-info i egen række, værktøjer stablet nedenunder (højre-
                  justeret) så filnavn/metadata får fuld bredde. Desktop: alt på
                  én vandret linje som før. */}
              <div
                className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3.5 group hover:bg-secondary/20 transition-colors ${i > 0 ? "border-t border-border" : ""} ${viewer?.doc?.doc_id === doc.doc_id ? "bg-secondary/30" : ""}`}>
                <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                  <span className="text-2xl shrink-0 leading-none mt-0.5 sm:mt-0">{fileIcon(doc.content_type)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold break-words sm:truncate">{doc.filename}</p>
                    <p className="text-[13px] text-muted-foreground break-words">{fmtBytes(doc.size)} · {doc.uploaded_by} · {fmtTsFull(doc.uploaded_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                  {canView(doc.content_type) && (
                    <button onClick={() => handleView(doc)}
                      disabled={viewerLoading}
                      className={`px-3 py-1.5 rounded-xl text-[13px] font-medium border transition-colors ${viewer?.doc?.doc_id === doc.doc_id ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "text-foreground hover:bg-secondary border-border"}`}>
                      {viewerLoading && viewer === null ? <Loader2 className="w-3 h-3 animate-spin inline" /> : viewer?.doc?.doc_id === doc.doc_id ? "Luk" : "Vis"}
                    </button>
                  )}
                  <button onClick={() => handleDownload(doc.doc_id, doc.filename)}
                    aria-label="Download"
                    className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(doc.doc_id, doc.filename)}
                      aria-label="Slet"
                      className="p-2 rounded-xl text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all sm:opacity-0 sm:group-hover:opacity-100">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Inline viewer — renders direkte under dokumentet */}
              {viewer?.doc?.doc_id === doc.doc_id && viewer.blobUrl && (
                <div className="border-t border-border bg-secondary/10">
                  {viewer.ct.includes("image") ? (
                    <div className="p-4 flex justify-center">
                      <img src={viewer.blobUrl} alt={doc.filename}
                        className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-sm" />
                    </div>
                  ) : (
                    <object
                      data={viewer.blobUrl}
                      type="application/pdf"
                      className="w-full rounded-b-2xl"
                      style={{ height: "80vh" }}>
                      <embed
                        src={viewer.blobUrl}
                        type="application/pdf"
                        className="w-full"
                        style={{ height: "80vh" }} />
                    </object>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Tilbud ────────────────────────────────────────────────────────────────────

function TilbudTab({ token, isAdmin }) {
  const [tilbud, setTilbud] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);
  const emptyForm = { overskrift: "", indhold: "", rabatkode: "", varighed: "", note: "", synlig_til: "" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/hub/tilbud`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTilbud(r.data.tilbud || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.overskrift.trim()) return;
    setAdding(true);
    try {
      const body = { ...form, synlig_til: form.synlig_til ? new Date(form.synlig_til).toISOString() : null };
      if (editId) {
        await axios.put(`${API}/hub/tilbud/${editId}`, body, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/hub/tilbud`, body, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowAdd(false); setEditId(null); setForm(emptyForm); load();
    } catch {}
    setAdding(false);
  };

  const handleEdit = (t) => {
    const synligTo = t.synlig_til ? t.synlig_til.slice(0, 10) : "";
    setForm({ overskrift: t.overskrift||"", indhold: t.indhold||"", rabatkode: t.rabatkode||"", varighed: t.varighed||"", note: t.note||"", synlig_til: synligTo });
    setEditId(t.tilbud_id);
    setShowAdd(true);
  };

  const handleDelete = async (tilbud_id) => {
    if (!window.confirm("Slet tilbud?")) return;
    await axios.delete(`${API}/hub/tilbud/${tilbud_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setTilbud(prev => prev.filter(t => t.tilbud_id !== tilbud_id));
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }); } catch { return null; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-base font-bold text-foreground">Tilbud og kampagner</p>
        {isAdmin && (
          <button onClick={() => { setForm(emptyForm); setEditId(null); setShowAdd(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">
            <Plus className="w-3.5 h-3.5" /> Nyt tilbud
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : tilbud.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground rounded-2xl border border-border">
          <Inbox className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen aktive tilbud</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tilbud.map(t => (
            <div key={t.tilbud_id} className="rounded-2xl border border-border bg-card overflow-hidden group hover:shadow-sm transition-shadow">
              <div className="px-4 pt-4 pb-1 flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg shrink-0">🏷️</span>
                  <h3 className="font-bold text-sm truncate">{t.overskrift}</h3>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button onClick={() => handleEdit(t)} className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary">
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDelete(t.tilbud_id)} className="p-1 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <div className="px-4 pb-4 space-y-2">
                {t.indhold && <p className="text-sm text-muted-foreground leading-relaxed">{t.indhold}</p>}
                {t.rabatkode && (
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-muted-foreground">Rabatkode:</span>
                    <span className="font-mono text-[13px] bg-orange-500/10 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-lg font-semibold">{t.rabatkode}</span>
                  </div>
                )}
                {t.varighed && (
                  <p className="text-[13px] text-muted-foreground flex items-center gap-1">
                    <span>⏱</span> {t.varighed}
                  </p>
                )}
                {t.note && <p className="text-[13px] text-muted-foreground italic border-t border-border pt-2">{t.note}</p>}
                {t.synlig_til && (
                  <p className="text-[13px] text-muted-foreground">Synligt til: {fmtDate(t.synlig_til)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-md overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">{editId ? "Rediger tilbud" : "Nyt tilbud"}</h3>
              <button onClick={() => { setShowAdd(false); setEditId(null); }} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[
                ["overskrift", "Overskrift *", "text"],
                ["indhold", "Indhold / beskrivelse", "textarea"],
                ["rabatkode", "Rabatkode (valgfri)", "text"],
                ["varighed", "Tilbuddets varighed (fx '3. april – 15. april')", "text"],
                ["note", "Note (intern)", "text"],
              ].map(([key, label, type]) => (
                <div key={key}>
                  <label className="text-[13px] text-muted-foreground mb-1 block">{label}</label>
                  {type === "textarea" ? (
                    <textarea value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} rows={3}
                      className="w-full bg-secondary border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 resize-none" />
                  ) : (
                    <input type="text" value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                      className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                  )}
                </div>
              ))}
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Synligt til (dato)</label>
                <input type="date" value={form.synlig_til} onChange={e => setForm(f => ({...f, synlig_til: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setShowAdd(false); setEditId(null); }} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary">Annuller</button>
              <button onClick={handleSave} disabled={adding || !form.overskrift.trim()}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Elev Hub ──────────────────────────────────────────────────────────────────
// By-specifikt feed + kalender for elever og medarbejdere

const ELEV_HUB_TAGS = [
  { id: "kosmetolog",  label: "Kosmetolog",         color: "#a855f7" },
  { id: "frisor",      label: "Frisør",             color: "#3B82F6" },
  { id: "vigtig",      label: "Vigtigt",            color: "#EF4444" },
];

const CITY_DISPLAY_NAMES = {
  "Koebenhavn": "København",
  "København":  "København",
  "Aarhus":     "Aarhus",
  "Odense":     "Odense",
  "Aalborg":    "Aalborg",
  "Kolding":    "Kolding",
};
const normalizeCity = (c) => CITY_DISPLAY_NAMES[c] || c || "";

function ElevHubFeed({ token, currentUser, city, canPost, isSuperAdmin, userRole }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTags, setActiveTags] = useState([]);
  const [search, setSearch] = useState("");
  const [openComments, setOpenComments] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [showSeen, setShowSeen] = useState({});
  const [showLikes, setShowLikes] = useState({});
  const [replyTo, setReplyTo] = useState({});
  // Composer
  const [newTekst, setNewTekst] = useState("");
  const [newTitel, setNewTitel] = useState("");
  const [newTags, setNewTags] = useState([]);
  const [showTitelField, setShowTitelField] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [posting, setPosting] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]);
  const [postCity, setPostCity] = useState(city || "");
  const imageInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Opdater postCity hvis city-prop ændrer sig
  const prevCityRef = useRef(city);
  if (prevCityRef.current !== city) { prevCityRef.current = city; setPostCity(city || ""); }

  const isStaff = ["member", "light", "admin", "superadmin", "owner", "api_admin"].includes(userRole);

  const hdrs = { headers: { Authorization: `Bearer ${token}` } };

  // SSE — live feed-opdateringer for denne by
  const elevSseUrl = token && city
    ? `${SSE_BASE}/api/hub/elev-feed/stream?token=${encodeURIComponent(token)}&city=${encodeURIComponent(city)}`
    : null;
  useFeedSSE(elevSseUrl, setPosts, currentUser, token, "hub/elev-feed");

  const load = useCallback((q = "") => {
    setLoading(true);
    const params = new URLSearchParams({ city, limit: "50" });
    if (q) params.set("search", q);
    axios.get(`${API}/hub/elev-feed?${params}`, hdrs)
      .then(r => setPosts(r.data.posts || []))
      .catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, city]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (q) => {
    setSearch(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => load(q), 400);
  };

  const filteredPosts = activeTags.length === 0
    ? posts
    : posts.filter(p => activeTags.every(tag => (p.tags || []).includes(tag)));

  const handlePost = async () => {
    if (!newTekst.trim()) return;
    setPosting(true);
    try {

      if (attachedImages.length > 0) {
        const fd = new FormData();
        fd.append("city", postCity); fd.append("titel", newTitel); fd.append("tekst", newTekst);
        fd.append("tags", JSON.stringify(newTags));
        for (const img of attachedImages) fd.append("images", img.file, img.file.name);
        await axios.post(`${API}/hub/elev-feed`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/hub/elev-feed`, { city: postCity, titel: newTitel, tekst: newTekst, tags: newTags }, hdrs);
      }
      setNewTekst(""); setNewTitel(""); setNewTags([]); setShowTitelField(false); setShowTagPicker(false); setAttachedImages([]);
    } catch {}
    setPosting(false);
  };

  const handleLike = (post_id) => {
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    axios.post(`${API}/hub/elev-feed/${post_id}/like`, {}, hdrs).catch(() => {
      setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    });
  };

  const handleSeen = async (post_id) => {
    await axios.post(`${API}/hub/elev-feed/${post_id}/seen`, {}, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p, seen_by_me: true, seen_by: [...new Set([...(p.seen_by||[]), currentUser])],
    } : p));
  };

  const handleDelete = async (post_id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    await axios.delete(`${API}/hub/elev-feed/${post_id}`, hdrs);
    setPosts(prev => prev.filter(p => p.post_id !== post_id));
  };

  const handleEditPost = async (post_id, updates) => {
    const r = await axios.patch(`${API}/hub/elev-feed/${post_id}`, updates, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, ...r.data } : p));
  };

  const handleDeleteComment = async (post_id, kommentar_id) => {
    await axios.delete(`${API}/hub/elev-feed/${post_id}/kommentar/${kommentar_id}`, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id
      ? { ...p, kommentarer: (p.kommentarer||[]).filter(k => k.kommentar_id !== kommentar_id) } : p));
  };

  const handleComment = async (post_id) => {
    const tekst = (commentDraft[post_id] || "").trim();
    if (!tekst) return;
    setSendingComment(s => ({ ...s, [post_id]: true }));
    try {
      const body = { tekst };
      if (replyTo[post_id]) body.reply_to = replyTo[post_id];
      const r = await axios.post(`${API}/hub/elev-feed/${post_id}/kommentar`, body, hdrs);
      setPosts(prev => prev.map(p => p.post_id === post_id
        ? { ...p, kommentarer: [...(p.kommentarer || []), r.data] } : p));
      setCommentDraft(d => ({ ...d, [post_id]: "" }));
      setReplyTo(r => ({ ...r, [post_id]: null }));
    } catch {}
    setSendingComment(s => ({ ...s, [post_id]: false }));
  };

  const handlePin = async (post_id) => {
    const r = await axios.post(`${API}/hub/elev-feed/${post_id}/pin`, {}, hdrs);
    setPosts(prev => {
      const updated = prev.map(p => p.post_id === post_id ? { ...p, pinned: r.data.pinned } : p);
      return [...updated.filter(p => p.pinned), ...updated.filter(p => !p.pinned)];
    });
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      {canPost && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
          <div className="flex items-start gap-3 p-4">
            <Avatar name={currentUser} size="md" token={token} />
            <div className="flex-1">
              {showTitelField && (
                <input type="text" placeholder="Overskrift (valgfri)" value={newTitel}
                  onChange={e => setNewTitel(e.target.value)}
                  className="w-full bg-secondary rounded-xl px-3 h-8 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 border border-border" />
              )}
              {isStaff && (
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  {[city, "Fælles"].filter((v, i, a) => v && a.indexOf(v) === i).map(c => (
                    <button key={c} type="button"
                      onClick={() => setPostCity(c)}
                      className={`px-3 py-1 rounded-full text-[13px] font-semibold border transition-all ${
                        postCity === c
                          ? "bg-[#FF2A6D] text-white border-[#FF2A6D]"
                          : "border-border text-muted-foreground hover:border-[#FF2A6D]/40"
                      }`}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                placeholder={`Skriv til ${postCity || city}...`}
                value={newTekst}
                onChange={e => setNewTekst(e.target.value)}
                rows={newTekst ? 4 : 2}
                className="w-full bg-secondary rounded-xl px-3 py-2 text-sm focus:outline-none resize-none border border-border focus:ring-2 focus:ring-[#FF2A6D]/30 transition-all"
              />
              {attachedImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachedImages.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-xl border border-border" />
                      <button onClick={() => {
                        setAttachedImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
                      }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center shadow-sm hover:bg-red-500/10">
                        <X className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {(newTekst || attachedImages.length > 0) && (
            <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-secondary/20 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setShowTitelField(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors
                    ${showTitelField ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                  <Edit3 className="w-3.5 h-3.5" /> Overskrift
                </button>
                <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    setAttachedImages(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
                    e.target.value = "";
                  }} />
                <button onClick={() => imageInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-secondary transition-colors">
                  <ImageIcon className="w-3.5 h-3.5" /> Billede
                </button>
                <button onClick={() => setShowTagPicker(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${showTagPicker || newTags.length > 0 ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                  # Tags {newTags.length > 0 && `(${newTags.length})`}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); setNewTags([]); setShowTagPicker(false); }}
                  className="px-3 py-1.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
                  Annuller
                </button>
                <button onClick={handlePost} disabled={posting || !newTekst.trim()}
                  className="px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
                  {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Del opslag
                </button>
              </div>
              {showTagPicker && (
                <div className="w-full flex flex-wrap gap-1.5">
                  {ELEV_HUB_TAGS.map(tag => {
                    const active = newTags.includes(tag.id);
                    return (
                      <button key={tag.id}
                        onClick={() => setNewTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                        style={active
                          ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                          : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                        # {tag.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Søg + tag-filter */}
      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
          <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input type="text" placeholder="Søg i opslag..." value={search}
            onChange={e => handleSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground" />
          {search && (
            <button onClick={() => handleSearch("")} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="px-3 py-2 flex flex-wrap gap-1.5">
          {ELEV_HUB_TAGS.map(tag => {
            const active = activeTags.includes(tag.id);
            return (
              <button key={tag.id}
                onClick={() => setActiveTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                style={active
                  ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                  : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                # {tag.label}
              </button>
            );
          })}
          {activeTags.length > 0 && (
            <button onClick={() => setActiveTags([])}
              className="px-2.5 py-1 rounded-full text-[13px] font-semibold border border-border text-muted-foreground hover:bg-secondary transition-colors">
              Ryd filter
            </button>
          )}
        </div>
      </div>

      {/* Opslag */}
      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground rounded-2xl border border-border bg-card">
          <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">{search || activeTags.length > 0 ? "Ingen opslag matcher søgning/filter" : "Ingen opslag endnu"}</p>
        </div>
      ) : filteredPosts.map(post => (
        <FeedPost key={post.post_id} post={post} currentUser={currentUser} token={token} API={API}
          feedPath="hub/elev-feed"
          openComments={!!openComments[post.post_id]}
          onToggleComments={() => {
            setOpenComments(o => ({ ...o, [post.post_id]: !o[post.post_id] }));
            if (!post.seen_by_me) handleSeen(post.post_id);
          }}
          onLike={() => handleLike(post.post_id)}
          showSeen={false}
          onToggleSeen={() => { if (!post.seen_by_me) handleSeen(post.post_id); }}
          showLikes={!!showLikes[post.post_id]}
          onToggleLikes={() => setShowLikes(s => ({ ...s, [post.post_id]: !s[post.post_id] }))}
          commentDraft={commentDraft[post.post_id] || ""}
          onCommentChange={v => setCommentDraft(d => ({ ...d, [post.post_id]: v }))}
          onCommentSubmit={() => handleComment(post.post_id)}
          sendingComment={!!sendingComment[post.post_id]}
          replyTo={replyTo[post.post_id] || null}
          onReply={(k) => { setReplyTo(r => ({ ...r, [post.post_id]: k })); setOpenComments(o => ({ ...o, [post.post_id]: true })); }}
          onCancelReply={() => setReplyTo(r => ({ ...r, [post.post_id]: null }))}
          onDelete={() => handleDelete(post.post_id)}
          canDelete={isSuperAdmin || post.created_by === currentUser}
          canEdit={isSuperAdmin || post.created_by === currentUser}
          onEdit={(updates) => handleEditPost(post.post_id, updates)}
          onDeleteComment={(kid) => handleDeleteComment(post.post_id, kid)}
          canPin={isSuperAdmin}
          onPin={() => handlePin(post.post_id)}
          feedTagOverride={ELEV_HUB_TAGS}
        />
      ))}
    </div>
  );
}

function ElevHubKalender({ token, city, isAdmin, isSuperAdmin }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const [newEvent, setNewEvent] = useState({ event_type: "Eksamen", custom_title: "", dato: todayStr, hele_dagen: true, fra_tid: "", til_tid: "" });

  // Liste-visning state
  const [visning, setVisning] = useState("maaned");
  const [allEvents, setAllEvents] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [listFilterType, setListFilterType] = useState("");
  const [listFilterPeriode, setListFilterPeriode] = useState("md");
  const [customFra, setCustomFra] = useState("");
  const [customTil, setCustomTil] = useState("");
  const [listSort, setListSort] = useState("date");
  const [listDir, setListDir] = useState("asc");

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/hub/elev-kalender?city=${encodeURIComponent(city)}&year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setEvents(r.data.events || [])).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, city, year, month]);

  useEffect(() => { load(); }, [load]);

  const loadList = useCallback(() => {
    setListLoading(true);
    const today = new Date();
    const months = [];
    for (let i = -12; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({ y: d.getFullYear(), m: d.getMonth() + 1 });
    }
    Promise.all(months.map(({ y, m }) =>
      axios.get(`${API}/hub/elev-kalender?city=${encodeURIComponent(city)}&year=${y}&month=${m}`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.data.events || [])
        .catch(() => [])
    )).then(results => {
      setAllEvents(results.flat());
    }).finally(() => setListLoading(false));
  }, [token, city]);

  useEffect(() => {
    if (visning === "liste" && allEvents.length === 0) loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visning]);

  const prevMonth = () => { if (month === 1) { setYear(y => y-1); setMonth(12); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 12) { setYear(y => y+1); setMonth(1); } else setMonth(m => m+1); };

  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const eventsByDay = {};
  for (const ev of events) {
    if (!eventsByDay[ev.day]) eventsByDay[ev.day] = [];
    eventsByDay[ev.day].push(ev);
  }

  const cityColor = CITY_COLORS[city] || "#6B7280";

  const listEvents = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let fra = null;
    let til = null;
    if (listFilterPeriode === "md") {
      fra = new Date(today.getFullYear(), today.getMonth(), 1);
      til = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (listFilterPeriode === "3m") {
      fra = startOfToday;
      til = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
    } else if (listFilterPeriode === "6m") {
      fra = startOfToday;
      til = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());
    } else if (listFilterPeriode === "b3m") {
      fra = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "b6m") {
      fra = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "bAar") {
      fra = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      til = startOfToday;
    } else if (listFilterPeriode === "custom") {
      if (customFra) {
        const [fy, fm, fd] = customFra.split("-").map(Number);
        fra = new Date(fy, fm - 1, fd);
      }
      if (customTil) {
        const [ty, tm, td] = customTil.split("-").map(Number);
        til = new Date(ty, tm - 1, td);
      }
    }
    return allEvents
      .filter(ev => {
        if (!ev.day || !ev.month || !ev.year) return false;
        const d = new Date(ev.year, ev.month - 1, ev.day);
        if (listFilterPeriode === "alt") {
          // Ingen dato-grænse
        } else {
          if (fra && d < fra) return false;
          if (til && d > til) return false;
        }
        if (listFilterType && ev.event_type !== listFilterType) return false;
        if (listSearch) {
          const q = listSearch.toLowerCase();
          if (!((ev.title || "").toLowerCase().includes(q) || (ev.event_type || "").toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => {
        let av, bv;
        if (listSort === "date") {
          av = new Date(a.year, a.month - 1, a.day).getTime();
          bv = new Date(b.year, b.month - 1, b.day).getTime();
        } else {
          av = (a[listSort] || "").toString().toLowerCase();
          bv = (b[listSort] || "").toString().toLowerCase();
        }
        if (av === bv) return 0;
        return listDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
  }, [allEvents, listFilterType, listFilterPeriode, customFra, customTil, listSearch, listSort, listDir]);

  const handleSort = (col) => {
    if (listSort === col) setListDir(d => d === "asc" ? "desc" : "asc");
    else { setListSort(col); setListDir("asc"); }
  };

  const handleAddEvent = async () => {
    setAdding(true);
    try {
      const [evYear, evMonth, evDay] = newEvent.dato.split("-").map(Number);
      const title = newEvent.event_type === "Andet" ? newEvent.custom_title : newEvent.event_type;
      await axios.post(`${API}/hub/elev-kalender`, {
        year: evYear, month: evMonth, day: evDay,
        event_type: newEvent.event_type, custom_title: newEvent.custom_title,
        title, city, color: cityColor,
        hele_dagen: !!newEvent.hele_dagen,
        fra_tid: newEvent.hele_dagen ? "" : (newEvent.fra_tid || ""),
        til_tid: newEvent.hele_dagen ? "" : (newEvent.til_tid || ""),
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (evYear === year && evMonth === month) load();
      else { setYear(evYear); setMonth(evMonth); }
      setAllEvents([]);
      setShowAdd(false);
    } catch {}
    setAdding(false);
  };

  const handleDelete = async (event_id) => {
    await axios.delete(`${API}/hub/elev-kalender/${event_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setEvents(prev => prev.filter(e => e.event_id !== event_id));
    setAllEvents(prev => prev.filter(e => e.event_id !== event_id));
    setSelectedEvent(null); setSelectedDay(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {visning === "maaned" && (
            <>
              <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <h2 className="text-base font-bold w-44 text-center">{MONTH_NAMES[month-1]} {year}</h2>
              <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </>
          )}
          <div className="inline-flex rounded-full border border-border bg-secondary p-1 text-[13px] font-semibold">
            <button
              onClick={() => setVisning("maaned")}
              className={`px-3 py-1 rounded-full transition-colors ${visning === "maaned" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Måned
            </button>
            <button
              onClick={() => setVisning("liste")}
              className={`px-3 py-1 rounded-full transition-colors ${visning === "liste" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Liste
            </button>
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[13px] font-semibold hover:opacity-90 transition-colors"
            style={{ backgroundColor: cityColor }}>
            <Plus className="w-3.5 h-3.5" /> Tilføj begivenhed
          </button>
        )}
      </div>

      {visning === "maaned" && (
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cityColor }} />
          <span className="text-[13px] text-muted-foreground font-medium">{city} · Fælles og helligdage vises altid</span>
        </div>
      )}

      {visning === "maaned" && (loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter kalender...</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="grid grid-cols-[28px_repeat(7,1fr)] md:grid-cols-[36px_repeat(7,1fr)] border-b border-border bg-secondary/30">
            <div className="py-2.5" />
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-[13px] font-semibold text-muted-foreground py-2.5">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-[28px_repeat(7,1fr)] md:grid-cols-[36px_repeat(7,1fr)]">
            {(() => {
              const isoWeek = (date) => {
                const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
                const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
              };
              const cells = [];
              days.forEach((day, i) => {
                const colInRow = i % 7;
                if (colInRow === 0) {
                  const cellDate = day ? new Date(year, month - 1, day) : new Date(year, month - 1, 1 - startDow + i);
                  const wn = isoWeek(cellDate);
                  cells.push(
                    <div key={`w${i}`} className="border-b border-r border-border bg-secondary/5 flex items-start justify-center pt-2">
                      <span className="text-[12px] md:text-[12px] font-bold text-muted-foreground/50">{wn}</span>
                    </div>
                  );
                }
                const dayEvents = day ? (eventsByDay[day] || []) : [];
                const isToday = day && year === now.getFullYear() && month === now.getMonth()+1 && day === now.getDate();
                const isSelected = day === selectedDay;
                cells.push(
                  <div key={`d${i}`}
                    onClick={() => day && setSelectedDay(selectedDay === day ? null : day)}
                    className={`min-h-[56px] md:min-h-[120px] border-b border-r border-border p-1 md:p-1.5 transition-colors
                      ${day ? "cursor-pointer hover:bg-secondary/20" : "bg-secondary/5"}
                      ${isSelected ? "bg-secondary/30" : ""}`}>
                    {day && (
                      <>
                        <div className={`text-[13px] md:text-[13px] font-bold mb-0.5 md:mb-1 w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full mx-auto
                          ${isToday ? "text-white" : "text-foreground"}`}
                          style={isToday ? { backgroundColor: cityColor } : {}}>
                          {day}
                        </div>
                        <div className="flex md:hidden flex-wrap gap-0.5 justify-center mt-0.5">
                          {dayEvents.slice(0, 3).map((ev, idx) => (
                            <button key={idx}
                              onClick={e => { e.stopPropagation(); setSelectedEvent({ ...ev, day, month, year }); }}
                              className="w-2 h-2 rounded-full shrink-0 hover:scale-125 transition-transform"
                              style={{ backgroundColor: ev.color || "#6B7280" }} />
                          ))}
                          {dayEvents.length > 3 && <span className="text-[12px] text-muted-foreground leading-none">+{dayEvents.length-3}</span>}
                        </div>
                        <div className="hidden md:block space-y-1">
                          {dayEvents.slice(0, 2).map((ev, idx) => {
                            const erHeleDagen = ev.hele_dagen || (!ev.fra_tid && !ev.til_tid);
                            const erFoedselsdag = ev.type === "foedselsdag";
                            const erHelligdag = ev.type === "helligdag";
                            const tidsTekst = erFoedselsdag || erHelligdag || erHeleDagen
                              ? "Hele dagen"
                              : `${ev.fra_tid || ""} – ${ev.til_tid || ""}`;
                            return (
                              <button key={idx}
                                onClick={e => { e.stopPropagation(); setSelectedEvent({ ...ev, day, month, year }); }}
                                className="w-full text-left rounded px-1.5 py-1 leading-tight hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: (ev.color || "#6B7280") + "20", color: ev.color || "#6B7280" }}>
                                <div className="text-[13px] font-semibold truncate flex items-center gap-1">
                                  {ev.city === "Online" && <Globe className="w-3 h-3 shrink-0" />}
                                  <span className="truncate">{ev.title}</span>
                                </div>
                                <div className="text-[12px] opacity-80 truncate">{tidsTekst}</div>
                                {ev.city && <div className="text-[12px] opacity-70 truncate">{ev.city}</div>}
                              </button>
                            );
                          })}
                          {dayEvents.length > 2 && (
                            <button onClick={e => { e.stopPropagation(); setSelectedDay(day); }}
                              className="w-full text-left text-[12px] text-muted-foreground hover:text-foreground px-1.5 py-0.5">
                              + {dayEvents.length - 2} flere
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              });
              return cells;
            })()}
          </div>
        </div>
      ))}

      {visning === "liste" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input type="text" placeholder="Søg på navn eller type..." value={listSearch}
              onChange={e => setListSearch(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm w-full max-w-xs focus:outline-none focus:ring-1"
              style={{ boxShadow: "none" }} />
            <select value={listFilterType} onChange={e => setListFilterType(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="">Alle typer</option>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={listFilterPeriode} onChange={e => setListFilterPeriode(e.target.value)}
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="md">Indeværende måned</option>
              <option value="3m">Næste 3 mdr</option>
              <option value="6m">Næste 6 mdr</option>
              <option value="b3m">Sidste 3 mdr</option>
              <option value="b6m">Sidste 6 mdr</option>
              <option value="bAar">Sidste år</option>
              <option value="alt">Alt</option>
              <option value="custom">Tilpasset</option>
            </select>
            {listFilterPeriode === "custom" && (
              <>
                <input type="date" value={customFra} onChange={e => setCustomFra(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none" />
                <input type="date" value={customTil} onChange={e => setCustomTil(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none" />
              </>
            )}
            <button onClick={loadList} disabled={listLoading}
              className="px-3 h-9 rounded-lg border border-border text-[13px] font-medium hover:bg-secondary transition-colors disabled:opacity-50">
              {listLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Opdater"}
            </button>
          </div>

          <div className="rounded-xl border border-border overflow-hidden">
            {listLoading && allEvents.length === 0 ? (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <p className="text-[13px] text-muted-foreground mt-3">Henter begivenheder...</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/50">
                    <SortableTh col="date" label="Dato" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <SortableTh col="title" label="Begivenhed" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <SortableTh col="event_type" label="Type" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                    <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Tidspunkt</th>
                    <SortableTh col="city" label="By / lokation" sortCol={listSort} sortDir={listDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {listEvents.map((ev, i) => {
                    const erHeleDagen = ev.hele_dagen || (!ev.fra_tid && !ev.til_tid);
                    const erFoedselsdag = ev.type === "foedselsdag";
                    const erHelligdag = ev.type === "helligdag";
                    const tidsTekst = erFoedselsdag || erHelligdag || erHeleDagen
                      ? "Hele dagen"
                      : `${ev.fra_tid || ""} – ${ev.til_tid || ""}`;
                    return (
                      <tr key={ev.event_id || `${ev.year}-${ev.month}-${ev.day}-${i}`}
                        className="border-b border-border hover:bg-secondary/30 cursor-pointer"
                        onClick={() => setSelectedEvent({ ...ev })}>
                        <td className="py-2 px-4 text-[13px] tabular-nums">{fmtDato(ev)}</td>
                        <td className="py-2 px-4 text-sm font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.color || cityColor }} />
                            {ev.city === "Online" && <Globe className="inline w-3.5 h-3.5 text-blue-500" />}
                            {ev.title}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground">{ev.event_type || (ev.type === "helligdag" ? "Helligdag" : ev.type === "foedselsdag" ? "Fødselsdag" : ev.type === "jubilaeum" ? "Jubilæum" : "—")}</td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground tabular-nums">{tidsTekst}</td>
                        <td className="py-2 px-4 text-[13px] text-muted-foreground">{ev.city || "—"}</td>
                      </tr>
                    );
                  })}
                  {listEvents.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Ingen begivenheder fundet</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setSelectedEvent(null)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="h-1.5 w-full" style={{ backgroundColor: selectedEvent.color || cityColor }} />
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full shrink-0 mt-0.5" style={{ backgroundColor: selectedEvent.color || cityColor }} />
                  <p className="font-bold text-sm leading-snug">
                    {selectedEvent.city === "Online" && <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {selectedEvent.title}
                  </p>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-muted-foreground hover:text-foreground shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1.5 pl-5">
                <p className="text-[13px] text-muted-foreground">
                  {selectedEvent.day}. {MONTH_NAMES[(selectedEvent.month||month)-1]} {selectedEvent.year||year}
                  {selectedEvent.hele_dagen === false && selectedEvent.fra_tid && selectedEvent.til_tid && (
                    <> · {selectedEvent.fra_tid}–{selectedEvent.til_tid}</>
                  )}
                </p>
                {selectedEvent.city && <p className="text-[13px] text-muted-foreground">{selectedEvent.city}{selectedEvent.subtype === "elev" ? " · Elev" : selectedEvent.subtype === "medarbejder" ? " · Medarbejder" : ""}</p>}
                {selectedEvent.type === "helligdag" && <p className="text-[13px] text-muted-foreground">Dansk helligdag</p>}
                {selectedEvent.event_type && selectedEvent.type !== "helligdag" && <p className="text-[13px] text-muted-foreground">{selectedEvent.event_type}</p>}
              </div>
              {isAdmin && selectedEvent.event_id && (
                <div className="mt-4 pt-3 border-t border-border flex justify-end">
                  <button onClick={() => handleDelete(selectedEvent.event_id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-red-500 hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Slet begivenhed
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedDay && eventsByDay[selectedDay] && (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/20">
            <p className="text-sm font-bold">{selectedDay}. {MONTH_NAMES[month-1]} {year}</p>
          </div>
          <div className="divide-y divide-border">
            {eventsByDay[selectedDay].map((ev, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: ev.color || "#6B7280" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {ev.city === "Online" && <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {ev.title}
                    {ev.hele_dagen === false && ev.fra_tid && ev.til_tid && (
                      <span className="ml-2 text-[13px] text-muted-foreground font-normal">{ev.fra_tid}–{ev.til_tid}</span>
                    )}
                  </p>
                  {ev.city && <p className="text-[13px] text-muted-foreground">{ev.city}</p>}
                </div>
                {isAdmin && ev.event_id && (
                  <button onClick={() => handleDelete(ev.event_id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Ny begivenhed — {city}</h3>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Dato</label>
                <input type="date" value={newEvent.dato} onChange={e => setNewEvent(n => ({...n, dato: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Type</label>
                <select value={newEvent.event_type} onChange={e => setNewEvent(n => ({...n, event_type: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {newEvent.event_type === "Andet" && (
                <div>
                  <label className="text-[13px] text-muted-foreground mb-1 block">Titel</label>
                  <input type="text" value={newEvent.custom_title} onChange={e => setNewEvent(n => ({...n, custom_title: e.target.value}))}
                    className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
                </div>
              )}
              <div>
                <label className="text-[13px] font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">Tidspunkt</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-elev-add" checked={!newEvent.hele_dagen}
                      onChange={() => setNewEvent(n => ({ ...n, hele_dagen: false }))} />
                    <span className="text-sm">Bestemt tidspunkt</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tidspunkt-type-elev-add" checked={!!newEvent.hele_dagen}
                      onChange={() => setNewEvent(n => ({ ...n, hele_dagen: true, fra_tid: "", til_tid: "" }))} />
                    <span className="text-sm">Hele dagen</span>
                  </label>
                </div>
                {!newEvent.hele_dagen && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Fra</label>
                      <input type="time" value={newEvent.fra_tid || ""}
                        onChange={e => setNewEvent(n => ({ ...n, fra_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                    <div>
                      <label className="text-[13px] text-muted-foreground mb-1 block">Til</label>
                      <input type="time" value={newEvent.til_tid || ""}
                        onChange={e => setNewEvent(n => ({ ...n, til_tid: e.target.value }))}
                        className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary">Annuller</button>
              <button onClick={handleAddEvent} disabled={adding}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: cityColor }}>
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gem"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Personlig Kasse ────────────────────────────────────────────────────────────

const UGEDAGE = ["Man", "Tirs", "Ons", "Tors", "Fre", "Lør", "Søn"];
const UGEDAGE_FULL = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];

function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return d;
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

const VAGTTYPER = ["", "Behandler timer", "Syg", "Ferie", "Fri", "Lægeeksamination", "Lægeundervisning", "Barnets første sygedag"];
const VAGTTYPE_COLORS = {
  "Behandler timer": "text-blue-500 bg-blue-500/10",
  "Syg": "text-red-500 bg-red-500/10",
  "Ferie": "text-emerald-500 bg-emerald-500/10",
  "Fri": "text-purple-500 bg-purple-500/10",
  "Lægeeksamination": "text-amber-500 bg-amber-500/10",
  "Lægeundervisning": "text-amber-500 bg-amber-500/10",
  "Barnets første sygedag": "text-orange-500 bg-orange-500/10",
};
const ALLE_BYER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"];

// Danske helligdage 2024-2027
const DK_HELLIGDAGE = new Set([
  "2025-01-01","2025-04-17","2025-04-18","2025-04-21","2025-05-16","2025-05-29","2025-06-05","2025-06-08","2025-06-09","2025-12-25","2025-12-26",
  "2026-01-01","2026-04-02","2026-04-03","2026-04-06","2026-05-01","2026-05-14","2026-05-24","2026-05-25","2026-06-05","2026-12-25","2026-12-26",
  "2027-01-01","2027-03-25","2027-03-26","2027-03-29","2027-04-23","2027-06-03","2027-06-05","2027-06-13","2027-06-14","2027-12-25","2027-12-26",
]);

// Read-only popup, der viser detaljer for én planlagt vagt.
// Bevidst lokal og lille — fuld redigering sker via "Åbn i vagtplan"-link,
// så vi ikke importerer den store VagtModal fra VagtplanPage.
function VagtViewModal({ vagt, onClose }) {
  if (!vagt) return null;
  const UGEDAGE = ["Søndag","Mandag","Tirsdag","Onsdag","Torsdag","Fredag","Lørdag"];
  const MND = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];
  let dagTekst = vagt.dato || "";
  try {
    const [y, m, d] = (vagt.dato || "").split("-").map(Number);
    if (y && m && d) {
      const dObj = new Date(y, m - 1, d);
      dagTekst = `${UGEDAGE[dObj.getDay()]} d. ${d}. ${MND[m - 1]}`;
    }
  } catch {}
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border p-5 w-full max-w-sm shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Planlagt vagt</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <p className="capitalize text-muted-foreground">{dagTekst}</p>
          {vagt.fra && vagt.til && (
            <p className="font-bold text-lg text-blue-500">{vagt.fra} – {vagt.til}</p>
          )}
          {vagt.vagttype && (
            <p className="text-[13px]"><span className="text-muted-foreground">Vagttype: </span>{vagt.vagttype}</p>
          )}
          {vagt.city && (
            <p className="text-[13px]"><span className="text-muted-foreground">Afdeling: </span>{vagt.city}</p>
          )}
          {vagt.note && (
            <p className="text-[13px] italic text-muted-foreground border-l-2 border-border pl-2">{vagt.note}</p>
          )}
        </div>
        <a href="/vagtplan"
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-border bg-secondary/40 text-[13px] font-semibold hover:bg-secondary transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Åbn i vagtplan
        </a>
        <button onClick={onClose}
          className="w-full px-4 py-2 rounded-xl border border-border text-[13px] font-semibold hover:bg-secondary transition-colors">
          Luk
        </button>
      </div>
    </div>
  );
}

// Vagttype-vælger (custom dropdown).
// Erstatter native <select> i DagensArbejdstid.
// "Normal timer" er første option — den manglede tidligere fra ad-hoc-listen.
const ADHOC_VAGTTYPER = [
  // #44.3: "Hjemmearbejdsplads" fjernet — hjemmefra er bare "Normal timer";
  // lokationen ses på selve stempel-rekorden.
  { value: "Normal timer", icon: "⏱", label: "Normal timer" },
  { value: "Behandler timer", icon: "💆", label: "Behandler timer" },
  { value: "Syg", icon: "🤒", label: "Syg" },
  { value: "Ferie", icon: "🏖", label: "Ferie" },
  { value: "Fri", icon: "🎉", label: "Fri" },
  { value: "Lægeeksamination", icon: "🩺", label: "Lægeeksamination" },
  { value: "Lægeundervisning", icon: "📚", label: "Lægeundervisning" },
  { value: "Barnets første sygedag", icon: "👶", label: "Barnets første sygedag" },
];

function VagttypeVælger({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const valgt = ADHOC_VAGTTYPER.find(t => t.value === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-border bg-background text-sm font-bold hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 transition-colors"
      >
        <span className="flex items-center gap-2">
          {valgt ? (<><span>{valgt.icon}</span><span>{valgt.label}</span></>) : <span className="text-muted-foreground">Vælg vagttype...</span>}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute z-[70] mt-1 w-full bg-background border border-border rounded-xl shadow-xl overflow-hidden">
            <div>
              {ADHOC_VAGTTYPER.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => { onChange(t.value); setOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#FF2A6D]/10 hover:text-[#FF2A6D] transition-colors text-left ${value === t.value ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : ""}`}
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DagensArbejdstid({ token, currentUser, collapsible = false, defaultCollapsed = false, footer = null }) {
  // Foldbar-tilstand (Krav #7) — kun aktiv når collapsible=true (mobil).
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Kanonisk medarbejder-navn fra /hub/me — login-navn kan afvige fra navnet på
  // vagter (fx "Søren IT" vs "Søren Sundahl"). Bruges til vagtplan-opslag.
  const [medarbejderNavn, setMedarbejderNavn] = useState(null);
  const [vagter, setVagter] = useState([]);
  const [loadingVagter, setLoadingVagter] = useState(true);
  const [clockRecord, setClockRecord] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [loadingClock, setLoadingClock] = useState(true);
  // #47.2: glemt (åben) stempling fra tidligere dag + foreslået udstemplings-tid.
  const [glemtAaben, setGlemtAaben] = useState(null);   // {clock_id, dato, clock_in, clock_in_lokation, foreslaaet_clock_out}
  const [glemtTid, setGlemtTid] = useState("");
  const [glemtBusy, setGlemtBusy] = useState(false);
  const [glemtSkjult, setGlemtSkjult] = useState(false);  // "Husk mig senere"
  const [periodeRecords, setPeriodeRecords] = useState([]);
  const [clockBusy, setClockBusy] = useState(false);
  const [clockError, setClockError] = useState("");
  const [showNoteField, setShowNoteField] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  // Ad-hoc-stempling: tillad-flag + valgt vagttype når der ikke er planlagt vagt
  const [tilladAdhoc, setTilladAdhoc] = useState(false);
  const [adhocVagttype, setAdhocVagttype] = useState("");
  // adhocMode: brugeren har valgt at starte ad-hoc selvom de har planlagt vagt
  const [adhocMode, setAdhocMode] = useState(false);
  // Read-only modal for planlagt vagt
  const [showVagtModal, setShowVagtModal] = useState(null);
  // Besked når en planlagt vagt blev erstattet af ad-hoc (fra backend-respons)
  const [replacedInfo, setReplacedInfo] = useState(null);
  // Position-tjek mod planlagt vagts lokation (opfølgning #14).
  // null = ikke tjekket endnu / GPS-fejl; ellers
  // {paa_vagt_adresse, distance, vagt_lokation, vagt_id}
  const [posCheck, setPosCheck] = useState(null);
  const [posCheckLoading, setPosCheckLoading] = useState(false);
  const [posCheckError, setPosCheckError] = useState("");
  const hdrs = { Authorization: `Bearer ${token}` };

  // Periode 20.–19.: hvis dato >= 20, så er periode 20. denne måned → 19. næste.
  // Ellers 20. forrige måned → 19. denne måned.
  const periode = useMemo(() => {
    const n = new Date();
    let fraDate, tilDate;
    if (n.getDate() >= 20) {
      fraDate = new Date(n.getFullYear(), n.getMonth(), 20);
      tilDate = new Date(n.getFullYear(), n.getMonth() + 1, 19);
    } else {
      fraDate = new Date(n.getFullYear(), n.getMonth() - 1, 20);
      tilDate = new Date(n.getFullYear(), n.getMonth(), 19);
    }
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const MNDS = ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"];
    const label = `${fraDate.getDate()}. ${MNDS[fraDate.getMonth()]} – ${tilDate.getDate()}. ${MNDS[tilDate.getMonth()]}`;
    return { fra: iso(fraDate), til: iso(tilDate), label };
  }, []);

  // Sum af planlagte timer i perioden (#60: inkl. Syg/Ferie/Fri).
  const planlagtTimer = useMemo(() => {
    // #60: Syg/Ferie/Fri tæller nu MED som almindelige planlagte timer.
    return vagter
      .filter(v => v.dato >= periode.fra && v.dato <= periode.til)
      .reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
  }, [vagter, periode]);

  // Sum af godkendte stempel-timer i perioden + tæl ikke-godkendte
  const stempletGodkendt = useMemo(() => {
    return periodeRecords
      .filter(r => r.status === "approved")
      .reduce((s, r) => s + (parseFloat(r.timer_actual) || 0), 0);
  }, [periodeRecords]);

  const harAfventende = useMemo(() => {
    return periodeRecords.some(r => r.status && r.status !== "approved" && r.status !== "rejected" && r.clock_out);
  }, [periodeRecords]);

  // Tæller i sekunder — summen af ALLE dagens records (Regel B: delt vagt).
  // - completed/approved/rejected: brug timer_actual (færdig værdi)
  // - open: live-beregn fra clock_in til nu minus pauser
  // - paused: live-beregn fra clock_in til sidste pause-fra minus afsluttede pauser
  // Records er allerede filtreret på dagens dato af backend (today i _clock_owner_query),
  // så tælleren nulstilles automatisk ved døgnskifte.
  useEffect(() => {
    const records = allRecords && allRecords.length ? allRecords : (clockRecord ? [clockRecord] : []);
    if (!records.length) { setElapsedSec(0); return; }

    const calcSec = () => {
      let totalSek = 0;
      for (const r of records) {
        const st = r.status;
        if ((st === "completed" || st === "approved" || st === "rejected") && r.timer_actual != null) {
          totalSek += (parseFloat(r.timer_actual) || 0) * 3600;
          continue;
        }
        if (!r.clock_in) continue;
        const clockInMs = new Date(r.clock_in).getTime();
        if (st === "open") {
          const pauseSek = (r.pauses || []).reduce((acc, p) => {
            if (!p.fra || !p.til) return acc;
            return acc + (new Date(p.til) - new Date(p.fra)) / 1000;
          }, 0);
          totalSek += Math.max(0, (Date.now() - clockInMs) / 1000 - pauseSek);
        } else if (st === "paused") {
          // Live: tæl indtil sidste pause-fra; træk tidligere afsluttede pauser fra.
          const pauses = r.pauses || [];
          const sidstePause = pauses[pauses.length - 1];
          const stopMs = sidstePause && sidstePause.fra && !sidstePause.til
            ? new Date(sidstePause.fra).getTime()
            : Date.now();
          const pauseSek = pauses.reduce((acc, p) => {
            if (!p.fra || !p.til) return acc;
            return acc + (new Date(p.til) - new Date(p.fra)) / 1000;
          }, 0);
          totalSek += Math.max(0, (stopMs - clockInMs) / 1000 - pauseSek);
        }
      }
      setElapsedSec(Math.floor(totalSek));
    };
    calcSec();
    const iv = setInterval(calcSec, 1000);
    return () => clearInterval(iv);
  }, [clockRecord, allRecords]);

  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const UGEDAGE = ["Søndag","Mandag","Tirsdag","Onsdag","Torsdag","Fredag","Lørdag"];
  const MND = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];
  const dagLabel = `${UGEDAGE[now.getDay()]} d. ${now.getDate()}. ${MND[now.getMonth()]}`;
  // Defensiv: et clockRecord fra en tidligere dag (fx open record fra i går
  // før auto-udstempling har fanget den) må aldrig vises som "Igangværende".
  const clockRecordErIdag = clockRecord && clockRecord.dato === todayIso;
  const activeForToday = clockRecordErIdag ? clockRecord : null;

  // State for glemt udstempling-modal (vises når backend returnerer 409
  // ATTACH_FORGOT_CLOCKOUT ved indstempling)
  const [glemtUdstempling, setGlemtUdstempling] = useState(null);
  const [korrTid, setKorrTid] = useState("");
  const [korrNote, setKorrNote] = useState("");
  const [korrBusy, setKorrBusy] = useState(false);

  // Hent /hub/me: ad-hoc-rettighed + kanonisk medarbejder-navn (til vagtplan-opslag).
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/hub/me`, { headers: hdrs })
      .then(r => {
        setTilladAdhoc(!!(r.data && r.data.tillad_adhoc_stempling));
        setMedarbejderNavn((r.data && r.data.medarbejder_navn) || null);
      })
      .catch(() => { setTilladAdhoc(false); setMedarbejderNavn(null); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Vagter slås op på det KANONISKE medarbejder-navn (fra /hub/me), ikke login-
  // navnet — ellers findes vagten ikke når de afviger ("Søren IT" vs "Søren Sundahl").
  // Falder tilbage til currentUser indtil /hub/me er hentet.
  // Perf (#56-fix): undgå dobbelt-hentning — hvis det kanoniske navn er det samme
  // som det allerede hentede (login-navn), genhent ikke når /hub/me lander.
  const sidstHentetNavn = useRef(null);
  useEffect(() => {
    const navn = medarbejderNavn || currentUser;
    if (!navn || sidstHentetNavn.current === navn) return;
    sidstHentetNavn.current = navn;
    setLoadingVagter(true);
    axios.get(`${API}/hub/vagtplan?medarbejder=${encodeURIComponent(navn)}`, { headers: hdrs })
      .then(r => setVagter(r.data.vagter || []))
      .catch(() => {})
      .finally(() => setLoadingVagter(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, medarbejderNavn, currentUser]);

  const fetchClock = () => {
    return axios.get(`${API}/hub/clock`, { headers: hdrs })
      .then(r => { setClockRecord(r.data.record || null); setAllRecords(r.data.records || []); })
      .catch(() => {});
  };

  useEffect(() => {
    if (!currentUser) return;
    fetchClock().finally(() => setLoadingClock(false));
    // #47.2: tjek for glemt (åben) stempling fra tidligere → advarselsboks.
    axios.get(`${API}/hub/clock/glemt-aaben`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const g = r.data?.glemt || null;
        setGlemtAaben(g);
        if (g) setGlemtTid(g.foreslaaet_clock_out || "");
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser]);

  // #47.2: luk den glemte stempling med medarbejderens (evt. ændrede) tid.
  const lukGlemt = async () => {
    if (!glemtAaben || !/^\d{1,2}:\d{2}$/.test(glemtTid)) return;
    setGlemtBusy(true);
    try {
      const body = { clock_id: glemtAaben.clock_id, foreslaaet_clock_out: glemtTid.padStart(5, "0") };
      // Forsøg at vedhæfte nuværende lokation (hjemme-adresse) hvis GPS tilgængelig.
      try { const { lat, lng } = await getGps(); body.lat = lat; body.lng = lng; } catch { /* ingen GPS er ok */ }
      await axios.post(`${API}/hub/clock/luk-glemt`, body, { headers: { Authorization: `Bearer ${token}` } });
      setGlemtAaben(null);
      await fetchClock();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke lukke vagten");
    } finally {
      setGlemtBusy(false);
    }
  };

  // Hent egne clock_records for perioden 20.–19.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams({ fra: periode.fra, til: periode.til });
    axios.get(`${API}/hub/clock/min-historik?${params}`, { headers: hdrs })
      .then(r => setPeriodeRecords(r.data.records || []))
      .catch(() => setPeriodeRecords([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser, periode.fra, periode.til]);

  const dagensVagter = vagter.filter(v => v.dato === todayIso);

  // Position-tjek mod planlagt vagts lokation (opfølgning #14).
  // Køres KUN når der er en planlagt vagt i dag, ingen aktiv/afsluttet
  // stempling, og brugeren har tillad_adhoc=true (ellers er knappen altid
  // aktiv — brugeren har intet alternativ). Resultatet bestemmer om
  // "Check ind på planlagt vagt" skal være aktiv eller disabled.
  const primaryVagt = dagensVagter[0];
  const primaryVagtId = primaryVagt?.vagt_id;
  useEffect(() => {
    if (!primaryVagtId) { setPosCheck(null); return; }
    if (clockRecord || allRecords.length > 0) { setPosCheck(null); return; }
    if (!tilladAdhoc) { setPosCheck(null); return; }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setPosCheck(null); return;
    }
    let cancelled = false;
    setPosCheckLoading(true); setPosCheckError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelled) return;
        try {
          const r = await axios.post(
            `${API}/hub/clock/check-position`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude, vagt_id: primaryVagtId },
            { headers: hdrs },
          );
          if (cancelled) return;
          setPosCheck({ ...r.data, vagt_id: primaryVagtId });
        } catch (e) {
          if (!cancelled) setPosCheckError(e?.response?.data?.detail || "Kunne ikke verificere position");
        } finally {
          if (!cancelled) setPosCheckLoading(false);
        }
      },
      () => {
        if (!cancelled) {
          setPosCheckError("Kunne ikke hente GPS-position");
          setPosCheckLoading(false);
        }
      },
      { timeout: 10000, maximumAge: 60000 },
    );
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryVagtId, !!clockRecord, allRecords.length, tilladAdhoc, token]);

  const VAGTTYPE_LABELS = {
    "": "Normal arbejdsdag", "Behandler timer": "Behandler timer",
    "Syg": "Syg", "Ferie": "Ferie", "Fri": "Fri",
    "Lægeeksamination": "Lægeeksamination", "Lægeundervisning": "Lægeundervisning",
  };
  const VAGTTYPE_COLORS_TODAY = {
    "Syg": "text-red-500", "Ferie": "text-emerald-500",
    "Fri": "text-purple-500", "Behandler timer": "text-blue-500",
    "": "text-blue-500",
  };

  const fmtTime = (isoStr) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
    } catch { return ""; }
  };

  // Trin-for-trin-guide til at give lokationstilladelse — vises når browseren
  // blokerer/afviser position for en IKKE-ad-hoc bruger (de har intet alternativ).
  const GPS_TILLAD_GUIDE =
    "Giv tilladelse til lokation: klik på låse-ikonet i adresselinjen → " +
    "Tilladelser → Placering → Tillad, og prøv igen.";

  // GPS-helper: Promise<{lat,lng}>. Forkaster med en Error hvis position ikke
  // kan hentes. err.code (1/2/3) bæres videre på Error-objektet som .gpsCode så
  // kalderen kan skelne PERMISSION_DENIED fra POSITION_UNAVAILABLE/TIMEOUT.
  // Bruger Permissions API (hvis tilgængelig) til at fange 'denied' op front —
  // men afhænger IKKE af den (Safari/ældre browsere mangler den).
  const getGps = () => new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      const e = new Error("Din browser understøtter ikke GPS. Prøv en anden browser eller enhed.");
      e.gpsCode = 0;
      reject(e);
      return;
    }

    const fail = (code, message) => {
      const e = new Error(message);
      e.gpsCode = code;
      reject(e);
    };

    const request = () => {
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        err => {
          const code = err && typeof err.code === "number" ? err.code : 0;
          const msg = (err && err.message) || "";
          let detail;
          if (code === 1) {
            // PERMISSION_DENIED
            detail = "Browseren har ikke tilladelse til at hente din position. " + GPS_TILLAD_GUIDE;
          } else if (code === 2) {
            // POSITION_UNAVAILABLE
            detail = "Vi kunne ikke hente din position. Tjek at GPS er aktiveret og at du har god dækning.";
          } else if (code === 3) {
            // TIMEOUT
            detail = "GPS-opslag tog for lang tid. Prøv igen, eller flyt dig udendørs hvis du er inden døre.";
          } else {
            detail = `Ukendt GPS-fejl (${code}): ${msg}`;
          }
          fail(code, detail);
        },
        // enableHighAccuracy for præcis afdelings-geofence; maximumAge tillader
        // en nylig cachet position (op til 60s); 5s timeout så ad-hoc-fallback
        // ikke venter for længe.
        { timeout: 5000, enableHighAccuracy: true, maximumAge: 60000 }
      );
    };

    // Optional: detect 'denied' up front for en hurtig, præcis fejl. Hvis
    // Permissions API mangler eller fejler, falder vi bare videre til request().
    if (navigator.permissions && typeof navigator.permissions.query === "function") {
      navigator.permissions.query({ name: "geolocation" })
        .then(status => {
          if (status && status.state === "denied") {
            fail(1, "Browseren har ikke tilladelse til at hente din position. " + GPS_TILLAD_GUIDE);
          } else {
            request();
          }
        })
        .catch(() => request());
    } else {
      request();
    }
  });

  const handleClockIn = async () => {
    setClockBusy(true); setClockError("");
    try {
      const body = {};
      try {
        const { lat, lng } = await getGps();
        body.lat = lat;
        body.lng = lng;
      } catch (gpsErr) {
        // Ad-hoc-medarbejdere: GPS-timeout → tillad stempling uden position.
        // Ikke-ad-hoc: blokér og vis fejl.
        if (tilladAdhoc && gpsErr.gpsCode === 3) {
          // fortsæt uden lat/lng — backend logger "Ikke registreret"
        } else {
          setClockBusy(false);
          setClockError(gpsErr.message);
          return;
        }
      }
      // Send vagttype med ved ad-hoc-scenarie (ingen planlagt vagt + adhoc tilladt
      // eller planlagt vagt men brugeren har valgt ad-hoc et andet sted)
      if (adhocVagttype) body.vagttype = adhocVagttype;
      const resp = await axios.post(`${API}/hub/clock/in`, body, { headers: hdrs });
      // Backend kan signalere at en planlagt vagt blev "erstattet" af ad-hoc
      const replaced = resp?.data?.replaced_planlagt;
      if (replaced) {
        setReplacedInfo(replaced);
      }
      setAdhocVagttype("");
      setAdhocMode(false);
      await fetchClock();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail?.kode === "ATTACH_FORGOT_CLOCKOUT") {
        setGlemtUdstempling(e.response.data.detail);
        setKorrTid("");
        setKorrNote("");
      } else {
        setClockError(e?.response?.data?.detail || e?.message || "Fejl ved check ind");
      }
    }
    setClockBusy(false);
  };

  const sendKorrektion = async () => {
    if (!glemtUdstempling?.clock_id || !korrTid || !korrNote.trim()) return;
    setKorrBusy(true);
    try {
      await axios.post(
        `${API}/hub/clock/korriger-udstempling`,
        {
          clock_id: glemtUdstempling.clock_id,
          foreslaaet_clock_out: korrTid,
          note: korrNote.trim(),
        },
        { headers: hdrs },
      );
      setGlemtUdstempling(null);
      setKorrTid("");
      setKorrNote("");
      // Prøv igen at stemple ind nu hvor korrektion er indsendt
      await handleClockIn();
    } catch (e) {
      setClockError(e?.response?.data?.detail || e?.message || "Fejl ved indsendelse af korrektion");
    }
    setKorrBusy(false);
  };

  const handlePause = async () => {
    setClockBusy(true); setClockError("");
    try {
      const r = await axios.post(`${API}/hub/clock/pause`, {}, { headers: hdrs });
      setClockRecord(r.data.record); await fetchClock();
    } catch (e) { setClockError(e?.response?.data?.detail || e?.message || "Fejl ved pause"); }
    setClockBusy(false);
  };

  const handleResume = async () => {
    setClockBusy(true); setClockError("");
    try {
      // #49.2: ad-hoc-medarbejdere må genoptage uden position.
      const body = {};
      try {
        const { lat, lng } = await getGps();
        body.lat = lat;
        body.lng = lng;
      } catch (gpsErr) {
        setClockBusy(false); setClockError(gpsErr.message); return; /* #60: lokation altid påkrævet */
      }
      await axios.post(`${API}/hub/clock/resume`, body, { headers: hdrs });
      await fetchClock();
    } catch (e) { setClockError(e?.response?.data?.detail || e?.message || "Fejl ved genoptag"); }
    setClockBusy(false);
  };

  const [showCheckOutNote, setShowCheckOutNote] = useState(false);
  const [checkOutNote, setCheckOutNote] = useState("");
  // 3-valgs modal når vagt er < 2 minutter. Sættes til { clock_id, duration_sec }
  // ved 422 fra /clock/out, ryddes når brugeren har valgt.
  const [tooShortInfo, setTooShortInfo] = useState(null);
  const [tooShortBusy, setTooShortBusy] = useState(false);

  // Modal-handlers — kaldes når brugeren vælger i tooShortInfo-modalen.
  const handleTooShortKasser = async () => {
    if (!tooShortInfo?.clock_id) return;
    setTooShortBusy(true);
    try {
      await axios.delete(`${API}/hub/clock/${tooShortInfo.clock_id}`, { headers: hdrs });
      setTooShortInfo(null);
      await fetchClock();
    } catch (e) {
      setClockError(e?.response?.data?.detail || e?.message || "Fejl ved sletning");
    }
    setTooShortBusy(false);
  };

  const handleTooShortRegistrer = async () => {
    if (!tooShortInfo?.clock_id) return;
    setTooShortBusy(true);
    try {
      await axios.patch(
        `${API}/hub/clock/${tooShortInfo.clock_id}/too-short-override`,
        {},
        { headers: hdrs },
      );
      setTooShortInfo(null);
      await fetchClock();
    } catch (e) {
      setClockError(e?.response?.data?.detail || e?.message || "Fejl ved registrering");
    }
    setTooShortBusy(false);
  };

  const handleTooShortAnnuller = async () => {
    if (!tooShortInfo?.clock_id) {
      setTooShortInfo(null);
      return;
    }
    setTooShortBusy(true);
    try {
      await axios.post(
        `${API}/hub/clock/${tooShortInfo.clock_id}/genaaben`,
        {},
        { headers: hdrs },
      );
      setTooShortInfo(null);
      await fetchClock();
    } catch (e) {
      setClockError(e?.response?.data?.detail || e?.message || "Fejl ved genåbning");
    }
    setTooShortBusy(false);
  };

  const handleClockOut = async (note = "") => {
    setClockBusy(true); setClockError("");
    try {
      // #49.2: ad-hoc-medarbejdere må stemple ud uden position.
      const body = { note };
      try {
        const { lat, lng } = await getGps();
        body.lat = lat;
        body.lng = lng;
      } catch (gpsErr) {
        setClockBusy(false); setClockError(gpsErr.message); return; /* #60: lokation altid påkrævet */
      }
      await axios.post(`${API}/hub/clock/out`, body, { headers: hdrs });
      await fetchClock();
      setShowCheckOutNote(false);
      setCheckOutNote("");
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 422 && data?.too_short && data?.clock_id) {
        setShowCheckOutNote(false);
        setCheckOutNote("");
        // Backend har gemt recorden som completed med too_short=True.
        // Åbn modal med 3 valg: Kassér / Registrér alligevel / Annullér.
        setTooShortInfo({ clock_id: data.clock_id, duration_sec: data.duration_sec || 0 });
        await fetchClock();
      } else {
        setClockError(data?.detail || e?.message || "Fejl ved check ud");
      }
    }
    setClockBusy(false);
  };

  const handleClockOutFromPause = async () => {
    // Afslut vagt mens på pause: backend bruger pausens fra-tidspunkt som clock_out.
    if (!clockRecord) return;
    setClockBusy(true); setClockError("");
    try {
      // #49.2: ad-hoc-medarbejdere må stemple ud uden position.
      const body = { from_pause: true };
      try {
        const { lat, lng } = await getGps();
        body.lat = lat;
        body.lng = lng;
      } catch (gpsErr) {
        setClockBusy(false); setClockError(gpsErr.message); return; /* #60: lokation altid påkrævet */
      }
      await axios.post(`${API}/hub/clock/out`, body, { headers: hdrs });
      await fetchClock();
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 422 && data?.too_short && data?.clock_id) {
        setTooShortInfo({ clock_id: data.clock_id, duration_sec: data.duration_sec || 0 });
        await fetchClock();
      } else {
        setClockError(data?.detail || e?.message || "Fejl ved check ud fra pause");
      }
    }
    setClockBusy(false);
  };

  const handleSaveNote = async () => {
    if (!clockRecord?.clock_id) return;
    setSavingNote(true);
    try {
      await axios.patch(`${API}/hub/clock/${clockRecord.clock_id}/note`, { note: noteDraft }, { headers: hdrs });
      setClockRecord(prev => ({ ...prev, note: noteDraft }));
      setShowNoteField(false);
    } catch {}
    setSavingNote(false);
  };

  const hasGps = typeof navigator !== "undefined" && !!navigator.geolocation;
  // Vis skeleton kun mens clock-status hentes — clock afgør om vi er stemplet ind.
  // Vagter loader bagefter uden at forsinke boksen (de vises når klar).
  const loading = loadingClock;

  const fmtElapsed = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
    return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  };

  // Tæller-format: "XtYm" for dagens totale arbejdstid (summen af alle records).
  const fmtTimerMin = (totalSek) => {
    const t = Math.floor(totalSek / 3600);
    const m = Math.floor((totalSek % 3600) / 60);
    return `${t}t ${m}m`;
  };

  const fmtTimerLabel = (decimalHours) => {
    if (decimalHours == null) return null;
    const h = Math.floor(decimalHours);
    const m = Math.round((decimalHours - h) * 60);
    if (h > 0 && m > 0) return `${h}t ${m}m`;
    if (h > 0) return `${h}t`;
    return `${m}m`;
  };

  // ── Dynamisk header-tekst til foldbar boks (Krav #7) ──────────────────────
  // Er medarbejderen stemplet ind lige nu (åben eller på pause)?
  const erStempletInd = !!activeForToday && !activeForToday.clock_out;
  // Er nuværende tid passeret planlagt sluttid og stadig stemplet ind?
  const passeretSluttid = useMemo(() => {
    if (!erStempletInd) return false;
    const sluttider = dagensVagter.map(v => v.til).filter(Boolean);
    if (!sluttider.length) return false;
    const seneste = sluttider.sort().slice(-1)[0]; // "HH:MM"
    const [h, m] = seneste.split(":").map(Number);
    const nu = new Date();
    return nu.getHours() * 60 + nu.getMinutes() > h * 60 + m;
  }, [erStempletInd, dagensVagter]);
  // Header-tekst: 3 tilstande iht. spec.
  let foldHeaderTekst;
  if (!erStempletInd && elapsedSec === 0) {
    foldHeaderTekst = "Husk at checke ind i dag";
  } else if (passeretSluttid) {
    foldHeaderTekst = "Husk at tjekke ud";
  } else {
    foldHeaderTekst = `I dag: ${fmtTimerMin(elapsedSec)} stemplet`;
  }
  const headerTone = (!erStempletInd && elapsedSec === 0)
    ? "text-red-600"
    : passeretSluttid ? "text-amber-600" : "text-muted-foreground";

  // Vis kun Tidsregistrerings-boksen for medarbejdere der er PÅ vagtplanen: har
  // mindst én planlagt vagt, ELLER ad-hoc-stempling, ELLER en åben stempling.
  // Placeret EFTER alle hooks (rules-of-hooks). Skjules helt (mobil + desktop).
  // Vis altid tidsregistrerings-boksen uanset om medarbejderen er på vagtplan

  // Skeleton-preload mens data hentes: reserverer pladsen med samme dimensioner
  // som den rigtige boks, så content nedenunder ikke hopper når den er klar.
  if (loading) {
    return (
      <div className="rounded-2xl border border-border w-full bg-card animate-pulse" aria-hidden>
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-secondary shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-32 rounded bg-secondary" />
            <div className="h-3 w-24 rounded bg-secondary/70" />
          </div>
          <div className="h-6 w-20 rounded-full bg-secondary" />
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="h-11 w-full rounded-xl bg-secondary" />
          <div className="h-11 w-full rounded-xl bg-secondary" />
          <div className="pt-2 flex items-center gap-4">
            <div className="h-3 w-40 rounded bg-secondary/70" />
            <div className="h-3 w-24 rounded bg-secondary/70" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    {/* #47.2: advarsel om glemt udstempling (åben vagt fra tidligere). */}
    {glemtAaben && !glemtSkjult && (
      <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-500/10 w-full p-4 mb-3">
        <p className="text-sm font-bold text-amber-700 dark:text-amber-400 flex items-center gap-2">
          <span aria-hidden>⚠</span> Du glemte at stemple ud
        </p>
        <p className="text-[13px] text-foreground mt-1.5">
          Du stemplede ind {glemtAaben.clock_in ? `kl. ${(String(glemtAaben.clock_in).match(/T(\d{2}:\d{2})/) || [])[1] || ""}` : ""}
          {glemtAaben.clock_in_lokation ? ` i ${glemtAaben.clock_in_lokation}` : ""}
          {glemtAaben.dato ? ` d. ${glemtAaben.dato}` : ""}. Vagten er stadig åben.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <label className="text-[13px] text-muted-foreground">Foreslået udstemplings-tid:</label>
          <input
            type="time"
            value={glemtTid}
            onChange={(e) => setGlemtTid(e.target.value)}
            className="bg-background border border-border rounded-lg px-2.5 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={lukGlemt}
            disabled={glemtBusy || !/^\d{1,2}:\d{2}$/.test(glemtTid)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {glemtBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Stempl ud nu
          </button>
          <button
            onClick={() => setGlemtSkjult(true)}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-secondary transition-colors"
          >
            Husk mig senere
          </button>
        </div>
      </div>
    )}
    <div className="rounded-2xl border border-border w-full bg-card">
      {/* Header: Tidsregistrering med dato + status-tag.
          Foldbar (Krav #7): klik på header toggler boksen på mobil. */}
      <button
        type="button"
        onClick={collapsible ? () => setCollapsed(c => !c) : undefined}
        className={`w-full text-left px-5 py-4 ${collapsed ? "" : "border-b border-border"} ${collapsible ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Clock className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm leading-tight">Tidsregistrering</p>
              {collapsible && collapsed ? (
                <p className={`text-[13px] font-semibold ${headerTone}`}>{loading ? "…" : foldHeaderTekst}</p>
              ) : (
                <p className="text-[13px] text-muted-foreground capitalize">I dag <span className="lowercase">d.</span> {now.getDate()}. {MND[now.getMonth()]}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!loading && dagensVagter.length === 0 && !activeForToday && elapsedSec === 0 && !(collapsible && collapsed) && (
              <span className="inline-flex items-center text-[13px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/20">
                Ingen vagter
              </span>
            )}
            {collapsible && (
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
            )}
          </div>
        </div>
      </button>
      {collapsible && collapsed ? null : loading ? (
        <div className="py-4 flex justify-center">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="px-5 py-4 space-y-3">
          {dagensVagter.length === 0 && !activeForToday && !allRecords.length ? null : dagensVagter.length === 0 && (activeForToday || allRecords.length > 0) ? (
            <div className="text-sm">
              <span className="font-semibold">I dag: </span>
              <span>{fmtTimerMin(elapsedSec)} stemplet</span>
            </div>
          ) : (
            dagensVagter.map(v => {
              const colorClass = VAGTTYPE_COLORS_TODAY[v.vagttype] || "text-blue-500";
              const label = VAGTTYPE_LABELS[v.vagttype] || v.vagttype || "Normal arbejdsdag";
              const completedRecord = allRecords.find(r => r.clock_out);
              const visTimer = completedRecord ? fmtTimerLabel(completedRecord.timer_actual) : fmtTimerLabel(v.timer);
              // "Tom" tilstand = ingen aktiv eller afsluttet stempling endnu.
              // I den tilstand er vagten klikbar (kan åbnes som info-popup).
              const ingenStempling = !activeForToday && allRecords.length === 0;
              if (ingenStempling) {
                return (
                  <div key={v.vagt_id} className="space-y-1.5">
                    <p className="text-[13px] uppercase tracking-wide font-bold text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Planlagt vagt
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowVagtModal(v)}
                      className="w-full text-left rounded-xl border border-border bg-card hover:bg-secondary/40 transition-colors px-3 py-2.5"
                    >
                      {v.fra && v.til ? (
                        <p className={`text-sm font-bold ${colorClass}`}>{v.fra} – {v.til}
                          {visTimer && <span className="ml-2 text-[13px] font-normal text-muted-foreground">{visTimer}</span>}
                        </p>
                      ) : (
                        <p className={`text-sm font-bold ${colorClass}`}>{label}</p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {v.fra && v.til && v.vagttype && (
                          <span className={`text-[13px] font-medium ${colorClass}`}>{label}</span>
                        )}
                        {v.city && (
                          <span className="text-[13px] text-muted-foreground flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5" /> {v.city}
                          </span>
                        )}
                        {v.note && (
                          <span className="text-[13px] text-muted-foreground italic">{v.note}</span>
                        )}
                      </div>
                    </button>
                  </div>
                );
              }
              // Med aktiv eller afsluttet stempling — bevar eksisterende kompakte layout
              return (
                <div key={v.vagt_id}>
                  {v.fra && v.til ? (
                    <p className={`text-sm font-bold ${colorClass}`}>{v.fra} – {v.til}
                      {visTimer && <span className="ml-2 text-[13px] font-normal text-muted-foreground">{visTimer}</span>}
                    </p>
                  ) : (
                    <p className={`text-sm font-bold ${colorClass}`}>{label}</p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {v.fra && v.til && v.vagttype && (
                      <span className={`text-[13px] font-medium ${colorClass}`}>{label}</span>
                    )}
                    {v.city && (
                      <span className="text-[13px] text-muted-foreground">{v.city}</span>
                    )}
                    {v.note && (
                      <span className="text-[13px] text-muted-foreground italic">{v.note}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {hasGps && (
            <div className="space-y-2">

              {/* Ingen aktiv record — vis Check ind ifølge 4 scenarier:
                  A) Planlagt vagt i dag           → "Stempel ind på vagten"
                  B) Planlagt + tilladAdhoc        → A + "ELLER" + "Start ad-hoc vagt"
                  C) Ingen planlagt + tilladAdhoc  → vagttype-vælger + Check ind
                  D) Ingen planlagt + !tilladAdhoc → forklarende tekst, ingen knap

                  adhocMode aktiveres når brugeren i scenarie B klikker
                  "Start ad-hoc vagt" — så renderes C-flow oveni. */}

              {/* Scenarie A + B: planlagt vagt findes, ikke adhocMode.
                  Opfølgning #14: Når tilladAdhoc=true, tjek GPS mod vagtens
                  lokation. Hvis brugeren ikke er på adressen, disables
                  "Check ind på planlagt vagt" og "Start ny vagt et andet
                  sted" forfremmes til primær handling. */}
              {!activeForToday && allRecords.length === 0 && dagensVagter.length > 0 && !adhocMode && (
                <div className="space-y-2">
                  {/* Posisions-tjek: kun relevant når ad-hoc er tilladt.
                      Uden tilladAdhoc har brugeren intet alternativ, så
                      knappen er altid aktiv (server-side validation
                      håndterer GPS-fejl). */}
                  {(() => {
                    const skalTjekke = tilladAdhoc;
                    const venter = skalTjekke && (posCheckLoading || (posCheck === null && !posCheckError));
                    const paaAdresse = !skalTjekke || (posCheck && posCheck.paa_vagt_adresse) || !!posCheckError;
                    const distance = posCheck?.distance;
                    const checkInTitle = (!skalTjekke || paaAdresse)
                      ? undefined
                      : `Du er ikke på den planlagte adresse (${distance}m væk)`;
                    const checkInDisabled = clockBusy || venter || (!paaAdresse && skalTjekke);
                    return (
                      <>
                        {skalTjekke && venter && (
                          <p className="text-[12px] text-muted-foreground italic flex items-center gap-1.5">
                            <Loader2 className="w-3 h-3 animate-spin" /> Tjekker din position...
                          </p>
                        )}
                        {skalTjekke && posCheck && !posCheck.paa_vagt_adresse && (
                          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-start gap-2">
                            <Info className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                            <p className="text-[12px] text-amber-700 leading-snug">
                              Du er <span className="font-semibold">{distance}m</span> fra den planlagte adresse
                              {posCheck.vagt_lokation ? ` (${posCheck.vagt_lokation})` : ""}.
                            </p>
                          </div>
                        )}
                        <button
                          onClick={handleClockIn}
                          disabled={checkInDisabled}
                          title={checkInTitle}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-opacity ${
                            (!skalTjekke || paaAdresse)
                              ? "bg-[#FF2A6D] text-white hover:opacity-90 disabled:opacity-50"
                              : "bg-secondary text-muted-foreground border border-border disabled:opacity-60"
                          }`}
                        >
                          {clockBusy
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : (skalTjekke && !paaAdresse)
                              ? <Ban className="w-4 h-4" />
                              : <Check className="w-4 h-4" />}
                          {clockBusy
                            ? "Henter position..."
                            : (skalTjekke && !paaAdresse)
                              ? "Check ind på planlagt vagt"
                              : "Check ind på planlagt vagt"}
                        </button>
                        {tilladAdhoc && (
                          <>
                            <div className="flex items-center gap-2 pt-0.5">
                              <div className="flex-1 h-px bg-border" />
                              <span className="text-[13px] uppercase tracking-wider text-muted-foreground font-semibold">eller</span>
                              <div className="flex-1 h-px bg-border" />
                            </div>
                            <button
                              type="button"
                              onClick={() => setAdhocMode(true)}
                              disabled={clockBusy}
                              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                                (skalTjekke && !paaAdresse)
                                  ? "bg-[#FF2A6D] text-white hover:opacity-90"
                                  : "border border-border bg-card hover:bg-secondary"
                              }`}
                            >
                              <Plus className="w-3.5 h-3.5" /> Start ny vagt et andet sted
                            </button>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Scenarie C: ingen planlagt vagt + tilladAdhoc
                  ELLER: planlagt vagt + brugeren har klikket "Start ad-hoc vagt" */}
              {!activeForToday && allRecords.length === 0
                && tilladAdhoc
                && (dagensVagter.length === 0 || adhocMode) && (
                <div className="space-y-2">
                  {adhocMode && dagensVagter.length > 0 && (
                    <p className="text-[13px] text-muted-foreground italic">
                      Du starter en ad-hoc vagt et andet sted end din planlagte vagt.
                      Den planlagte vagt bliver markeret som erstattet.
                    </p>
                  )}
                  <VagttypeVælger value={adhocVagttype} onChange={setAdhocVagttype} />
                  <div className="grid grid-cols-2 gap-2">
                    {adhocMode && (
                      <button
                        type="button"
                        onClick={() => { setAdhocMode(false); setAdhocVagttype(""); }}
                        disabled={clockBusy}
                        className="px-3 py-2 rounded-xl border border-border bg-card text-[13px] font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
                      >
                        Tilbage
                      </button>
                    )}
                    <button
                      onClick={handleClockIn}
                      disabled={!adhocVagttype || clockBusy}
                      title={!adhocVagttype ? "Vælg vagttype først" : undefined}
                      className={`${adhocMode ? "" : "col-span-2"} mt-0 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity`}
                    >
                      {clockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                      {clockBusy ? "Henter position..." : "Check ind"}
                    </button>
                  </div>
                </div>
              )}

              {/* Scenarie D: ingen planlagt + ingen ad-hoc-tilladelse */}
              {!activeForToday && allRecords.length === 0
                && dagensVagter.length === 0
                && !tilladAdhoc && (
                <div className="rounded-xl border border-border bg-secondary/30 px-3 py-2.5 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-[12px] text-muted-foreground leading-snug">
                    Du har ikke tilladelse til at stemple ind uden planlagt vagt.
                    Kontakt en superadmin hvis du mener det er en fejl.
                  </p>
                </div>
              )}

              {/* Bekræftelse efter ad-hoc-stempling der erstattede en planlagt vagt */}
              {replacedInfo && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-[12px] text-amber-700 leading-snug">
                      Din planlagte vagt i {replacedInfo.city || "anden by"}
                      {replacedInfo.fra && replacedInfo.til ? ` (${replacedInfo.fra}–${replacedInfo.til})` : ""}
                      {" "}er erstattet af din nuværende stempling.
                    </p>
                    <button onClick={() => setReplacedInfo(null)}
                      className="text-[13px] text-amber-700 font-semibold hover:underline mt-1">
                      OK
                    </button>
                  </div>
                </div>
              )}

              {/* Aktiv record (open eller paused) — KUN hvis dato matcher i dag */}
              {activeForToday && !activeForToday.clock_out && (
                <div className="space-y-2.5">
                  {/* Lokation */}
                  <div className="flex items-start gap-1.5">
                    <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-[12px] text-muted-foreground leading-snug">
                      {[activeForToday.clock_in_lokation, activeForToday.clock_in_adresse].filter(Boolean).join(" · ") || "Ukendt lokation"}
                      <span className="ml-1">· ind kl. {fmtTime(activeForToday.clock_in)}</span>
                    </p>
                  </div>

                  {/* Tæller — summen af alle dagens records (Regel B: delt vagt) */}
                  {activeForToday.status === "paused" ? (
                    <div className="text-center py-1">
                      <p className="text-2xl font-bold tabular-nums text-muted-foreground">PAUSE</p>
                      <p className="text-[13px] text-muted-foreground mt-0.5">
                        I dag: <span className="font-semibold">{fmtTimerMin(elapsedSec)}</span> · timer tæller ikke under pause
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-1">
                      <p className="text-3xl font-bold tabular-nums text-foreground tracking-tight">{fmtTimerMin(elapsedSec)}</p>
                      <p className="text-[13px] text-muted-foreground mt-0.5">aktiv arbejdstid i dag</p>
                    </div>
                  )}

                  {/* Check-ud note dialog */}
                  {showCheckOutNote && (
                    <div className="space-y-2 p-3 rounded-xl bg-secondary/40 border border-border">
                      <p className="text-[13px] font-semibold">Tilføj en note inden check ud (valgfrit)</p>
                      <textarea value={checkOutNote} onChange={e => setCheckOutNote(e.target.value)} rows={2}
                        className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]/40"
                        placeholder="Fx. ekstra tid, opgaver, bemærkninger..." autoFocus />
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setShowCheckOutNote(false)}
                          className="px-3 py-1.5 rounded-lg border border-border text-[13px] hover:bg-secondary transition-colors">
                          Tilbage
                        </button>
                        <button onClick={() => handleClockOut(checkOutNote)} disabled={clockBusy}
                          className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50">
                          {clockBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
                          Check ud
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Knapper */}
                  {!showCheckOutNote && (activeForToday.status === "open" ? (
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={handlePause} disabled={clockBusy}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-[13px] font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                        {clockBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>⏸</span>}
                        Pause
                      </button>
                      <button onClick={() => { setCheckOutNote(""); setShowCheckOutNote(true); }} disabled={clockBusy}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-[13px] font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                        <Clock className="w-3.5 h-3.5" />
                        Check ud
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={handleResume} disabled={clockBusy}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                        {clockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>▶</span>}
                        Genoptag
                      </button>
                      <button onClick={handleClockOutFromPause} disabled={clockBusy}
                        className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-border bg-card text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                        {clockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                        Afslut vagt
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Afsluttet record — vis historik + mulighed for ny check ind.
                  Backendens "record"-felt returnerer kun open/paused. Når
                  alle dagens stemplinger er afsluttede, er clockRecord=null
                  men allRecords>0 — så vi tjekker på det.

                  Note-redigering refererer til sidste record i listen
                  (allRecords er sorteret stigende efter clock_in). */}
              {!activeForToday && allRecords.length > 0 && (() => {
                const sidste = allRecords[allRecords.length - 1];
                return (
                <div className="space-y-2">
                  {allRecords.map((r, i) => (
                    <div key={r.clock_id} className="text-[13px] text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3 shrink-0" />
                        <span>{fmtTime(r.clock_in)} – {r.clock_out ? fmtTime(r.clock_out) : "?"}</span>
                        {r.timer_actual != null && <span className="font-semibold text-foreground">{fmtTimerLabel(r.timer_actual)}</span>}
                      </div>
                      {(r.clock_in_adresse || r.clock_in_lokation) && (
                        <div className="flex items-start gap-1.5 pl-4">
                          <MapPin className="w-2.5 h-2.5 shrink-0 mt-0.5" />
                          <span>Ind: {r.clock_in_adresse || r.clock_in_lokation}</span>
                        </div>
                      )}
                      {(r.clock_out_adresse || r.clock_out_lokation) && (
                        <div className="flex items-start gap-1.5 pl-4">
                          <MapPin className="w-2.5 h-2.5 shrink-0 mt-0.5" />
                          <span>Ud: {r.clock_out_adresse || r.clock_out_lokation}</span>
                        </div>
                      )}
                      {r.note && <div className="pl-4 italic">{r.note}</div>}
                    </div>
                  ))}
                  {dagensVagter.length === 0 && tilladAdhoc && (
                    <VagttypeVælger value={adhocVagttype} onChange={setAdhocVagttype} />
                  )}
                  <button
                    onClick={handleClockIn}
                    disabled={clockBusy || (dagensVagter.length === 0 && tilladAdhoc && !adhocVagttype)}
                    title={(dagensVagter.length === 0 && tilladAdhoc && !adhocVagttype) ? "Vælg vagttype først" : undefined}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                    {clockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                    {clockBusy ? "Henter position..." : "Check ind igen"}
                  </button>
                  {!showNoteField && (
                    <button onClick={() => { setNoteDraft(sidste.note || ""); setShowNoteField(true); }}
                      className="text-[13px] text-[#FF2A6D] font-semibold hover:underline">
                      {sidste.note ? "Rediger note" : "Tilføj note"}
                    </button>
                  )}
                  {showNoteField && (
                    <div className="space-y-1.5">
                      <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={2}
                        className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]/40"
                        placeholder="Tilføj en note..." />
                      <div className="flex gap-2">
                        <button onClick={() => setShowNoteField(false)}
                          className="flex-1 px-3 py-1.5 rounded-lg border border-border text-[13px] hover:bg-secondary transition-colors">Annuller</button>
                        <button onClick={() => handleSaveNote(sidste.clock_id)} disabled={savingNote}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50">
                          {savingNote ? "Gemmer..." : "Gem note"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}

              {clockError && (
                /* #60: GPS-/lokationsfejl vises som tydelig blokerende boks med
                   trin-for-trin guide; stemplingen kan ikke gennemføres uden. */
                /placering|gps|position/i.test(clockError) ? (
                  <div className="rounded-xl border-2 border-amber-500/50 bg-amber-500/10 p-3.5">
                    <p className="text-sm font-bold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <span aria-hidden>📍</span> Lokation påkrævet
                    </p>
                    <p className="text-[13px] text-foreground mt-1.5 whitespace-pre-line leading-snug">{clockError}</p>
                    <p className="text-[12px] text-muted-foreground mt-2">
                      Stempling kan ikke gennemføres uden lokation. Aktivér placering og prøv igen.
                    </p>
                  </div>
                ) : (
                  <p className="text-[13px] text-red-500">{clockError}</p>
                )
              )}
            </div>
          )}
          {/* Footer-slot: periode-sum + Anmod om ferie, placeret i bunden af boksen */}
          {footer && (
            <div className="pt-3 mt-1 border-t border-border">
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
    {showVagtModal && (
      <VagtViewModal vagt={showVagtModal} onClose={() => setShowVagtModal(null)} />
    )}
    {tooShortInfo && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-card rounded-2xl border border-border p-5 w-full max-w-sm shadow-2xl space-y-4">
          <div>
            <h3 className="font-bold text-base">Vagten var kortere end 2 minutter</h3>
            <p className="text-[13px] text-muted-foreground mt-1.5">
              Skal den kasseres eller registreres alligevel?
            </p>
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={handleTooShortKasser}
              disabled={tooShortBusy}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {tooShortBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Kassér vagt
            </button>
            <button
              type="button"
              onClick={handleTooShortRegistrer}
              disabled={tooShortBusy}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-card text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50"
            >
              {tooShortBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
              Registrér alligevel
            </button>
            <button
              type="button"
              onClick={handleTooShortAnnuller}
              disabled={tooShortBusy}
              className="w-full text-[13px] text-muted-foreground hover:text-foreground hover:underline pt-1 disabled:opacity-50"
            >
              Annullér (fortsæt vagt)
            </button>
          </div>
        </div>
      </div>
    )}
    {glemtUdstempling && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4">
        <div className="bg-background border border-amber-500/40 rounded-2xl shadow-2xl w-full max-w-md p-5">
          <h3 className="text-base font-bold mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Du glemte at stemple ud
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Du blev automatisk stemplet ud kl. 23:59 d. {glemtUdstempling.dato || "?"}. Angiv det
            faktiske tidspunkt og en kort note, så bliver det sendt til godkendelse.
          </p>
          <label className="block text-[13px] font-semibold mb-1">Hvornår sluttede din vagt?</label>
          <input type="time" value={korrTid} onChange={e => setKorrTid(e.target.value)}
            className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm mb-3" />
          <label className="block text-[13px] font-semibold mb-1">Note (påkrævet)</label>
          <textarea value={korrNote} onChange={e => setKorrNote(e.target.value)}
            placeholder="Glemte at stemple ud, sluttede ..."
            className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm mb-4 min-h-[60px]" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setGlemtUdstempling(null)} disabled={korrBusy}
              className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary disabled:opacity-50">
              Annullér
            </button>
            <button onClick={sendKorrektion} disabled={korrBusy || !korrTid || !korrNote.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
              {korrBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Send til godkendelse og fortsæt
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// variant="profil": foldbar profil-boks (avatar/navn/rolle + udfoldet kontakt).
// variant="periode": periode-sum (Planlagt/Stemplet) + Anmod om ferie — placeres
// under Tidsregistrering, da det vedrører tid/ferie (ikke profil-stamdata).
function PersonligKasse({ token, currentUser, variant = "profil" }) {
  const [me, setMe] = useState(null);
  const [vagter, setVagter] = useState([]);
  const [periodeRecords, setPeriodeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // Foldbar profil: header (avatar + navn + rolle) står altid; resten foldes ud.
  const [profilOpen, setProfilOpen] = useState(false);

  // Ferie-anmodning state
  const [showFerieAnmodning, setShowFerieAnmodning] = useState(false);
  const [ferieFra, setFerieFra] = useState("");
  const [ferieTil, setFerieTil] = useState("");
  const [ferieNote, setFerieNote] = useState("");
  const [ferieSaving, setFerieSaving] = useState(false);
  const [ferieFeedback, setFerieFeedback] = useState(null);
  const [feriebank, setFeriebank] = useState(null);
  const [mineAnmodninger, setMineAnmodninger] = useState([]);

  const hdrs = { Authorization: `Bearer ${token}` };

  // Periode 20.–19. Hvis dato >= 20: 20. denne måned → 19. næste,
  // ellers 20. forrige måned → 19. denne måned.
  const periode = useMemo(() => {
    const now = new Date();
    let fraDate, tilDate;
    if (now.getDate() >= 20) {
      fraDate = new Date(now.getFullYear(), now.getMonth(), 20);
      tilDate = new Date(now.getFullYear(), now.getMonth() + 1, 19);
    } else {
      fraDate = new Date(now.getFullYear(), now.getMonth() - 1, 20);
      tilDate = new Date(now.getFullYear(), now.getMonth(), 19);
    }
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const MND = ["jan","feb","mar","apr","maj","jun","jul","aug","sep","okt","nov","dec"];
    // Konsistent format på tværs af HUB: "20. maj – 19. jun"
    const label = `${fraDate.getDate()}. ${MND[fraDate.getMonth()]} – ${tilDate.getDate()}. ${MND[tilDate.getMonth()]}`;
    return { fra: iso(fraDate), til: iso(tilDate), label };
  }, []);

  // Planlagt: summer vagter i perioden (#60: inkl. Syg/Ferie/Fri).
  const planlagtTimer = useMemo(() => {
    // #60: Syg/Ferie/Fri tæller nu MED som almindelige planlagte timer.
    return vagter
      .filter(v => v.dato >= periode.fra && v.dato <= periode.til)
      .reduce((s, v) => s + (parseFloat(v.timer) || 0), 0);
  }, [vagter, periode]);

  // Stemplet: summer kun godkendte clock_records.
  const stempletTimer = useMemo(() => {
    return periodeRecords
      .filter(r => r.status === "approved")
      .reduce((s, r) => s + (parseFloat(r.timer_actual) || 0), 0);
  }, [periodeRecords]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ fra: periode.fra, til: periode.til });
        // /hub/me FØRST → kanonisk medarbejder-navn til vagtplan-opslag (login-navn
        // kan afvige, fx "Søren IT" vs "Søren Sundahl" → ellers 0 planlagte timer).
        const meRes = await axios.get(`${API}/hub/me`, { headers: hdrs });
        setMe(meRes.data);
        const navn = meRes.data?.medarbejder_navn || currentUser || "";
        const [vagterRes, recRes, fbRes, mineRes] = await Promise.all([
          axios.get(`${API}/hub/vagtplan?medarbejder=${encodeURIComponent(navn)}`, { headers: hdrs }),
          axios.get(`${API}/hub/clock/min-historik?${params}`, { headers: hdrs }).catch(() => ({ data: { records: [] } })),
          axios.get(`${API}/hub/min-feriebank`, { headers: hdrs }).catch(() => ({ data: { saldo: null } })),
          axios.get(`${API}/hub/mine-ferie-anmodninger`, { headers: hdrs }).catch(() => ({ data: { anmodninger: [] } })),
        ]);
        setVagter(vagterRes.data.vagter || []);
        setPeriodeRecords(recRes.data.records || []);
        setFeriebank(fbRes.data || null);
        setMineAnmodninger(mineRes.data.anmodninger || []);
      } catch {}
      setLoading(false);
    };
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser, periode.fra, periode.til]);

  // Beregn arbejdsdage + uger fra fra/til (man-fre minus helligdage).
  const ferieInfo = useMemo(() => {
    if (!ferieFra || !ferieTil) return null;
    const fd = new Date(ferieFra + "T00:00:00");
    const td = new Date(ferieTil + "T00:00:00");
    if (isNaN(fd) || isNaN(td) || td < fd) return null;
    const DK_HELLIGDAGE = new Set([
      "2024-01-01","2024-03-28","2024-03-29","2024-04-01","2024-04-26","2024-05-09","2024-05-19","2024-05-20","2024-06-05","2024-12-25","2024-12-26",
      "2025-01-01","2025-04-17","2025-04-18","2025-04-21","2025-05-16","2025-05-29","2025-06-08","2025-06-09","2025-06-05","2025-12-25","2025-12-26",
      "2026-01-01","2026-04-02","2026-04-03","2026-04-06","2026-05-01","2026-05-14","2026-05-24","2026-05-25","2026-06-05","2026-12-25","2026-12-26",
      "2027-01-01","2027-03-25","2027-03-26","2027-03-29","2027-04-23","2027-06-03","2027-06-13","2027-06-14","2027-06-05","2027-12-25","2027-12-26",
    ]);
    const uger = new Set();
    let arbejdsdage = 0;
    const cur = new Date(fd);
    while (cur <= td) {
      const iso = cur.toISOString().slice(0, 10);
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6 && !DK_HELLIGDAGE.has(iso)) arbejdsdage += 1;
      // ISO uge-tal
      const tmp = new Date(Date.UTC(cur.getFullYear(), cur.getMonth(), cur.getDate()));
      tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
      const week = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
      uger.add(week);
      cur.setDate(cur.getDate() + 1);
    }
    return { arbejdsdage, uger: [...uger].sort((a, b) => a - b) };
  }, [ferieFra, ferieTil]);

  const sendFerieAnmodning = async () => {
    if (!ferieFra || !ferieTil) return;
    setFerieSaving(true);
    setFerieFeedback(null);
    try {
      await axios.post(`${API}/hub/ferie-anmodning`,
        { fra_dato: ferieFra, til_dato: ferieTil, note: ferieNote },
        { headers: hdrs }
      );
      // Refresh mine anmodninger
      try {
        const r = await axios.get(`${API}/hub/mine-ferie-anmodninger`, { headers: hdrs });
        setMineAnmodninger(r.data.anmodninger || []);
      } catch {}
      setFerieFeedback({ type: "ok", msg: "Anmodning sendt — du får besked når den er behandlet." });
      setTimeout(() => {
        setShowFerieAnmodning(false);
        setFerieFra(""); setFerieTil(""); setFerieNote("");
        setFerieFeedback(null);
      }, 1500);
    } catch (e) {
      setFerieFeedback({
        type: "err",
        msg: e?.response?.data?.detail || "Kunne ikke sende anmodning",
      });
    }
    setFerieSaving(false);
  };

  // Periode-varianten er bar (slottes ind i en anden boks) → ingen spinner-kort.
  if (loading) {
    if (variant === "periode") return null;
    return (
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="h-24 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const displayCity = (c) => c === "Koebenhavn" ? "København" : (c || "");
  const fmtTimer = (t) => `${Math.round((t || 0) * 10) / 10}t`;
  // KUN arbejds-kontakt vises (privacy). Tom værdi → "—".
  const visTlf = (me?.arbejds_telefon || "").trim();
  const visMail = (me?.arbejds_email || "").trim();
  const undertekst = [me?.stilling, displayCity(me?.afdeling || me?.city)]
    .filter(Boolean).join(" · ");

  // Ferie-anmodning-modal (hører til "periode"-varianten, hvor knappen findes).
  const ferieModal = showFerieAnmodning && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4"
         onClick={() => !ferieSaving && setShowFerieAnmodning(false)}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md p-5"
           onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-base mb-4">Anmod om ferie</h3>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Fra dato</label>
            <input type="date" value={ferieFra} onChange={e => setFerieFra(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Til dato</label>
            <input type="date" value={ferieTil} onChange={e => setFerieTil(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm" />
          </div>
        </div>

        {ferieInfo && (
          <p className="text-[13px] text-muted-foreground mb-3">
            (Uge {ferieInfo.uger.join(", ")} · {ferieInfo.arbejdsdage} arbejdsdage)
          </p>
        )}

        <div className="mb-4">
          <label className="text-[13px] text-muted-foreground mb-1 block">Note (valgfri)</label>
          <textarea value={ferieNote} onChange={e => setFerieNote(e.target.value)}
            className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-sm min-h-[60px]" />
        </div>

        {feriebank && typeof feriebank.saldo === "number" && (
          <div className="mb-4 p-3 rounded-lg bg-secondary/50 text-[13px]">
            <p>Du har <span className="font-semibold">{feriebank.saldo.toFixed(2)} feriedage</span> til gode.</p>
            {ferieInfo && (feriebank.saldo - ferieInfo.arbejdsdage) >= 0 && (
              <p>Efter ferie: {(feriebank.saldo - ferieInfo.arbejdsdage).toFixed(2)} dage tilbage.</p>
            )}
            {ferieInfo && (feriebank.saldo - ferieInfo.arbejdsdage) < 0 && (
              <p className="text-amber-600">Mangler {(ferieInfo.arbejdsdage - feriebank.saldo).toFixed(2)} dage.</p>
            )}
          </div>
        )}

        {ferieFeedback && (
          <div className={`mb-3 p-2.5 rounded-lg text-[13px] ${
            ferieFeedback.type === "ok"
              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
              : "bg-red-500/10 text-red-700 dark:text-red-400"
          }`}>
            {ferieFeedback.msg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={() => setShowFerieAnmodning(false)} disabled={ferieSaving}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary disabled:opacity-50">
            Annullér
          </button>
          <button onClick={sendFerieAnmodning} disabled={ferieSaving || !ferieFra || !ferieTil}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            {ferieSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {ferieSaving ? "Sender..." : "Send anmodning"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── variant="periode": periode-sum + ferieanmodninger + Anmod-knap ────────
  // Placeres under Tidsregistrering (tid/ferie-relateret, ikke profil-stamdata).
  if (variant === "periode") {
    // Bar (uden eget kort) — slottes ind i bunden af Tidsregistrering-boksen.
    if (loading) return null;
    return (
      <>
        {/* Periode-linje. Opf. #37: "Anmod om ferie" er flyttet til Arbejdsplan-
            fanen på medarbejderens stamkort, så forsiden er renere. */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-bold text-foreground capitalize leading-tight min-w-0 truncate">
            <span className="text-muted-foreground font-semibold">Periode: </span>{periode.label}
          </p>
        </div>
        <p className="text-[13px] text-muted-foreground mt-1 leading-tight">
          Planlagt: <span className="font-semibold text-foreground">{fmtTimer(planlagtTimer)}</span>
          <span className="mx-1.5 opacity-40">·</span>
          Stemplet: <span className="font-semibold text-foreground">{fmtTimer(stempletTimer)}</span>
        </p>
      </>
    );
  }

  // ── variant="profil": foldbar profil-boks ─────────────────────────────────
  return (
    <div className="rounded-2xl border border-border bg-card w-full overflow-hidden">
      {/* Header: avatar + navn + rolle/afdeling — altid synlig, klik for at folde ud */}
      <button
        type="button"
        onClick={() => setProfilOpen(o => !o)}
        className={`w-full text-left p-5 flex items-center gap-3 hover:bg-secondary/30 transition-colors ${profilOpen ? "border-b border-border" : ""}`}
      >
        <div className="w-12 h-12 rounded-full bg-[#FF2A6D]/10 flex items-center justify-center shrink-0 text-[#FF2A6D] font-bold text-lg">
          {(me?.name || currentUser || "?").charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base truncate">{me?.name || currentUser}</p>
          {undertekst && (
            <p className="text-[13px] text-muted-foreground truncate">{undertekst}</p>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${profilOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Foldet ud: kun arbejds-kontakt (telefon + email) — profil-stamdata */}
      {profilOpen && (
        <div className="p-5 pt-4 space-y-1">
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Phone className="w-3 h-3 shrink-0" />
            {visTlf
              ? <a href={`tel:${visTlf.replace(/\s+/g,"")}`} className="hover:text-foreground transition-colors truncate">{visTlf}</a>
              : <span>—</span>}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Mail className="w-3 h-3 shrink-0" />
            {visMail
              ? <a href={`mailto:${visMail}`} className="hover:text-foreground transition-colors truncate break-all">{visMail}</a>
              : <span>—</span>}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Elev Hub Filer (separat collection fra medarbejder-hub) ───────────────────

function ElevHubFiler({ token, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [navnModal, setNavnModal] = useState(null);
  const [navnDraft, setNavnDraft] = useState("");
  const [viewer, setViewer] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hub/elev-dokumenter`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDocs(r.data.dokumenter || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    return () => { if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl); };
  }, [viewer]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNavnDraft(file.name);
    setNavnModal({ file });
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!navnModal?.file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", navnModal.file);
      fd.append("navn", navnDraft.trim() || navnModal.file.name);
      const r = await axios.post(`${API}/hub/elev-dokumenter`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDocs(prev => [r.data, ...prev]);
      setNavnModal(null); setNavnDraft("");
    } catch { alert("Upload fejlede"); }
    setUploading(false);
  };

  const handleDelete = async (doc_id, filename) => {
    if (!window.confirm(`Slet "${filename}"?`)) return;
    await axios.delete(`${API}/hub/elev-dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    setDocs(prev => prev.filter(d => d.doc_id !== doc_id));
    if (viewer?.doc?.doc_id === doc_id) setViewer(null);
  };

  const handleDownload = async (doc_id, filename) => {
    const r = await fetch(`${API}/hub/elev-dokumenter/${doc_id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleView = async (doc) => {
    if (viewer?.doc?.doc_id === doc.doc_id) { setViewer(null); return; }
    setViewerLoading(true);
    try {
      const r = await fetch(`${API}/hub/elev-dokumenter/${doc.doc_id}?inline=true`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (viewer?.blobUrl) URL.revokeObjectURL(viewer.blobUrl);
      setViewer({ doc, blobUrl, ct: doc.content_type });
    } catch {}
    setViewerLoading(false);
  };

  const canView = (ct = "") => ct.includes("pdf") || ct.includes("image");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-base font-bold text-foreground">Delte dokumenter</p>
        {isAdmin && (
          <div>
            <input ref={fileRef} type="file" className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
              onChange={handleFileSelect} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Upload
            </button>
          </div>
        )}
      </div>

      {navnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-background border border-border rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Navngiv dokumentet</h3>
              <button onClick={() => setNavnModal(null)} className="text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <input type="text" value={navnDraft} onChange={e => setNavnDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleUpload()} autoFocus
              className="w-full bg-secondary border border-border rounded-xl px-3 h-9 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNavnModal(null)} className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-secondary">Annuller</button>
              <button onClick={handleUpload} disabled={uploading}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <FileText className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-sm">Ingen dokumenter endnu</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          {docs.map((doc, i) => (
            <div key={doc.doc_id}>
              <div className={`flex items-center gap-3 px-4 py-3.5 group hover:bg-secondary/20 transition-colors ${i > 0 ? "border-t border-border" : ""} ${viewer?.doc?.doc_id === doc.doc_id ? "bg-secondary/30" : ""}`}>
                <span className="text-2xl shrink-0">{fileIcon(doc.content_type)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{doc.filename}</p>
                  <p className="text-[13px] text-muted-foreground">{fmtBytes(doc.size)} · {doc.uploaded_by} · {fmtTsFull(doc.uploaded_at)}</p>
                </div>
                {canView(doc.content_type) && (
                  <button onClick={() => handleView(doc)} disabled={viewerLoading}
                    className={`px-2.5 py-1.5 rounded-xl text-[13px] font-medium border transition-colors ${viewer?.doc?.doc_id === doc.doc_id ? "bg-[#FF2A6D] text-white border-[#FF2A6D]" : "text-foreground hover:bg-secondary border-border"}`}>
                    {viewerLoading && viewer === null ? <Loader2 className="w-3 h-3 animate-spin inline" /> : viewer?.doc?.doc_id === doc.doc_id ? "Luk" : "Vis"}
                  </button>
                )}
                <button onClick={() => handleDownload(doc.doc_id, doc.filename)}
                  className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                  <Download className="w-4 h-4" />
                </button>
                {isAdmin && (
                  <button onClick={() => handleDelete(doc.doc_id, doc.filename)}
                    className="p-2 rounded-xl text-muted-foreground hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {viewer?.doc?.doc_id === doc.doc_id && viewer.blobUrl && (
                <div className="border-t border-border bg-secondary/10">
                  {viewer.ct.includes("image") ? (
                    <div className="p-4 flex justify-center">
                      <img src={viewer.blobUrl} alt={doc.filename}
                        className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-sm" />
                    </div>
                  ) : (
                    <object data={viewer.blobUrl} type="application/pdf"
                      className="w-full rounded-b-2xl" style={{ height: "80vh" }}>
                      <embed src={viewer.blobUrl} type="application/pdf" className="w-full" style={{ height: "80vh" }} />
                    </object>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Elev Hub Oversigt ─────────────────────────────────────────────────────────

function ElevHubOverview({ token, currentUser, city, canPost, isAdmin, isSuperAdmin, onNavigate, userRole }) {
  const now = new Date();
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tilbud, setTilbud] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [newTekst, setNewTekst] = useState("");
  const [newTitel, setNewTitel] = useState("");
  const [newTags, setNewTags] = useState([]);
  const [showTitelField, setShowTitelField] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [posting, setPosting] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]);
  const [showSeen, setShowSeen] = useState({});
  const [showLikes, setShowLikes] = useState({});
  const [replyTo, setReplyTo] = useState({});
  const [postCity, setPostCity] = useState(city || "");
  const imageInputRef = useRef(null);

  const isStaff = ["member", "light", "admin", "superadmin", "owner", "api_admin"].includes(userRole);

  const hdrs = { headers: { Authorization: `Bearer ${token}` } };

  // SSE — live elev-feed for denne by
  const elevSseUrl = token && city
    ? `${SSE_BASE}/api/hub/elev-feed/stream?token=${encodeURIComponent(token)}&city=${encodeURIComponent(city)}`
    : null;
  useFeedSSE(elevSseUrl, setPosts, currentUser, token, "hub/elev-feed");

  // Beregn mandag og lørdag for indeværende uge
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  const dow = monday.getDay();
  monday.setDate(monday.getDate() - (dow === 0 ? 6 : dow - 1));
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  saturday.setHours(23, 59, 59, 999);

  const fetchMonths = [];
  fetchMonths.push({ y: monday.getFullYear(), m: monday.getMonth() + 1 });
  if (saturday.getMonth() !== monday.getMonth()) {
    fetchMonths.push({ y: saturday.getFullYear(), m: saturday.getMonth() + 1 });
  }

  useEffect(() => {
    if (!city) return;
    const kalRequests = fetchMonths.map(({ y, m }) =>
      axios.get(`${API}/hub/elev-kalender?city=${encodeURIComponent(city)}&year=${y}&month=${m}`, hdrs)
    );
    Promise.all([
      ...kalRequests,
      axios.get(`${API}/hub/elev-feed?city=${encodeURIComponent(city)}&limit=50`, hdrs),
      axios.get(`${API}/hub/elev-dokumenter`, hdrs),
      axios.get(`${API}/hub/tilbud`, hdrs).catch(() => ({ data: { tilbud: [] } })),
    ]).then(results => {
      const kalResults = results.slice(0, fetchMonths.length);
      const [feed, dok, til] = results.slice(fetchMonths.length);
      const allEvents = kalResults.flatMap(r => r.data.events || []);
      setEvents(allEvents);
      setPosts(feed.data.posts || []);
      setDocs(dok.data.dokumenter || []);
      setTilbud(til.data.tilbud || []);
    }).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, city]);

  const weekNumber = (() => {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  })();

  const weekEvents = events
    .filter(e => {
      if (!e.year || !e.month || !e.day) return false;
      const d = new Date(e.year, e.month - 1, e.day);
      return d >= monday && d <= saturday;
    })
    .sort((a, b) => new Date(a.year, a.month - 1, a.day) - new Date(b.year, b.month - 1, b.day));

  const handlePost = async () => {
    if (!newTekst.trim()) return;
    setPosting(true);
    try {

      if (attachedImages.length > 0) {
        const fd = new FormData();
        fd.append("city", postCity); fd.append("titel", newTitel); fd.append("tekst", newTekst);
        fd.append("tags", JSON.stringify(newTags));
        for (const img of attachedImages) fd.append("images", img.file, img.file.name);
        await axios.post(`${API}/hub/elev-feed`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/hub/elev-feed`, { city: postCity, titel: newTitel, tekst: newTekst, tags: newTags }, hdrs);
      }
      setNewTekst(""); setNewTitel(""); setNewTags([]); setShowTitelField(false); setShowTagPicker(false); setAttachedImages([]);
    } catch {}
    setPosting(false);
  };

  const handleLike = (post_id) => {
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    axios.post(`${API}/hub/elev-feed/${post_id}/like`, {}, hdrs).catch(() => {
      setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, liked_by_me: !p.liked_by_me } : p));
    });
  };

  const handleSeen = async (post_id) => {
    await axios.post(`${API}/hub/elev-feed/${post_id}/seen`, {}, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id ? {
      ...p, seen_by_me: true, seen_by: [...new Set([...(p.seen_by||[]), currentUser])],
    } : p));
  };

  const handleDeletePost = async (post_id) => {
    if (!window.confirm("Slet dette opslag?")) return;
    await axios.delete(`${API}/hub/elev-feed/${post_id}`, hdrs);
    setPosts(prev => prev.filter(p => p.post_id !== post_id));
  };

  const handleEditPost = async (post_id, updates) => {
    const r = await axios.patch(`${API}/hub/elev-feed/${post_id}`, updates, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id ? { ...p, ...r.data } : p));
  };

  const handleDeleteComment = async (post_id, kommentar_id) => {
    await axios.delete(`${API}/hub/elev-feed/${post_id}/kommentar/${kommentar_id}`, hdrs);
    setPosts(prev => prev.map(p => p.post_id === post_id
      ? { ...p, kommentarer: (p.kommentarer||[]).filter(k => k.kommentar_id !== kommentar_id) } : p));
  };

  const handleComment = async (post_id) => {
    const tekst = (commentDraft[post_id] || "").trim();
    if (!tekst) return;
    setSendingComment(s => ({ ...s, [post_id]: true }));
    try {
      const body = { tekst };
      if (replyTo[post_id]) body.reply_to = replyTo[post_id];
      const r = await axios.post(`${API}/hub/elev-feed/${post_id}/kommentar`, body, hdrs);
      setPosts(prev => prev.map(p => p.post_id === post_id
        ? { ...p, kommentarer: [...(p.kommentarer || []), r.data] } : p));
      setCommentDraft(d => ({ ...d, [post_id]: "" }));
      setReplyTo(r => ({ ...r, [post_id]: null }));
    } catch {}
    setSendingComment(s => ({ ...s, [post_id]: false }));
  };

  const handlePin = async (post_id) => {
    const r = await axios.post(`${API}/hub/elev-feed/${post_id}/pin`, {}, hdrs);
    setPosts(prev => {
      const updated = prev.map(p => p.post_id === post_id ? { ...p, pinned: r.data.pinned } : p);
      return [...updated.filter(p => p.pinned), ...updated.filter(p => !p.pinned)];
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] xl:grid-cols-[1fr_340px] gap-4 items-start">

      {/* ── FEED (venstre) ── */}
      <div className="space-y-4">
        {/* Composer — kun canPost (medarbejdere) */}
        {canPost && (
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
            <div className="flex items-start gap-3 p-4">
              <Avatar name={currentUser} size="md" token={token} />
              <div className="flex-1">
                {showTitelField && (
                  <input type="text" placeholder="Overskrift (valgfri)" value={newTitel}
                    onChange={e => setNewTitel(e.target.value)}
                    className="w-full bg-secondary rounded-xl px-3 h-8 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 border border-border" />
                )}
                {isStaff && (
                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    {[city, "Fælles"].filter((v, i, a) => v && a.indexOf(v) === i).map(c => (
                      <button key={c} type="button"
                        onClick={() => setPostCity(c)}
                        className={`px-3 py-1 rounded-full text-[13px] font-semibold border transition-all ${
                          postCity === c
                            ? "bg-[#FF2A6D] text-white border-[#FF2A6D]"
                            : "border-border text-muted-foreground hover:border-[#FF2A6D]/40"
                        }`}>
                        {c}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  placeholder={`Skriv til ${postCity || city}...`}
                  value={newTekst}
                  onChange={e => setNewTekst(e.target.value)}
                  rows={newTekst ? 4 : 2}
                  className="w-full bg-secondary rounded-xl px-3 py-2 text-sm focus:outline-none resize-none border border-border focus:ring-2 focus:ring-[#FF2A6D]/30 transition-all"
                />
                {attachedImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {attachedImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img.preview} alt="" className="w-20 h-20 object-cover rounded-xl border border-border" />
                        <button onClick={() => {
                          setAttachedImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
                        }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center shadow-sm hover:bg-red-500/10">
                          <X className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {(newTekst || attachedImages.length > 0) && (
              <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3 bg-secondary/20 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => setShowTitelField(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors
                      ${showTitelField ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Overskrift
                  </button>
                  <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      const files = Array.from(e.target.files || []);
                      setAttachedImages(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
                      e.target.value = "";
                    }} />
                  <button onClick={() => imageInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-secondary transition-colors">
                    <ImageIcon className="w-3.5 h-3.5" /> Billede
                  </button>
                  <button onClick={() => setShowTagPicker(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${showTagPicker || newTags.length > 0 ? "bg-[#FF2A6D]/10 text-[#FF2A6D]" : "text-muted-foreground hover:bg-secondary"}`}>
                    # Tags {newTags.length > 0 && `(${newTags.length})`}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setNewTekst(""); setNewTitel(""); setShowTitelField(false); setAttachedImages([]); setNewTags([]); setShowTagPicker(false); }}
                    className="px-3 py-1.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary transition-colors">
                    Annuller
                  </button>
                  <button onClick={handlePost} disabled={posting || !newTekst.trim()}
                    className="px-4 py-1.5 rounded-lg text-[13px] font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
                    {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Del opslag
                  </button>
                </div>
                {showTagPicker && (
                  <div className="w-full flex flex-wrap gap-1.5">
                    {ELEV_HUB_TAGS.map(tag => {
                      const active = newTags.includes(tag.id);
                      return (
                        <button key={tag.id}
                          onClick={() => setNewTags(prev => active ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold border transition-all"
                          style={active
                            ? { backgroundColor: tag.color, color: "#fff", borderColor: tag.color }
                            : { backgroundColor: tag.color + "15", color: tag.color, borderColor: tag.color + "30" }}>
                          # {tag.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Opslag */}
        {posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground rounded-2xl border border-border bg-card">
            <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Ingen opslag endnu</p>
          </div>
        ) : posts.slice(0, 10).map(post => (
          <FeedPost key={post.post_id} post={post} currentUser={currentUser} token={token} API={API}
            feedPath="hub/elev-feed"
            openComments={!!openComments[post.post_id]}
            onToggleComments={() => {
              setOpenComments(o => ({ ...o, [post.post_id]: !o[post.post_id] }));
              if (!post.seen_by_me) handleSeen(post.post_id);
            }}
            onLike={() => handleLike(post.post_id)}
            showSeen={false}
            onToggleSeen={() => { if (!post.seen_by_me) handleSeen(post.post_id); }}
            showLikes={!!showLikes[post.post_id]}
            onToggleLikes={() => setShowLikes(s => ({ ...s, [post.post_id]: !s[post.post_id] }))}
            commentDraft={commentDraft[post.post_id] || ""}
            onCommentChange={v => setCommentDraft(d => ({ ...d, [post.post_id]: v }))}
            onCommentSubmit={() => handleComment(post.post_id)}
            sendingComment={!!sendingComment[post.post_id]}
            replyTo={replyTo[post.post_id] || null}
            onReply={(k) => { setReplyTo(r => ({ ...r, [post.post_id]: k })); setOpenComments(o => ({ ...o, [post.post_id]: true })); }}
            onCancelReply={() => setReplyTo(r => ({ ...r, [post.post_id]: null }))}
            onDelete={() => handleDeletePost(post.post_id)}
            canDelete={isSuperAdmin || post.created_by === currentUser}
            canEdit={isSuperAdmin || post.created_by === currentUser}
            onEdit={(updates) => handleEditPost(post.post_id, updates)}
            onDeleteComment={(kid) => handleDeleteComment(post.post_id, kid)}
            canPin={isSuperAdmin}
            onPin={() => handlePin(post.post_id)}
            feedTagOverride={ELEV_HUB_TAGS}
          />
        ))}
        {posts.length > 10 && (
          <button onClick={() => onNavigate("feed")}
            className="w-full py-3 rounded-2xl border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors font-medium">
            Se alle {posts.length} opslag →
          </button>
        )}
      </div>

      {/* ── SIDEBAR (højre) ── */}
      <div className="space-y-4">

        {/* Ugens events */}
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border"
            style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(16,185,129,0.04))" }}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <div>
                <span className="font-bold text-[13px]">Uge {weekNumber}</span>
                <p className="text-[13px] text-muted-foreground">{monday.getDate()}/{monday.getMonth()+1} – {saturday.getDate()}/{saturday.getMonth()+1}</p>
              </div>
            </div>
            <button onClick={() => onNavigate("kalender")} className="text-[13px] text-blue-500 font-semibold hover:underline">Kalender →</button>
          </div>
          {weekEvents.length === 0 ? (
            <div className="py-4 text-center text-[13px] text-muted-foreground">Ingen events denne uge</div>
          ) : (
            <div className="divide-y divide-border">
              {weekEvents.map((ev, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2.5">
                  <div className="w-8 h-8 rounded-xl flex flex-col items-center justify-center shrink-0 font-bold text-white"
                    style={{ backgroundColor: ev.color || "#6B7280" }}>
                    <span className="text-[12px] leading-none uppercase">{MONTH_NAMES[(ev.month||now.getMonth()+1)-1].slice(0,3)}</span>
                    <span className="text-sm leading-tight">{ev.day}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate">{ev.title}</p>
                    {ev.city && <p className="text-[13px] text-muted-foreground">{ev.city}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Aktuelle tilbud */}
        {tilbud.length > 0 && (
          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="flex items-center px-4 py-3 border-b border-border gap-2"
              style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(234,179,8,0.04))" }}>
              <div className="w-7 h-7 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <span className="text-sm">🏷️</span>
              </div>
              <span className="font-bold text-[13px]">Aktuelle tilbud</span>
            </div>
            <div className="divide-y divide-border">
              {tilbud.slice(0, 3).map(t => (
                <div key={t.tilbud_id} className="px-4 py-2.5">
                  <p className="text-[13px] font-semibold">{t.overskrift}</p>
                  {t.varighed && <p className="text-[13px] text-muted-foreground">{t.varighed}</p>}
                  {t.rabatkode && (
                    <span className="inline-block mt-0.5 text-[13px] font-mono bg-orange-500/10 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded-lg">{t.rabatkode}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seneste filer */}
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border"
            style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(249,115,22,0.04))" }}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <span className="font-bold text-[13px]">Filer</span>
            </div>
            <button onClick={() => onNavigate("filer")} className="text-[13px] text-amber-500 font-semibold hover:underline">Se alle →</button>
          </div>
          {docs.length === 0 ? (
            <div className="py-4 text-center text-[13px] text-muted-foreground">Ingen filer endnu</div>
          ) : (
            <div className="divide-y divide-border">
              {docs.slice(0, 5).map(doc => (
                <div key={doc.doc_id} className="flex items-center gap-2.5 px-3 py-2.5">
                  <span className="text-base shrink-0">{fileIcon(doc.content_type)}</span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate">{doc.filename}</p>
                    <p className="text-[13px] text-muted-foreground">{fmtBytes(doc.size)} · {doc.uploaded_by}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const ELEV_HUB_TABS = [
  { id: "overview", label: "Oversigt",  emoji: "🏠" },
  { id: "kalender", label: "Kalender",  emoji: "📅" },
  { id: "filer",    label: "Filer",     emoji: "📁" },
];

export function ElevHubView({ token, currentUser, userCity, isAdmin, isSuperAdmin, isMember, activeTab: activeTabProp, onTabChange }) {
  const [internalTab, setInternalTab] = useState("overview");
  const activeTab = activeTabProp ?? internalTab;
  const setActiveTab = onTabChange ?? setInternalTab;

  const rawCity = normalizeCity(userCity || "");
  const [selectedCity, setSelectedCity] = useState(rawCity || "København");

  // Superadmin kan skifte by; alle kan skifte til Fælles
  const city = isSuperAdmin ? selectedCity : (selectedCity === "Fælles" ? "Fælles" : rawCity);
  const canPost = isSuperAdmin || isMember || isAdmin;
  const userRole = isSuperAdmin ? "superadmin" : isAdmin ? "admin" : isMember ? "member" : "guest";

  const cityColor = CITY_COLORS[city] || "#6B7280";

  return (
    <div className="space-y-6">
      {/* Header med by-vælger */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: cityColor }}>
            {city.charAt(0)}
          </div>
          <div>
            <h2 className="text-base font-bold">Elev Hub</h2>
            <p className="text-[13px] text-muted-foreground">{city}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {/* Alle kan skifte til/fra Fælles */}
          {!isSuperAdmin && (
            <>
              <button onClick={() => setSelectedCity(rawCity)}
                className="px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all"
                style={city !== "Fælles"
                  ? { backgroundColor: CITY_COLORS[rawCity] || "#6B7280", color: "#fff" }
                  : { backgroundColor: (CITY_COLORS[rawCity] || "#6B7280") + "18", color: CITY_COLORS[rawCity] || "#6B7280" }}>
                {rawCity || "Min by"}
              </button>
              <button onClick={() => setSelectedCity("Fælles")}
                className="px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all"
                style={city === "Fælles"
                  ? { backgroundColor: "#6B7280", color: "#fff" }
                  : { backgroundColor: "#6B728018", color: "#6B7280" }}>
                Fælles
              </button>
            </>
          )}
          {isSuperAdmin && CITIES_WITH_FAELLES.map(c => (
            <button key={c} onClick={() => setSelectedCity(c)}
              className="px-2.5 py-1 rounded-full text-[13px] font-semibold transition-all"
              style={selectedCity === c
                ? { backgroundColor: CITY_COLORS[c] || "#6B7280", color: "#fff" }
                : { backgroundColor: (CITY_COLORS[c] || "#6B7280") + "18", color: CITY_COLORS[c] || "#6B7280" }}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <HubMobileTabMenu tabs={ELEV_HUB_TABS} value={activeTab} onChange={setActiveTab} hideMobile={!!onTabChange} />

      {/* Afstand submenu→content lig over (12px): -mt-3 modvirker root space-y-6. */}
      <div key={activeTab} className="animate-fade-in-up -mt-3">
        {activeTab === "overview" && (
          <ElevHubOverview token={token} currentUser={currentUser} city={city}
            canPost={canPost} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} onNavigate={setActiveTab} userRole={userRole} />
        )}
        {activeTab === "kalender" && (
          <ElevHubKalender token={token} city={city} isAdmin={isAdmin || isSuperAdmin} isSuperAdmin={isSuperAdmin} />
        )}
        {activeTab === "filer" && (
          <ElevHubFiler token={token} isAdmin={isAdmin || isSuperAdmin} />
        )}
      </div>
    </div>
  );
}

ElevHubView.MobileMenu = function ElevHubMobileMenuExport({ activeTab, onChange }) {
  return <HubMobileTabMenu tabs={ELEV_HUB_TABS} value={activeTab} onChange={onChange} hideDesktop />;
};

// ── Hoved HubView ─────────────────────────────────────────────────────────────

const TABS = [
  { id: "overview",     label: "Oversigt",      emoji: "🏠" },
  { id: "kalender",     label: "Kalender",       emoji: "📅" },
  { id: "tilbud",       label: "Tilbud",         emoji: "🏷️" },
  { id: "filer",        label: "Filer",          emoji: "📁" },
];

function HubMobileTabMenu({ tabs, value, onChange, hideDesktop = false, hideMobile = false }) {
  // Bruger den fælles SubTabs-skabelon. hideDesktop = kun mobil (Dashboard-rækken);
  // hideMobile = kun desktop (in-page). Responsiv visibility på en ydre wrapper, så
  // SubTabs' egen `flex` ikke konflikter.
  const visibility = hideDesktop ? "block md:hidden" : hideMobile ? "hidden md:block" : "block";
  return (
    <div className={visibility}>
      {/* Ikoner kun på Forsiden/Hub-undermenuen (icons). Øvrige submenuer = ren tekst. */}
      <SubTabs tabs={tabs} value={value} onChange={onChange} icons />
    </div>
  );
}

HubView.MobileMenu = function HubMobileMenuExport({ activeTab, onChange }) {
  return <HubMobileTabMenu tabs={TABS} value={activeTab} onChange={onChange} hideDesktop />;
};

export default function HubView({ token, currentUser, userCity, isAdmin, isSuperAdmin, isMember, activeTab: activeTabProp, onTabChange }) {
  const [internalTab, setInternalTab] = useState("overview");
  const activeTab = activeTabProp ?? internalTab;
  const setActiveTab = onTabChange ?? setInternalTab;

  return (
    <div>
      {/* Afstand submenu→content lig afstanden OVER submenuen (12px). På mobil
          renderes submenuen i Dashboard-rækken (mb-3=12px), så her kun desktop-gap. */}
      <HubMobileTabMenu tabs={TABS} value={activeTab} onChange={setActiveTab} hideMobile={!!onTabChange} />

      <div key={activeTab} className="animate-fade-in-up md:mt-3">
        {activeTab === "overview"    && <DashboardOverview token={token} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isMember={isMember} currentUser={currentUser} onNavigate={setActiveTab} />}
        {activeTab === "kalender"    && <KalenderTab token={token} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />}
        {activeTab === "tilbud"      && <TilbudTab token={token} isAdmin={isAdmin} />}
        {activeTab === "filer"       && <DokumenterTab token={token} isAdmin={isAdmin} />}
      </div>
    </div>
  );
}
